// CotacaoRespostas — tela mestra portada do ControleFC.
// Edicao inline com auto-save (debounce + flush no focusout), banner de restauracao de
// rascunho (localStorage), ranking unit_com_imp, modal Fornecedores, modo "Inserir cotacao"
// focado num fornecedor, ModalImportarCotacao IA, PDFs client-side (cotacao em branco pra
// fornecedor + Pedido de Compra), reordenar via drag-drop, multiplas variacoes por (item × fornecedor).
//
// Pendente: aplicar isOcultoPdf nos PDFs gerados (hoje e so visual no checkbox).
import {
  Fragment,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { DragEvent } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AvisoOcultosComprados } from './AvisoOcultosComprados';
import {
  ArrowLeft,
  BarChart3,
  Check,
  ChevronDown,
  ChevronUp,
  BookOpen,
  ChevronsDown,
  ChevronsUp,
  ClipboardList,
  FileText,
  GripVertical,
  ImageOff,
  LayoutGrid,
  List,
  Loader2,
  Minus,
  MoreVertical,
  Plus,
  RefreshCw,
  Scissors,
  Search,
  FolderInput,
  ShoppingCart,
  Trash2,
  Upload,
  ListChecks,
  MessageCircle,
  Users,
  X,
} from 'lucide-react';
import { ModalPdfPronto, type PdfPronto } from '../../components/ModalPdfPronto';
import { escolherArquivoPdf, gravarPdf } from '../../lib/salvar-pdf';
import { api, ApiError, getToken } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo, SeloInativo } from '../../lib/produto-inativo';
import { ConfirmModal } from '../../components/ConfirmModal';
import { ModalEnviarWhatsApp } from '../../components/whatsapp/ModalEnviarWhatsApp';
import { AutocompleteMarca, type MarcaOpcao } from '../../components/AutocompleteMarca';
import { ModalCadastroRapido } from '../../components/ModalCadastroRapido';
import { ModalHistoricoProduto, type ProdutoHistorico } from './ModalHistoricoProduto';
import { ModalVendas30d } from './MaisVendidos';
import { ModalSelecionarCotacao } from './ListaCompras';
import { CotacaoLadoALado } from './CotacaoLadoALado';
import { EditorPrecoCatalogo, type AlvoEdicaoPreco } from './EditorPrecoCatalogo';
import { EditorDataCompra, type AlvoEdicaoData } from '../../components/EditorDataCompra';
import { ModalEmbalagemCompra, type EmbalagemCompraGravada } from './ModalEmbalagemCompra';
import { ModalEmbalagemNovoProduto } from './ModalEmbalagemNovoProduto';
import { FotoProduto } from '../../components/FotoProduto';
import {
  formatMoeda,
  formatMoedaSemSimbolo,
  formatQtd,
  parseQtd,
  getNomeFornecedor,
  formatDataSP,
  formatHoraSP,
  precoEmbComDifal,
  difalSeAplica,
  aliquotaDifalValida,
  quantidadeComEmbalagem,
  conteudoDaEmbalagem,
  conteudoDaCaixaDoItem,
  temCaixaNoCadastro,
} from '@fiodecobre/shared';

// Quantidade dos PDFs do Compras: "36 UN (3 CX)" — o conteudo grudado na unidade e as embalagens entre
// parenteses (pedido do Ruan, 28/10/2026). A quantidade do item e em CAIXAS (o "Tot Emb", 29/10), e
// a caixa e a do CADASTRO (30/10: "pegar o total de itens da caixa multiplicar pelo total de
// caixas") — o `unidade_sigla` daqui ja vem com a unidade de compra do cadastro na frente. A da
// linha do fornecedor so vale quando o cadastro nao tem Qtd na Emb.
function qtdComEmbalagemDoItem(item: ItemCotacao, resp?: { embalagem?: string | null; quantidade_embalagem?: string | number | null }) {
  const qeForn = resp?.quantidade_embalagem;
  const temEmbForn = qeForn != null && String(qeForn).trim() !== '';
  const doCadastro = temCaixaNoCadastro(item.qtd_embalagem_padrao);
  return quantidadeComEmbalagem(item.quantidade, {
    qtdEmbalagemPadrao: doCadastro ? item.qtd_embalagem_padrao : (temEmbForn ? qeForn : null),
    unidadeCompra: doCadastro ? item.unidade_sigla : ((temEmbForn && resp?.embalagem) || item.unidade_sigla),
    unidadeConteudo: item.unidade_conteudo_embalagem,
    unidadeVenda: item.unidade_venda_sigla,
    unidadeSemEmbalagem: item.unidade_sigla,
  });
}

// Quanto o item custa pelo NOSSO custo: caixas x unidades da caixa do cadastro x custo do cadastro
// (o custo e por unidade do conteudo, [DA-864]). Peca unica da coluna TOTAL da linha do produto e do
// "Nosso custo" do card do fornecedor (pedido do Ruan, 30/10/2026). A quantidade chega do banco como
// "10.000" ou digitada como "1,5" — por isso a virgula vira ponto e nada de parseQtd.
function custoLojaDoItem(item: ItemCotacao): number {
  const totEmb = Number(String(item.quantidade ?? '').replace(',', '.'));
  const custo = Number(item.preco_custo || 0);
  return Number.isFinite(totEmb) && totEmb > 0 && custo > 0
    ? totEmb * conteudoDaEmbalagem(item.qtd_embalagem_padrao) * custo
    : 0;
}

const ModalImportarCotacao = lazy(() =>
  import('./ModalImportarCotacao').then((m) => ({ default: m.ModalImportarCotacao })),
);

// ============================================================
// Tipos
// ============================================================

interface CotacaoCabecalho {
  id: number;
  numero: string;
  status: string;
  data_abertura: string;
  data_validade: string | null;
  comprador_id: number | null;
  comprador_nome: string | null;
  observacoes: string | null;
  // Usado pelo filtro "Esconder ja comprei nesta rodada" (DA-368): compara com
  // produto.ultima_compra_em. Vem do `c.*` do GET /:id, sempre presente.
  criado_em: string;
}

export interface ItemCotacao {
  id: number;
  cotacao_id: number;
  // NULL e caso legitimo: item sem produto do nosso cadastro. Nasce da compra informativa (DA-309)
  // e, desde 22/08/2026, tambem do Catalogo do Fornecedor — cotar o que a loja ainda nao vende.
  produto_id: number | null;
  // A identidade do item que so existe no catalogo do fornecedor (migration 0247). Preenchida so
  // quando `produto_id` e NULL; e por ela que o espelho do catalogo acha as ofertas desta linha.
  catalogo_item_id?: number | null;
  // A TERCEIRA identidade possivel (migration 0248, DA-606): a prateleira Novos Produtos, do item
  // que nao existe nem na loja nem no catalogo de ninguem. As tres se excluem na pratica.
  novo_produto_id?: number | null;
  // O nome do item quando nao ha produto do cadastro. A tela le pelo `produto_descricao`, que ja
  // vem com o COALESCE resolvido do backend; este campo cru viaja pra sobreviver ao rascunho.
  descricao_livre?: string | null;
  quantidade: string;
  ordem: number;
  unidade_sigla: string | null;
  marca_preferida: string | null;
  codigo_interno: string | null;
  /**
   * O codigo da prateleira nova ([DA-610]). Ocupa a MESMA coluna do codigo interno — sem ele, item
   * de prateleira aparece sem codigo nenhum, e itens de nome parecido (tres facoes de 12") ficam
   * indistinguiveis na grade.
   */
  np_codigo?: string | null;
  produto_descricao: string | null;
  descricao?: string | null;
  foto_url: string | null;
  estoque_atual: string | null;
  // Vendas dos ultimos 30 dias (giro-30d.ts) — botao na coluna Alteracao. NULL = item sem produto.
  vendas_30d?: string | null;
  preco_custo: string | null;
  qtd_embalagem_padrao: string | null;
  unidade_conteudo_embalagem: string | null;
  unidade_venda_sigla: string | null;
  produto_marca: string | null;
  // Compra em aberto (DA-368) — vive no PRODUTO, atravessa cotacoes. Alimenta o selo de
  // "a caminho"/"ultima compra" por linha, o filtro "esconder ja comprei nesta rodada" e o
  // checkbox "C" por celula (produto x fornecedor) — `ultima_compra_fornecedor_id` e o ID cru
  // que diz QUAL celula marcar (o nome so serve pra exibir).
  compra_em_aberto?: boolean;
  ultima_compra_em?: string | null;
  ultima_compra_fornecedor_id?: number | null;
  ultima_compra_fornecedor_nome?: string | null;
  ultima_entrada_xml_em?: string | null;
  ultima_entrada_xml_fornecedor_nome?: string | null;
  /** Status do PRODUTO do cadastro (NULL = item sem produto). */
  produto_status?: string | null;
}

/**
 * Item cujo produto foi INATIVADO no cadastro (pedido dele, 28/10/2026): a linha fica riscada e
 * indisponivel — nao edita, nao marca "comprei", sai do total e nao vai pro fornecedor. Peca unica
 * das duas vistas; quem soma ou monta documento le `itensAtivos`, nunca `itens`.
 */
export function itemInativo(item: Pick<ItemCotacao, 'produto_id' | 'produto_status'>): boolean {
  return item.produto_id != null && item.produto_status != null && item.produto_status !== 'ativo';
}

/**
 * Onde mora a marca de "comprei" deste item (DA-368): no PRODUTO, ou — item da prateleira Novos
 * Produtos, que ainda nao tem produto — na propria prateleira (migration 0338). `null` = item so de
 * Catalogo do Fornecedor, que nao tem onde guardar a marca; ali o carrinho nao aparece.
 * Produto INATIVO tambem devolve `null`: a linha esta indisponivel e nao marca "comprei".
 */
export function alvoDaMarcaCompra(item: ItemCotacao):
  | { ids: { produto_ids?: number[]; novo_produto_ids?: number[] }; chave: { produto_id?: number; novo_produto_id?: number } }
  | null {
  if (itemInativo(item)) return null;
  if (item.produto_id != null) return { ids: { produto_ids: [item.produto_id] }, chave: { produto_id: item.produto_id } };
  if (item.novo_produto_id != null) {
    return { ids: { novo_produto_ids: [item.novo_produto_id] }, chave: { novo_produto_id: item.novo_produto_id } };
  }
  return null;
}

/**
 * A linha da cotacao viaja pro `PUT /api/cotacoes/:id` com o PROPRIO id — e ele que identifica a
 * linha no backend. ⚠️ Sem o id o servidor cai no casamento por produto, que NAO distingue duas
 * linhas sem produto do cadastro (as que vem do Catalogo do Fornecedor): as duas colapsam na
 * mesma chave e uma sobrescreve a outra em SILENCIO, sem erro nem 500.
 *
 * Existe como funcao unica porque o payload e montado em 7 lugares desta tela — e bastava um
 * esquecer o `id` pra o defeito voltar so naquele caminho.
 */
function payloadItemCotacao(i: {
  id?: number;
  produto_id?: number | null;
  quantidade?: string | null;
  unidade_sigla?: string | null;
  marca_preferida?: string | null;
  catalogo_item_id?: number | null;
  novo_produto_id?: number | null;
  descricao_livre?: string | null;
}) {
  return {
    id: i.id,
    produto_id: i.produto_id ?? null,
    quantidade: i.quantidade,
    unidade_sigla: i.unidade_sigla ?? null,
    marca_preferida: i.marca_preferida ?? null,
    // So viajam na CRIACAO (o backend ignora no update) — mas vao sempre, pra o payload de uma
    // linha existente nunca ficar mais pobre que o da linha que a criou.
    catalogo_item_id: i.catalogo_item_id ?? null,
    novo_produto_id: i.novo_produto_id ?? null,
    descricao_livre: i.descricao_livre ?? null,
  };
}

export interface FornecedorVinculado {
  id: number;
  cotacao_id: number;
  fornecedor_id: number;
  total_c_imp: string | null;
  tipo_origem: string | null;
  valor_frete: string | null;
  razao_social: string | null;
  nome_fantasia: string | null;
  telefone: string | null;
  whatsapp: string | null;
  email: string | null;
  /* Quantas filiais a empresa tem (DA-391). Mais de uma = as linhas ganham o selo da UF, porque
     ali existem duas ofertas de verdade, com preco proprio, e sem o selo elas ficam gemeas. */
  total_filiais?: number | string | null;
}

interface RespostaServidor {
  id: number;
  cotacao_id: number;
  cotacao_item_id: number;
  fornecedor_id: number;
  // DA-391 — a filial de origem da linha; `filial_uf` vem do JOIN, so pra exibir o selo.
  filial_id: number | null;
  filial_uf: string | null;
  filial_principal?: boolean;
  // Aliquota do DIFAL escolhida no Monitor pra este codigo ([DA-727]); null = a conferir / fora do Monitor.
  difal_pct?: string | number | null;
  cod_produto_fornecedor: string | null;
  marca: string | null;
  descricao_complementar: string | null;
  // Ultima mudanca de PRECO da perna do Catalogo (coluna "Alteracao").
  preco_atualizado_em?: string | null;
  embalagem: string | null;
  quantidade_produto: string | null;
  quantidade_embalagem: string | null;
  quantidade_cotada: string | null;
  preco_principal: string | null;
  preco_unitario: string | null;
  ipi_percentual: string | null;
  observacoes: string | null;
  escolhida?: boolean;
  desconsiderada?: boolean;
  forcada?: boolean;
  oculta_pdf?: boolean;
}

interface CotacaoCompleta extends CotacaoCabecalho {
  itens: ItemCotacao[];
  fornecedores: FornecedorVinculado[];
  respostas: RespostaServidor[];
}

export interface RespostaForm {
  /* De QUAL filial do fornecedor e esta linha (DA-391). O catalogo tem uma perna por filial
     (DA-310) e MARTINS/TAMBASA/COFER tem duas (GO + MG), cada uma com preco proprio — sem isto
     as duas linhas eram gemeas e o F5 devolvia as duas com o preco de uma so. `filial_uf` e so
     pra exibir o selo; quem identifica e o `filial_id`. */
  filial_id: number | null;
  filial_uf: string | null;
  /* Filial de casa (a `principal` do cadastro) ou de fora — a de fora sofre DIFAL na entrada,
     mesma leitura que o Catalogo do Fornecedor ja faz no modal de editar item. */
  filial_principal: boolean;
  /* Aliquota do DIFAL escolhida no Monitor ([DA-727]). NAO vai ao banco: a cotacao guarda o preco
     do fornecedor e a tela calcula o preco com DIFAL por cima (precoEmbComDifal). Ausente/null =
     estimativa de 4,49% fora de GO. Opcional porque o rascunho antigo do localStorage nao tem. */
  difal_pct?: number | null;
  cod_produto_fornecedor: string;
  descricao_fornecedor: string;
  /* Quando o PRECO desta perna mudou pela ultima vez no Catalogo (coluna "Alteracao", pedido do
     Ruan 21/10/2026). So exibicao: NAO vai no payload do Salvar — a fonte e o Catalogo. Opcional
     porque o rascunho antigo do localStorage nao tem. */
  preco_atualizado_em?: string | null;
  marca: string;
  embalagem: string;
  quantidade_produto: string;
  quantidade_embalagem: string;
  quantidade_cotada: string;
  preco_principal: string;
  ipi_percentual: number | null;
  observacoes: string;
  /* Qual oferta vale quando o fornecedor tem 2+ codigos pro mesmo produto (migration 0161).
     Vive na resposta, nao no catalogo: muda a cada cotacao — o fornecedor pode nao ter uma
     marca este mes e ter a outra. Par com UMA linha so nasce true (nao ha o que escolher). */
  escolhida: boolean;
  /* As 3 decisoes da grade (D / F / I), gravadas desde a migration 0162. Antes viviam num
     `Record<'itemId-fornId-respIdx', boolean>` de `useState`: sumiam no F5 e, como a chave era a
     POSICAO da linha, remover uma variacao fazia a marca escorregar calada pra outra oferta.
     Aqui elas acompanham a propria linha. `oculta_pdf` e invertida na tela: o checkbox mostra
     "incluir no PDF", entao marcado = `oculta_pdf: false`. */
  desconsiderada: boolean;
  forcada: boolean;
  oculta_pdf: boolean;
}

interface FornecedorMin {
  id: number;
  razao_social: string | null;
  nome_fantasia: string | null;
  cidade: string | null;
  uf: string | null;
}

interface ProdutoBusca {
  id: number;
  codigo_interno: string;
  descricao: string;
  foto_url: string | null;
  estoque_atual: string | null;
  unidade_compra: string | null;
  unidade_sigla: string | null;
  fornecedores: string | null;
  status: string;
  compra_em_aberto?: boolean;
  ultima_compra_em?: string | null;
  ultima_compra_fornecedor_nome?: string | null;
}

/**
 * Um item do Catalogo do Fornecedor no dropdown de busca da cotacao. E o registro da EMPRESA
 * (um por par empresa+codigo — DA-310); `pernas` sao as ofertas por filial, e delas so se usa
 * aqui a embalagem, pra a linha nova nascer com a unidade certa. Resposta de
 * `GET /api/fornecedor-catalogo/itens`.
 */
interface ItemCatalogoBusca {
  id: number;
  fornecedor_id: number;
  codigo_fornecedor: string | null;
  descricao_fornecedor: string;
  marca: string | null;
  ncm: string | null;
  empresa_nome: string | null;
  foto_url: string | null;
  /** hub da prateleira nova (DA-610) — item ligado a um NP sai da secao do Catalogo */
  novo_produto_id?: number | null;
  pernas?: Array<{ embalagem: string | null; quantidade_embalagem: string | null; preco_unitario: string | null; uf: string | null }>;
}

/**
 * Um item da prateleira NOVOS PRODUTOS (DA-606) no dropdown de busca. Nao tem produto nosso nem
 * fornecedor: e o item que o Ruan quer cotar antes de existir em qualquer lugar. Resposta de
 * `GET /api/novos-produtos/busca`.
 */
interface NovoProdutoBusca {
  id: number;
  codigo: string;
  descricao: string;
  marca: string | null;
  embalagem: string | null;
  quantidade_embalagem: string | null;
  ncm: string | null;
  foto_url: string | null;
}

interface CotacaoLista {
  id: number;
  numero: string;
  status: string;
  data_abertura: string;
  data_validade: string | null;
  comprador_nome: string | null;
  observacoes: string | null;
  total_itens: string;
  total_fornecedores: string;
}

// ============================================================
// Helpers
// ============================================================

const RESP_VAZIA: RespostaForm = {
  filial_id: null,
  filial_uf: null,
  filial_principal: true,
  cod_produto_fornecedor: '',
  descricao_fornecedor: '',
  marca: '',
  embalagem: 'UN',
  quantidade_produto: '1',
  quantidade_embalagem: '1',
  quantidade_cotada: '',
  preco_principal: '',
  ipi_percentual: null,
  observacoes: '',
  escolhida: true,
  desconsiderada: false,
  forcada: false,
  oculta_pdf: false,
};

/* Como o preco do catalogo vira TEXTO na celula. Peca unica de proposito: o "Recarregar catalogo" e
   a edicao pela caixinha de preco escrevem no MESMO campo, e formatar diferente faria o recarregar
   enxergar diferenca onde o valor e o mesmo (acendendo "alteracoes nao salvas" a toa). 4 casas
   porque `preco_principal` tem 4 (migration 0137) e cabo de R$ 1,1991 nao pode virar R$ 1,20. */
function fmtPrecoCatalogo(v: number | string | null | undefined): string {
  const n = Number(v || 0);
  return n > 0 ? n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : '';
}

// So pra COMPARAR datas (nunca exibir — pra isso e formatDataSP). A API devolve carimbo em duas
// grafias (DA-330, leva 3b): raw SQL solta o texto do Postgres com o fuso junto ("...-03", sem
// T), e o Drizzle solta ISO com "Z". `new Date()` entende a segunda sozinho; a primeira precisa
// virar ISO (troca o espaco por T + fecha o offset em ":00") pra nao voltar NaN.
// Ha uma COPIA local desta mesma conta em `CotacaoLadoALado.tsx` (evita import circular entre
// as duas vistas) — mexeu numa, mexer na outra.
export function paraDataMs(s: string | null | undefined): number {
  if (!s) return 0;
  const iso = s.includes('T') || s.endsWith('Z') ? s : s.replace(' ', 'T') + (/[+-]\d{2}$/.test(s) ? ':00' : '');
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

// Chave de conteudo de uma linha de resposta — colapsa copias IDENTICAS.
// (linhas distintas — marca/embalagem/preco diferentes — geram chaves diferentes e ficam.)
function chaveResposta(r: RespostaForm): string {
  return [
    // A FILIAL entra na chave (DA-391): GO e MG sao ofertas distintas do mesmo codigo. Sem ela,
    // quando as duas estao sem preco no catalogo tudo o mais coincide e o dedup comia uma.
    r.filial_id ?? '',
    (r.cod_produto_fornecedor || '').trim(),
    (r.descricao_fornecedor || '').trim(),
    (r.marca || '').trim(),
    (r.embalagem || '').trim(),
    String(r.quantidade_produto ?? '').trim(),
    String(r.quantidade_embalagem ?? '').trim(),
    String(r.quantidade_cotada ?? '').trim(),
    String(r.preco_principal ?? '').trim(),
    r.ipi_percentual ?? '',
    (r.observacoes || '').trim(),
  ].join('|');
}

function dedupLinhas(linhas: RespostaForm[]): RespostaForm[] {
  const vistas = new Set<string>();
  const out: RespostaForm[] = [];
  for (const l of linhas) {
    const k = chaveResposta(l);
    if (vistas.has(k)) continue;
    vistas.add(k);
    out.push(l);
  }
  return out;
}

const RASCUNHO_KEY = (id: number | string) => `cotacao-rascunho-${id}`;

/* DA-472 — status em que a cotacao ainda ANDA e por isso segue o Catalogo sozinha. Os outros tres
   do menu "Mover para" da lista (`aprovada`, `finalizada`, `cancelada`) sao documento fechado e
   congelam no valor que tinham ao fechar. */
const ESPELHA_CATALOGO = ['aberta', 'em_analise'];

const STATUS_TABS: Array<{ key: string; label: string }> = [
  { key: 'aberta', label: 'Aberta' },
  { key: 'em_analise', label: 'Em Analise' },
  { key: 'aprovada', label: 'Aprovada' },
  { key: 'finalizada', label: 'Finalizada' },
  { key: 'cancelada', label: 'Cancelada' },
];

function lerRascunho(id: number | string): any {
  try {
    const raw = localStorage.getItem(RASCUNHO_KEY(id));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function limparRascunho(id: number | string) {
  try {
    localStorage.removeItem(RASCUNHO_KEY(id));
  } catch {
    /* ignore */
  }
}

function snapshotDifere(
  snap: any,
  formAtual: any,
  itensAtuais: ItemCotacao[],
  respostasAtuais: Record<string, RespostaForm[]>,
  totalCImpAtual: Record<number, string>,
): boolean {
  if (!snap) return false;
  const j = (x: unknown) => {
    try {
      return JSON.stringify(x);
    } catch {
      return '';
    }
  };
  const itensKeys = (arr: ItemCotacao[]) =>
    (arr || []).map((i) => ({
      id: i.id,
      marca_preferida: i.marca_preferida || null,
      quantidade: String(i.quantidade ?? ''),
      unidade_sigla: i.unidade_sigla || null,
    }));
  if (j(snap.formCotacao || {}) !== j(formAtual || {})) return true;
  if (j(itensKeys(snap.itens || [])) !== j(itensKeys(itensAtuais))) return true;
  if (j(snap.respostas || {}) !== j(respostasAtuais || {})) return true;
  if (j(snap.totalCImpForn || {}) !== j(totalCImpAtual || {})) return true;
  return false;
}

// ============================================================
// Componente principal
// ============================================================

export function CotacaoRespostas() {
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  // Voltar honra de onde a tela foi aberta (ex.: "O que falta comprar" passa state.from);
  // sem esse contexto, o padrao de sempre e a lista de Cotacoes.
  const location = useLocation();
  const voltarPara = (location.state as { from?: string } | null)?.from ?? '/cotacoes';
  const id = idParam || '';
  const toast = useToast();
  const dialog = useDialog();
  const { usuario } = useAuth();
  const podeEditarCatalogo = ['admin', 'gerente', 'comprador'].includes(usuario?.perfil ?? '');

  const [cotacao, setCotacao] = useState<CotacaoCabecalho | null>(null);
  const [itens, setItens] = useState<ItemCotacao[]>([]);
  // Os itens que CONTAM: totais, contadores, PDFs e textos pro fornecedor leem daqui. O inativo
  // segue na grade (riscado), mas fora de tudo que soma ou sai de casa (pedido dele, 28/10/2026).
  const itensAtivos = useMemo(() => itens.filter((i) => !itemInativo(i)), [itens]);
  const [fornecedores, setFornecedores] = useState<FornecedorVinculado[]>([]);
  // { [fornecedor_id]: string pt-BR } — Total c Imp do fornecedor
  const [totalCImpForn, setTotalCImpForn] = useState<Record<number, string>>({});
  // { "itemId-fornId": RespostaForm[] }
  const [respostas, setRespostas] = useState<Record<string, RespostaForm[]>>({});
  /* Pares "itemId-fornId" cujo preco veio da ANOTACAO do Novos Produtos e NAO tem lastro no
     Catalogo do Fornecedor (31/08/2026). Vive fora da `RespostaForm` de proposito: nao e' dado
     da resposta, e' de ONDE ela veio — poe-lo la o mandaria pro payload do Salvar e pro banco,
     onde ele nao significa nada. O conjunto se refaz a cada `aplicarCatalogo` (inclusive no modo
     espelho da abertura, que le a rota mesmo sem preencher linha), entao ele acompanha sozinho o
     dia em que o item ganhar cadastro de verdade — a rota para de emitir a anotacao e o selo some. */
  const [paresAnotacao, setParesAnotacao] = useState<Set<string>>(new Set());
  // Espelho sincrono do estado das respostas. O `recarregarCatalogo` precisa CONTAR quantas
  // linhas encheu pra decidir o toast e o "tem alteracoes" — e contar dentro do updater do
  // setState nao funciona (o updater so roda no render seguinte, depois do if). Ver DA do fix.
  const respostasRef = useRef(respostas);
  useEffect(() => {
    respostasRef.current = respostas;
  }, [respostas]);
  // Mesmo motivo do espelho acima: o `recarregarCatalogo` le a quantidade do produto pra semear
  // a linha nova. Via ref, e nao pela dependencia do useCallback, pra a funcao nao se recriar a
  // cada tecla digitada numa quantidade.
  const itensRef = useRef<ItemCotacao[]>([]);
  useEffect(() => {
    itensRef.current = itens;
  }, [itens]);

  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [recarregandoCat, setRecarregandoCat] = useState(false);
  const [fotoZoom, setFotoZoom] = useState<string | null>(null);

  // Form do cabecalho
  const [formCotacao, setFormCotacao] = useState<{
    data_validade: string;
    comprador_id: number | string;
    observacoes: string;
  }>({ data_validade: '', comprador_id: '', observacoes: '' });

  const [buscaProduto, setBuscaProduto] = useState('');
  const [resultadosProduto, setResultadosProduto] = useState<ProdutoBusca[]>([]);
  // O segundo balcao da busca: itens que so existem no Catalogo do Fornecedor, sem produto nosso.
  const [resultadosCatalogo, setResultadosCatalogo] = useState<ItemCatalogoBusca[]>([]);
  // E o terceiro: a prateleira Novos Produtos, o item que nao existe em lugar nenhum ainda (DA-606).
  const [resultadosNovos, setResultadosNovos] = useState<NovoProdutoBusca[]>([]);
  const buscaTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Lista lateral de cotacoes
  const [listaCotacoes, setListaCotacoes] = useState<CotacaoLista[]>([]);
  const [buscaLista, setBuscaLista] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('aberta');
  const [loadingLista, setLoadingLista] = useState(true);
  const buscaListaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Modais e UI
  const [menuPrincipal, setMenuPrincipal] = useState(false);
  const [modalFornecedores, setModalFornecedores] = useState(false);
  const [todosFornecedores, setTodosFornecedores] = useState<FornecedorMin[]>([]);
  const [menuFornModalAberto, setMenuFornModalAberto] = useState<number | null>(null);
  const [fornecedorIdsSelecionados, setFornecedorIdsSelecionados] = useState<number[]>([]);
  const [buscaFornModal, setBuscaFornModal] = useState('');
  const buscaFornModalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [inserirCotacaoForn, setInserirCotacaoForn] = useState<FornecedorVinculado | null>(null);
  const [modalImportarIA, setModalImportarIA] = useState(false);
  const [confirmarLimparForn, setConfirmarLimparForn] = useState<FornecedorVinculado | null>(null);
  const [enviarWhatsForn, setEnviarWhatsForn] = useState<FornecedorVinculado | null>(null);
  const [enviarWhatsTel, setEnviarWhatsTel] = useState<string>('');

  function abrirEnviarWhatsApp(forn: FornecedorVinculado) {
    const texto = gerarTextoPedidoCompra(forn);
    if (!texto) {
      toast.erro(`${getNomeFornecedor(forn)} nao venceu nenhum item nesta cotacao`);
      return;
    }
    setEnviarWhatsForn(forn);
    setEnviarWhatsTel('');
    api.get<{ telefone?: string | null; whatsapp?: string | null }>(`/api/fornecedores/${forn.fornecedor_id}`)
      .then((f) => setEnviarWhatsTel((f.whatsapp || f.telefone || '').replace(/\D/g, '')))
      .catch(() => {});
  }

  // Auto-save
  const debounceRespostasRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceFormRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const itensFlushRef = useRef<(() => Promise<void>) | null>(null);
  const salvarTudoRef = useRef<(() => Promise<boolean>) | null>(null);
  const gerandoPdfFornRef = useRef(false);
  const [pdfPronto, setPdfPronto] = useState<PdfPronto | null>(null);
  const jaCarregou = useRef(false);
  const snapshotTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const verificouRascunho = useRef(false);
  const [rascunhoOferta, setRascunhoOferta] = useState<any>(null);
  const [temAlteracoes, setTemAlteracoes] = useState(false);

  type ModalCatalogoCriarState = {
    item_id: number;
    fornecedor_id: number;
    // NULL no item que so existe no catalogo do fornecedor: nao ha produto nosso pra amarrar, e a
    // identidade da linha nova passa a ser o CODIGO (invariante do catalogo: produto OU codigo).
    produto_id: number | null;
    // O item da prateleira NOVOS PRODUTOS ([DA-610]). E' o terceiro jeito de a linha ter
    // identidade: com ele, o codigo do fornecedor deixa de ser obrigatorio — da' pra cadastrar os
    // cinco fornecedores primeiro e ir atras dos codigos depois.
    novo_produto_id: number | null;
    forn_nome: string;
    prod_desc: string;
    /* O que ja esta na linha da cotacao, pra o modal abrir preenchido em vez de cobrar a mesma
       digitacao duas vezes (31/08/2026 — o caso que motivou foi a anotacao do Novos Produtos, que
       ja traz codigo, descricao, marca, embalagem e quantidade). `undefined` = abre em branco. */
    inicial?: {
      codigo_fornecedor: string;
      descricao_fornecedor: string;
      marca: string;
      embalagem: string;
      quantidade_embalagem: string;
    };
  };
  const [modalCatalogoCriar, setModalCatalogoCriar] = useState<ModalCatalogoCriarState | null>(null);

  // Itens expandidos (collapse/expand)
  const [expandidos, setExpandidos] = useState<Set<number>>(() => {
    if (!id) return new Set();
    try {
      const saved = sessionStorage.getItem(`cotacao_${id}_expandidos`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Vista "lado a lado" (produtos nas linhas, fornecedores nas colunas). E so um modo de
  // EXIBICAO da mesma cotacao — nao carrega nada a mais. A escolha persiste porque quem
  // trabalha comparando quer abrir sempre nela.
  const [ladoALado, setLadoALado] = useState<boolean>(() => {
    try {
      return localStorage.getItem('cotacoes:vista') === 'lado-a-lado';
    } catch {
      return false;
    }
  });

  function trocarVista(novo: boolean) {
    setLadoALado(novo);
    try {
      localStorage.setItem('cotacoes:vista', novo ? 'lado-a-lado' : 'por-produto');
    } catch {
      /* ignore */
    }
  }

  // "Esconder ja comprei nesta rodada" (DA-368): oculta produtos cuja ultima_compra_em e MAIS
  // RECENTE que o criado_em desta cotacao — comparacao de data no front, os dois campos ja vem
  // no GET. Preferencia persiste com o mesmo prefixo de modulo das outras desta tela (linha 453).
  const [escondeCompradas, setEscondeCompradas] = useState<boolean>(() => {
    try {
      return localStorage.getItem('cotacoes:respostas:escondeCompradas') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('cotacoes:respostas:escondeCompradas', escondeCompradas ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [escondeCompradas]);

  // Drag-drop pra reordenar itens
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [dragOverPos, setDragOverPos] = useState<'above' | 'below' | null>(null);

  // Marcas pro AutocompleteMarca + modal de cadastro rapido
  const [marcas, setMarcas] = useState<MarcaOpcao[]>([]);
  type NovaMarcaCtx =
    | { tipo: 'preferida'; itemId: number }
    | { tipo: 'resp'; itemId: number; fornId: number; respIdx: number };
  const [novaMarcaCtx, setNovaMarcaCtx] = useState<NovaMarcaCtx | null>(null);

  // Modal Historico do produto em todos os fornecedores (icone livrinho)
  const [historicoProduto, setHistoricoProduto] = useState<ProdutoHistorico | null>(null);

  // Janela "N vendas · 30d" (coluna Alteracao) e o Enviar p/ Cotacao dela — mesmo par da lista de
  // Produtos (DA-862): o Enviar fecha a janela e abre o modal da Lista de Compras.
  const [vendas30d, setVendas30d] = useState<number | null>(null);
  const [enviarCotacao, setEnviarCotacao] = useState<{ produto_id: number; descricao: string } | null>(null);

  // As decisoes D/F/I nao moram mais aqui: viraram campos da propria linha de resposta
  // (`desconsiderada`/`forcada`/`oculta_pdf`, migration 0162), gravados junto com ela no Salvar.
  // Ver as funcoes `isForcarCompra`/`toggleForcarCompra` & cia mais abaixo.

  // Modal Split (separar itens comprados em nova cotacao ou existente)
  type SplitComprado = {
    item_id: number;
    fornecedor_id: number;
    item_desc: string;
    forn_nome: string;
  };
  const [splitModal, setSplitModal] = useState<'choose' | 'select' | null>(null);
  const [splitComprados, setSplitComprados] = useState<SplitComprado[]>([]);
  const [splitCotacoes, setSplitCotacoes] = useState<CotacaoLista[]>([]);
  const [splitCotacoesLoading, setSplitCotacoesLoading] = useState(false);
  const [splitLoading, setSplitLoading] = useState(false);

  // Mover UM item pra outra cotacao levando TODOS os fornecedores (DA-368/Fase 2).
  // (`moverAlvo`, nao `moverItem`: ja existe a funcao moverItem(idx,delta) da reordenacao por setas.)
  const [moverAlvo, setMoverAlvo] = useState<{ id: number; desc: string } | null>(null);
  const [moverCotacoes, setMoverCotacoes] = useState<CotacaoLista[]>([]);
  const [moverCotacoesLoading, setMoverCotacoesLoading] = useState(false);
  const [moverEnviando, setMoverEnviando] = useState(false);

  // ============================================================
  // Carregamento da cotacao
  // ============================================================

  const carregarDados = useCallback(async () => {
    if (!id) {
      setCotacao(null);
      setItens([]);
      setFornecedores([]);
      setRespostas({});
      setLoading(false);
      return;
    }
    setLoading(true);
    jaCarregou.current = false;
    try {
      const data = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
      setCotacao(data);
      setItens(data.itens);
      setFornecedores(data.fornecedores);
      setFormCotacao({
        data_validade: data.data_validade ? data.data_validade.substring(0, 10) : '',
        comprador_id: data.comprador_id ?? '',
        observacoes: data.observacoes || '',
      });

      const tMap: Record<number, string> = {};
      for (const f of data.fornecedores) {
        tMap[f.fornecedor_id] =
          f.total_c_imp != null
            ? Number(f.total_c_imp).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '100,00';
      }
      setTotalCImpForn(tMap);

      // Helpers de formatacao no load
      const fmtPrecoLoad = (v: unknown): string => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? formatMoedaSemSimbolo(n) : '';
      };
      const fmtQtdLoad = (v: unknown): string => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? formatQtd(n) : '';
      };

      // Respostas do servidor: identificacao + preco_principal ja vem do catalogo
      // (COALESCE no backend com fornecedor_catalogo). Cotacao puxa SO do catalogo;
      // NF de entrada alimenta o catalogo (DA-122 e extensao).
      const map: Record<string, RespostaForm[]> = {};
      for (const r of data.respostas) {
        const key = `${r.cotacao_item_id}-${r.fornecedor_id}`;
        if (!map[key]) map[key] = [];
        map[key]!.push({
          filial_id: r.filial_id ?? null,
          filial_uf: r.filial_uf || null,
          filial_principal: r.filial_principal !== false,
          difal_pct: r.difal_pct == null ? null : Number(r.difal_pct),
          cod_produto_fornecedor: r.cod_produto_fornecedor || '',
          descricao_fornecedor: r.descricao_complementar || '',
          preco_atualizado_em: r.preco_atualizado_em ?? null,
          marca: r.marca || '',
          embalagem: r.embalagem || 'UN',
          quantidade_produto: fmtQtdLoad(r.quantidade_produto) || formatQtd(1),
          quantidade_embalagem: fmtQtdLoad(r.quantidade_embalagem) || formatQtd(1),
          quantidade_cotada: fmtQtdLoad(r.quantidade_cotada),
          preco_principal: fmtPrecoLoad(r.preco_principal),
          ipi_percentual: r.ipi_percentual != null ? parseFloat(r.ipi_percentual) : null,
          observacoes: r.observacoes || '',
          escolhida: !!r.escolhida,
          desconsiderada: !!r.desconsiderada,
          forcada: !!r.forcada,
          oculta_pdf: !!r.oculta_pdf,
        });
      }
      // Dedup de copias identicas que tenham vindo do banco (auto-cura ao re-salvar)
      for (const k of Object.keys(map)) map[k] = dedupLinhas(map[k]!);
      // Par que ficou com UMA linha so nao tem o que escolher — ela e a valida. Cobre o
      // registro antigo que a semeadura da 0161 nao alcancou e o que o dedup acabou de colapsar.
      for (const k of Object.keys(map)) {
        if (map[k]!.length === 1) map[k]![0]!.escolhida = true;
      }
      setRespostas(map);

      // Expansao default = todos
      const saved = sessionStorage.getItem(`cotacao_${id}_expandidos`);
      if (!saved) setExpandidos(new Set(data.itens.map((i) => i.id)));

      setTimeout(() => {
        jaCarregou.current = true;
      }, 100);
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao carregar cotacao');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // Espelha o Catalogo (fonte canonica, DA-283/285) nas respostas desta cotacao. UMA implementacao
  // da regra, dois gatilhos — o botao "Recarregar catalogo" e a carga automatica da cotacao aberta
  // (DA-472). Devolve quantas linhas mudaram de valor; quem chamou decide o que dizer na tela.
  //
  // `soEspelho` separa os dois: no modo espelho a funcao SO atualiza linha ja identificada (preco,
  // qtd na embalagem, embalagem) e nao mexe na composicao da grade — nao cria linha em par vazio,
  // nao importa oferta sem linha correspondente, nao colapsa duplicata. Isso e de proposito: rodando
  // sozinha a cada abertura, criar/remover linha faria o documento mudar de FORMA nas costas do
  // comprador, e a variacao que ele apagou de proposito voltaria toda vez que a tela abrisse. O
  // botao segue fazendo tudo, porque ali quem pediu foi ele.
  //
  // ⚠️ **UMA excecao desde 31/08/2026**: par cujas ofertas vem TODAS da anotacao do Novos Produtos
  // e' preenchido tambem na abertura — o motivo e o preco estao no proprio ramo do par vazio, mais
  // abaixo. Fora dela o paragrafo acima vale inteiro.
  const aplicarCatalogo = useCallback(async (soEspelho: boolean): Promise<number> => {
    if (!id) return 0;
    {
      type HistResp = {
        cotacao_item_id: number; fornecedor_id: number;
        // DA-391 — uma linha POR PERNA (filial), com o preco daquela filial. `preco_principal`
        // vem null quando aquela filial nao tem preco no catalogo (nao 0: a tela precisa
        // distinguir "sem preco cadastrado" de "custa zero").
        filial_id: number | null; filial_uf: string | null; filial_apelido: string | null;
        filial_principal: boolean;
        cod_produto_fornecedor: string; descricao_complementar: string;
        preco_atualizado_em?: string | null;
        marca: string; embalagem: string;
        quantidade_embalagem: number; quantidade_cotada: number;
        preco_principal: number | null; preco_unitario: number;
        difal_pct?: number | null; // [DA-727] aliquota escolhida no Monitor pra este codigo
        /* A linha veio da ANOTACAO do Novos Produtos (fornecedor + codigo + preco que o Ruan
           digitou na prateleira), nao do Catalogo do Fornecedor — 31/08/2026. A rota so a emite
           quando NAO ha oferta de catalogo pro par, entao isto significa, sempre: "este preco
           esta na tela e nao tem lastro no Catalogo". A tela usa pra nao afirmar um cadastro
           que nao existe e pra manter o botao que promove. */
        origem_anotacao?: boolean;
      };
      const dados = await api.get<HistResp[]>(`/api/cotacoes/${id}/historico-respostas`);
      // Antes de qualquer decisao sobre preencher: registrar de onde cada par veio. Roda nos DOIS
      // modos — no espelho da abertura a funcao nao cria linha nenhuma, mas o selo da anotacao
      // precisa aparecer na linha que ja esta salva, e e' aqui que ele se sabe.
      setParesAnotacao(new Set(
        dados.filter((r) => r.origem_anotacao).map((r) => `${r.cotacao_item_id}-${r.fornecedor_id}`),
      ));
      let preenchidos = 0;
      // Separado do `preenchidos` (que so conta o que muda VALOR, pro toast): carimbar a filial
      // numa linha antiga nao e "atualizacao de catalogo", mas precisa ir pro estado — senao o
      // selo da UF nunca aparece.
      let mudouEstado = false;
      // Preco vem do catalogo (fornecedor_catalogo.preco_unitario e decimal 12,4) e pode ter ate
      // 4 casas. Preserva a precisao real ao trazer pra cotacao — nao arredonda pra 2 casas, que
      // esconderia uma correcao de centavo feita no Catalogo (min 2 casas pro padrao monetario).
      const fmtPrecoCat = fmtPrecoCatalogo;
      // No modo comparacao (Preco Emb so-leitura, DA-283) o catalogo manda: SOBRESCREVE. No modo
      // Inserir Cotacao o preco e editavel (captura de cotacao nova) — la so completa se estiver
      // vazio, pra nao apagar um preco recem-digitado.
      const podeSobrescrever = !inserirCotacaoForn;
      // Monta o proximo estado FORA do setState. Antes isso vivia dentro de `setRespostas(prev
      // => ...)` e o `preenchidos++` acontecia la dentro — mas o updater so roda no render
      // seguinte, entao o `if (preenchidos > 0)` logo abaixo lia SEMPRE zero: a tela enchia,
      // o toast dizia "Catalogo ja em dia" e o `setTemAlteracoes(true)` nunca disparava, o que
      // deixava o Salvar cinza com dado novo na tela — que sumia no F5 sem nunca ir ao banco.
      const prev = respostasRef.current;
      {
        const next = { ...prev };
        // O catalogo pode ter VARIAS ofertas pro mesmo par item×fornecedor (a CALDEIRA LEMES tem
        // "REFIL GAS R22 700G DUGOLD" e "RECARGA 900G FRIVEN" no mesmo produto). Agrupar antes de
        // mexer no estado e o que impede o bug antigo: cada oferta era tratada isolada, a primeira
        // criava a linha e a seguinte caia no ramo "ja existe" e SOBRESCREVIA SO O PRECO — nascia
        // uma linha com o codigo de uma e o valor da outra, que nao existe na vida real.
        const porChave: Record<string, typeof dados> = {};
        for (const r of dados) {
          const key = `${r.cotacao_item_id}-${r.fornecedor_id}`;
          (porChave[key] ||= []).push(r);
        }
        // Quantidades vem do banco como texto numerico ("10.000" = dez). NUNCA usar String() cru:
        // parseQtd("10.000") leria 10000 (heuristica de milhar) e inflaria a qtd ×1000 ao salvar.
        const linhaDoCatalogo = (r: (typeof dados)[number]): RespostaForm => ({
          ...RESP_VAZIA,
          filial_id: r.filial_id ?? null,
          filial_uf: r.filial_uf || null,
          filial_principal: r.filial_principal !== false,
          difal_pct: r.difal_pct ?? null,
          cod_produto_fornecedor: r.cod_produto_fornecedor || '',
          descricao_fornecedor: r.descricao_complementar || '',
          preco_atualizado_em: r.preco_atualizado_em ?? null,
          marca: r.marca || '',
          embalagem: r.embalagem || 'UN',
          quantidade_embalagem: Number(r.quantidade_embalagem) > 0 ? formatQtd(Number(r.quantidade_embalagem)) : '1',
          // Sem quantidade propria vinda do catalogo/ultima cotacao, a linha NASCE com a
          // quantidade do produto — mesma regra do espelho acima: a linha nova esta vazia,
          // entao preencher nao atropela nada. Sem isto a oferta que aparece DEPOIS de o
          // Ruan digitar a quantidade voltava a exigir a digitacao dele.
          quantidade_cotada: Number(r.quantidade_cotada) > 0
            ? formatQtd(Number(r.quantidade_cotada))
            : (itensRef.current.find((i) => i.id === r.cotacao_item_id)?.quantidade ?? '').toString().trim(),
          preco_principal: fmtPrecoCat(r.preco_principal),
          escolhida: false,
        });

        for (const [key, ofertas] of Object.entries(porChave)) {
          const atual = next[key];
          const vazia = !atual || (atual.length === 1 && !atual[0]!.cod_produto_fornecedor && !atual[0]!.descricao_fornecedor);

          if (vazia) {
            // Par sem linha nenhuma: no modo espelho nao ha o que espelhar, e preencher aqui seria
            // encher a grade sozinho na abertura. Fica pro botao.
            //
            // ⚠️ UMA excecao, aberta em 31/08/2026 a pedido dele: o par cujas ofertas vem TODAS da
            // anotacao do Novos Produtos. Ali nao preencher custa mais caro do que a regra protege
            // — a linha fica dizendo "Sem cadastro no Catalogo do fornecedor", com o botao de
            // cadastrar ao lado, sobre um item cujo preco o sistema JA conhece. Nao e' so friccao
            // de um clique: e a tela AFIRMANDO o que nao e' verdade ate alguem clicar.
            // ⛔ O preco a pagar, e ele e' real: linha de anotacao APAGADA de proposito volta no
            // proximo F5 — nao ha como distinguir "apagada" de "nunca preenchida". Decisao dele
            // com o efeito a vista; o caso de apagar e' raro, o de abrir e querer ver o preco e'
            // diario. O resto da regra ([DA-472]) segue intacto: oferta de CATALOGO em par vazio
            // continua esperando o botao.
            if (soEspelho && !ofertas.every((o) => o.origem_anotacao)) continue;
            // Traz TODAS as ofertas como linhas separadas. Com uma so, ela ja e a valida; com
            // varias, ninguem nasce eleito — a escolha e do comprador (a mais barata por unidade
            // costuma ser outra embalagem, nao um negocio melhor).
            const linhas = ofertas.map(linhaDoCatalogo);
            if (linhas.length === 1) linhas[0]!.escolhida = true;
            next[key] = linhas;
            preenchidos += linhas.length;
            mudouEstado = true;
            continue;
          }

          // Ja ha linha identificada: casa cada oferta com a linha do MESMO codigo e espelha do
          // catalogo (fonte canonica, DA-283/285) o PRECO, a QTD NA EMBALAGEM e a EMBALAGEM —
          // cod/desc/marca/qtd cotada digitados ficam intactos. A qtd na embalagem manda no
          // unitario (preco/qtd), entao vem junto: so puxar o preco deixava o unitario torto. A
          // EMBALAGEM (CX/UN/RL) e a unidade em que esse trio e lido — ela ficava de fora e a
          // celula so-leitura exibia pra sempre o valor com que a linha nasceu: trocar UN pra CX
          // no Catalogo nao chegava na cotacao por recarregar NEM por F5, e o rotulo errado ia
          // impresso no pedido que sai pro fornecedor. No modo Inserir Cotacao (preco/embalagem
          // editaveis) nada se sobrescreve, pra nao apagar o que a pessoa acabou de digitar.
          // Oferta do catalogo sem linha correspondente ENTRA como linha nova.
          const norm = (s: string) => (s || '').trim().toUpperCase().replace(/[.\s-]/g, '').replace(/^0+/, '') || '0';
          let linhas = [...atual!];
          const usadas = new Set<number>();
          // Casa por CODIGO + FILIAL (DA-391). So pelo codigo, as duas pernas de um fornecedor
          // com duas filiais (GO e MG tem o MESMO codigo) se atropelavam: a 1a casava com a linha
          // existente e a 2a, sem par, entrava como linha NOVA a cada recarregar. A linha antiga
          // ainda nao tem filial gravada — nesse caso o codigo sozinho decide, e ela adota a
          // filial da oferta que casou.
          const casa = (l: RespostaForm, r: (typeof dados)[number]) =>
            norm(l.cod_produto_fornecedor) === norm(r.cod_produto_fornecedor)
            && (l.filial_id == null || r.filial_id == null || l.filial_id === r.filial_id);
          for (const r of ofertas) {
            let idx = linhas.findIndex((l, i) => !usadas.has(i) && l.filial_id != null && l.filial_id === r.filial_id && casa(l, r));
            if (idx === -1) idx = linhas.findIndex((l, i) => !usadas.has(i) && casa(l, r));
            if (idx === -1) {
              // Oferta do catalogo que nao casa com nenhuma linha: entra como linha NOVA, tambem na
              // abertura. Desde a [DA-858] a rota manda UMA oferta por fornecedor (a de alteracao
              // mais recente) e ela SUBSTITUI as linhas do par — esperar o botao deixaria na tela o
              // codigo velho que ele pediu pra nao entrar mais.
              linhas.push(linhaDoCatalogo(r));
              usadas.add(linhas.length - 1);
              preenchidos++;
              mudouEstado = true;
              continue;
            }
            usadas.add(idx);
            const cur = linhas[idx]!;
            const jaTemPreco = cur.preco_principal.trim() !== '';
            // Perna sem preco no catalogo LIMPA a celula no modo comparacao — o catalogo e a
            // fonte (DA-283) e "sem preco" e informacao, nao ausencia de informacao. Deixar o
            // valor velho ali era o que fazia a filial de fora exibir um preco que ela nunca
            // cobrou. No modo Inserir Cotacao o preco e digitado a mao: nao se apaga nada.
            const precoDoCatalogo = r.preco_principal != null && Number(r.preco_principal) > 0
              ? fmtPrecoCat(r.preco_principal) : '';
            const novoPreco = podeSobrescrever
              ? precoDoCatalogo
              : (jaTemPreco ? cur.preco_principal : precoDoCatalogo);
            const novaQtd = podeSobrescrever && Number(r.quantidade_embalagem) > 0
              ? formatQtd(Number(r.quantidade_embalagem)) : cur.quantidade_embalagem;
            const novaEmb = podeSobrescrever && (r.embalagem || '').trim()
              ? r.embalagem.trim().toUpperCase() : cur.embalagem;
            const novaFilial = cur.filial_id ?? r.filial_id ?? null;
            const novaUf = cur.filial_uf ?? r.filial_uf ?? null;
            const novaPrincipal = cur.filial_id != null ? cur.filial_principal : r.filial_principal !== false;
            // A data da alteracao acompanha o preco que o espelho acabou de trazer. So exibicao:
            // fica fora do `preenchidos`, entao sozinha nao acende o Salvar.
            const novaAlteracao = r.preco_atualizado_em ?? null;
            if (novoPreco !== cur.preco_principal || novaQtd !== cur.quantidade_embalagem
              || novaEmb !== cur.embalagem
              || novaFilial !== cur.filial_id || novaUf !== cur.filial_uf
              || novaPrincipal !== cur.filial_principal
              || novaAlteracao !== (cur.preco_atualizado_em ?? null)) {
              linhas[idx] = {
                ...cur, preco_principal: novoPreco, quantidade_embalagem: novaQtd,
                embalagem: novaEmb,
                filial_id: novaFilial, filial_uf: novaUf, filial_principal: novaPrincipal,
                preco_atualizado_em: novaAlteracao,
              };
              mudouEstado = true;
              if (novoPreco !== cur.preco_principal || novaQtd !== cur.quantidade_embalagem
                || novaEmb !== cur.embalagem) preenchidos++;
            }
          }
          // [DA-858] So a oferta mais recente do fornecedor fica: as linhas que nao casaram com ela
          // (codigo trocado, marca antiga, codigo que nem existe mais no Catalogo) saem — nos DOIS
          // modos, e contam como mudanca pra acender o Salvar (e' o Salvar que as apaga do banco).
          const sobra = linhas.filter((_, i) => usadas.has(i));
          if (sobra.length > 0 && sobra.length < linhas.length) {
            preenchidos += linhas.length - sobra.length;
            mudouEstado = true;
            linhas = sobra.map((l) => ({ ...l, escolhida: true }));
          }
          // Daqui pra baixo so se mexe na COMPOSICAO da grade (colapsar duplicata, reeleger a
          // linha valida) — fora do alcance do espelho, que nunca tira nem promove linha sozinho.
          if (soEspelho) { next[key] = linhas; continue; }
          // Colapsa linhas com mesmo codigo normalizado + filial — artefato de grafias diferentes
          // do mesmo codigo (ex: '600300020' vs '0600300020') unificadas pela forma canonica.
          const vistosNorm = new Set<string>();
          const dupls: number[] = [];
          linhas.forEach((l, i) => {
            const k = `${norm(l.cod_produto_fornecedor || '')}|${l.filial_id ?? ''}`;
            if (vistosNorm.has(k)) dupls.push(i);
            else vistosNorm.add(k);
          });
          for (let i = dupls.length - 1; i >= 0; i--) { linhas.splice(dupls[i]!, 1); mudouEstado = true; }
          // Virou multipla agora? Entao a marca herdada da linha unica nao vale mais — ninguem
          // fica eleito ate o comprador escolher. Com uma linha so, ela e a valida.
          if (linhas.length === 1) linhas[0] = { ...linhas[0]!, escolhida: true };
          else if (atual!.length === 1 && linhas.length > 1) {
            for (let i = 0; i < linhas.length; i++) linhas[i] = { ...linhas[i]!, escolhida: false };
          }
          next[key] = linhas;
        }
        if (mudouEstado) setRespostas(next);
      }
      return preenchidos;
    }
  }, [id, inserirCotacaoForn]);

  const recarregarCatalogo = useCallback(async () => {
    if (!id) return;
    setRecarregandoCat(true);
    try {
      const preenchidos = await aplicarCatalogo(false);
      if (preenchidos > 0) {
        setTemAlteracoes(true);
        toast.sucesso(`${preenchidos} ${preenchidos === 1 ? 'item atualizado' : 'itens atualizados'} do catálogo`);
      } else {
        toast.aviso('Catálogo já em dia — nada a atualizar');
      }
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao recarregar catálogo');
    } finally {
      setRecarregandoCat(false);
    }
  }, [id, toast, aplicarCatalogo]);

  // DA-472 — cotacao ABERTA espelha o Catalogo sozinha ao abrir. A celula EMBAL. (e o Preco Emb, e
  // a Qtd na emb) e so-leitura porque quem manda e o Catalogo; enquanto o valor so se atualizava no
  // botao, a cotacao exibia pra sempre a copia com que a linha nasceu — trocar UN pra CX no
  // Catalogo nao chegava aqui nem por F5, e o rotulo errado saia impresso no pedido do fornecedor.
  // Espelho que espera clique nao e espelho: e um campo travado com dado velho.
  //
  // QUEM espelha e quem congela — decisao do Ruan (07/08), lida direto do menu "Mover para" da
  // lista: `aberta` e `em_analise` sao cotacao em ANDAMENTO e seguem o Catalogo; `aprovada`,
  // `finalizada` e `cancelada` sao documento FECHADO e congelam na foto de quando fecharam —
  // relatorio de meses atras nao pode mudar porque alguem corrigiu o cadastro hoje. `aprovada`
  // congela junto porque aprovar e' decidir com base num preco: o numero que ele aprovou tem que
  // continuar sendo o numero que ele viu. (A lista mora no topo do arquivo, fora do componente —
  // aqui dentro seria um array novo a cada render, e o `useEffect` abaixo o toma por dependencia.)
  const espelhouCatalogo = useRef<string | null>(null);
  useEffect(() => {
    if (!id || !cotacao || loading || inserirCotacaoForn) return;
    if (!ESPELHA_CATALOGO.includes(cotacao.status)) return;
    // Uma vez por cotacao aberta na tela — nao a cada re-render.
    if (espelhouCatalogo.current === String(id)) return;
    espelhouCatalogo.current = String(id);
    (async () => {
      try {
        const preenchidos = await aplicarCatalogo(true);
        if (preenchidos > 0) {
          // Marca sujo de proposito: a tela ja mostra o valor certo, mas o documento no banco ainda
          // guarda o velho — e e ELE que congela quando a cotacao for finalizada. Sem isto a tela
          // diria CX por semanas e o fecho gravaria UN. Uma vez salvo, a proxima abertura nao acha
          // diferenca e o Salvar volta a nascer cinza sozinho.
          setTemAlteracoes(true);
          toast.sucesso(`${preenchidos} ${preenchidos === 1 ? 'item atualizado' : 'itens atualizados'} do catálogo`);
        }
      } catch {
        // Catalogo fora do ar nao pode impedir de abrir a cotacao: a tela segue com o valor salvo,
        // e o botao continua ali pra tentar de novo.
      }
    })();
  }, [id, cotacao, loading, inserirCotacaoForn, aplicarCatalogo, toast]);

  // Snapshot localStorage debounce 300ms
  useEffect(() => {
    if (!jaCarregou.current || !id) return;
    if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current);
    snapshotTimerRef.current = setTimeout(() => {
      try {
        const snap = {
          v: 1,
          salvo_em: Date.now(),
          cotacao_id: id,
          formCotacao,
          itens: itens.map(payloadItemCotacao),
          respostas,
          totalCImpForn,
        };
        localStorage.setItem(RASCUNHO_KEY(id), JSON.stringify(snap));
      } catch {
        /* ignore */
      }
    }, 300);
    return () => {
      if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current);
    };
  }, [id, formCotacao, itens, respostas, totalCImpForn]);

  // Detecta rascunho mais novo
  useEffect(() => {
    if (!cotacao || !id) return;
    if (verificouRascunho.current) return;
    verificouRascunho.current = true;
    const snap = lerRascunho(id);
    if (!snap || String(snap.cotacao_id) !== String(id)) {
      if (snap) limparRascunho(id);
      return;
    }
    if (snapshotDifere(snap, formCotacao, itens, respostas, totalCImpForn)) {
      setRascunhoOferta(snap);
    } else {
      limparRascunho(id);
    }
  }, [cotacao, id, formCotacao, itens, respostas, totalCImpForn]);

  useEffect(() => {
    verificouRascunho.current = false;
    setRascunhoOferta(null);
  }, [id]);

  // Carrega marcas cadastradas pro AutocompleteMarca
  useEffect(() => {
    api.get<MarcaOpcao[]>('/api/marcas').then(setMarcas).catch(() => setMarcas([]));
  }, []);

  function handleMarcaSalva(novo: MarcaOpcao) {
    setMarcas((prev) => [...prev, novo].sort((a, b) => a.nome.localeCompare(b.nome)));
    if (novaMarcaCtx?.tipo === 'preferida') {
      atualizarItem(novaMarcaCtx.itemId, 'marca_preferida', novo.nome);
    } else if (novaMarcaCtx?.tipo === 'resp') {
      setResp(novaMarcaCtx.itemId, novaMarcaCtx.fornId, novaMarcaCtx.respIdx, 'marca', novo.nome);
    }
    setNovaMarcaCtx(null);
  }

  // Save no blur (focusout) — flush imediato
  useEffect(() => {
    const handler = (e: FocusEvent) => {
      const t = e.target;
      if (!t || !(t instanceof Element)) return;
      if (!t.matches('input,select,textarea')) return;
      if (itensFlushRef.current) {
        const fn = itensFlushRef.current;
        itensFlushRef.current = null;
        if (debounceFormRef.current) {
          clearTimeout(debounceFormRef.current);
          debounceFormRef.current = null;
        }
        void fn();
      }
    };
    document.addEventListener('focusout', handler, true);
    return () => document.removeEventListener('focusout', handler, true);
  }, []);

  // ============================================================
  // Lista de cotacoes (sidebar quando nao tem cotacao aberta)
  // ============================================================

  const carregarLista = useCallback(async () => {
    try {
      setLoadingLista(true);
      const r = await api.get<CotacaoLista[]>('/api/cotacoes', {
        query: { q: buscaLista || undefined, status: filtroStatus || undefined },
      });
      setListaCotacoes(r);
    } catch {
      /* ignore */
    } finally {
      setLoadingLista(false);
    }
  }, [buscaLista, filtroStatus]);

  useEffect(() => {
    if (buscaListaTimerRef.current) clearTimeout(buscaListaTimerRef.current);
    buscaListaTimerRef.current = setTimeout(carregarLista, 300);
    return () => {
      if (buscaListaTimerRef.current) clearTimeout(buscaListaTimerRef.current);
    };
  }, [carregarLista]);

  // ============================================================
  // Sessao expandidos
  // ============================================================

  useEffect(() => {
    if (!id) return;
    sessionStorage.setItem(`cotacao_${id}_expandidos`, JSON.stringify([...expandidos]));
  }, [expandidos, id]);

  function toggleExpandir(itemId: number) {
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }

  // ============================================================
  // Manipulacao de respostas
  // ============================================================

  function getResps(itemId: number, fornId: number): RespostaForm[] {
    const key = `${itemId}-${fornId}`;
    return respostas[key] || [{ ...RESP_VAZIA }];
  }

  function setResp(itemId: number, fornId: number, idx: number, campo: keyof RespostaForm, valor: any) {
    const key = `${itemId}-${fornId}`;
    setRespostas((prev) => {
      const arr = [...(prev[key] || [{ ...RESP_VAZIA }])];
      arr[idx] = { ...arr[idx]!, [campo]: valor };
      return { ...prev, [key]: arr };
    });
    setTemAlteracoes(true);
  }

  // ------------------------------------------------------------------------------------------
  // Edicao do preco pela cotacao — grava no CATALOGO, que segue sendo a fonte unica
  //
  // Historia: a caixinha nasceu em 22/07 ([DA-355]), saiu em 22/09 ([DA-740], "eu nunca altero
  // preco na tela de cotacao") e voltou em 28/10/2026 a pedido dele, com a mesma condicao: "quero
  // que o preco continue tendo apenas uma fonte, o Catalogo. O que quero e que nossa cotacao consiga
  // editar o preco do catalogo". Por isso a caixinha grava no catalogo PRIMEIRO e so com a resposta
  // dele rebate na linha — a linha nunca tem um preco que o catalogo nao tenha.
  // O numero digitado e' o preco do FORNECEDOR (sem DIFAL); a celula recalcula o DIFAL por cima.
  // So em cotacao que espelha o catalogo (aberta/em analise): a fechada e' foto e nao muda.
  const [edicaoPreco, setEdicaoPreco] = useState<AlvoEdicaoPreco | null>(null);
  const [salvandoPreco, setSalvandoPreco] = useState(false);
  const podeEditarPreco = podeEditarCatalogo && ESPELHA_CATALOGO.includes(cotacao?.status ?? '');

  function abrirEditorPreco(
    item: ItemCotacao,
    forn: FornecedorVinculado,
    respIdx: number,
    rect: { top: number; left: number; bottom: number; right: number; width: number },
  ) {
    if (!podeEditarPreco || itemInativo(item)) return;
    const resp = getResps(item.id, forn.fornecedor_id)[respIdx];
    if (!resp) return;
    // Linha sem identidade nao tem onde gravar: o catalogo precisa de codigo OU de um HUB (produto
    // do cadastro ou o item da prateleira nova — [DA-309]/[DA-610]).
    if (!resp.cod_produto_fornecedor && item.produto_id == null && item.novo_produto_id == null) {
      toast.erro('Este item ainda não tem cadastro no Catálogo deste fornecedor — cadastre pelo Catálogo do fornecedor.');
      return;
    }
    setEdicaoPreco({
      itemId: item.id,
      fornId: forn.fornecedor_id,
      respIdx,
      rect,
      fornNome: getNomeFornecedor(forn),
      codigo: resp.cod_produto_fornecedor || '',
      descricao: resp.descricao_fornecedor || '',
      embalagem: resp.embalagem || '',
      precoEmb: resp.preco_principal || '',
      qtdEmb: resp.quantidade_embalagem || '1',
      filialUf: resp.filial_uf ?? null,
      difalPct: resp.difal_pct ?? null,
    });
  }

  // Embal. e Qtd na Emb. abrem a linha no CATALOGO DO FORNECEDOR (pedido dele, 31/10/2026) — o mesmo
  // deep-link do lapis da Notas de Entrada: a janela de edicao ja abre, e fecha-la volta pra ca. Na
  // volta a tela remonta e o espelho do catalogo (DA-472) traz o que mudou la. Alteracao nao salva
  // se perderia na saida, entao pergunta e salva antes.
  async function abrirNoCatalogo(fornecedorId: number, codigo: string) {
    if (temAlteracoes) {
      const ok = await dialog.confirmar('Esta cotação tem alterações não salvas. Salvar agora e abrir o Catálogo?');
      if (!ok) return;
      const salvou = await salvarTudoRef.current?.();
      if (salvou === false) return;
    }
    navigate(`/catalogo-fornecedor?${new URLSearchParams({ fornecedor: String(fornecedorId), codigo })}`);
  }

  async function confirmarPrecoCatalogo(v:{ precoEmb: number; qtdEmb: number; difalPct?: number | null }) {
    const alvo = edicaoPreco;
    if (!alvo) return;
    const item = itens.find((i) => i.id === alvo.itemId);
    // A linha sabe a filial (DA-391): sem ela o backend recusaria com 409 os itens de duas
    // inscricoes (MARTINS/TAMBASA/COFER), por nao saber em qual CNPJ gravar.
    const filialDaLinha = respostas[`${alvo.itemId}-${alvo.fornId}`]?.[alvo.respIdx]?.filial_id ?? null;
    setSalvandoPreco(true);
    try {
      const r = await api.put<{ ok: boolean; perna?: { preco_atualizado_em?: string | null } }>('/api/fornecedor-catalogo/preco', {
        fornecedor_id: alvo.fornId,
        filial_id: filialDaLinha,
        codigo_fornecedor: alvo.codigo || null,
        produto_id: item?.produto_id ?? null,
        novo_produto_id: item?.novo_produto_id ?? null,
        embalagem: alvo.embalagem || null,
        preco_embalagem: v.precoEmb,
        quantidade_embalagem: v.qtdEmb,
        cotacao_id: Number(id),
        // So vai quando a aliquota mudou: sem a chave a rota nao mexe nela ([DA-860]).
        ...('difalPct' in v ? { difal_pct: v.difalPct } : {}),
      });
      // So DEPOIS do catalogo aceitar a linha acompanha — o mesmo espelho que a abertura da tela
      // faz (DA-472). `setResp` acende o Salvar: a copia da cotacao so grava no botao, e um F5
      // antes disso refaz o espelho do catalogo de qualquer jeito.
      setResp(alvo.itemId, alvo.fornId, alvo.respIdx, 'preco_principal', fmtPrecoCatalogo(v.precoEmb));
      setResp(alvo.itemId, alvo.fornId, alvo.respIdx, 'quantidade_embalagem', formatQtd(v.qtdEmb));
      // A coluna Alteracao le a data do catalogo; sem isto ela seguiria com a data velha ate o F5.
      if (r?.perna?.preco_atualizado_em) {
        setResp(alvo.itemId, alvo.fornId, alvo.respIdx, 'preco_atualizado_em', r.perna.preco_atualizado_em);
      }
      // A aliquota nao e' da cotacao (nao vai no Salvar): a linha so passa a calcular com a nova.
      if ('difalPct' in v) setResp(alvo.itemId, alvo.fornId, alvo.respIdx, 'difal_pct', v.difalPct ?? null);
      setEdicaoPreco(null);
      toast.sucesso('Preço gravado no Catálogo. Clique em Salvar para a cotação acompanhar.');
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'erro ao gravar o preço');
    } finally {
      setSalvandoPreco(false);
    }
  }

  // Edicao da DATA da compra pelo selo "últ. compra" (DA-368) — mesma peca da Lista de Produtos.
  // Grava por PRODUTO (a marca atravessa cotacoes); rebate em TODOS os itens do mesmo produto na
  // tela com o valor que o backend devolve, sem esperar o Salvar (que e da cotacao).
  const [edicaoData, setEdicaoData] = useState<AlvoEdicaoData | null>(null);
  const [salvandoData, setSalvandoData] = useState(false);

  // Janelinha dos campos da COMPRA do cadastro (Unidade de Compra / Qtd na Emb / UN do conteudo),
  // aberta pelo clique nas celulas Embal./Qtd Emb da linha do produto (pedido do Ruan, 28/10/2026).
  // Mesmos perfis do Salvar da ficha — a rota recusa os outros, e porta que leva a 403 e' beco.
  // Grava no PRODUTO (nao na cotacao), entao rebate nas linhas do mesmo produto sem marcar a cotacao
  // como alterada — mesmo gesto da data da compra acima.
  const podeEditarCadastroProduto = ['admin', 'gerente', 'estoquista'].includes(usuario?.perfil ?? '');
  const [embalagemDe, setEmbalagemDe] = useState<ItemCotacao | null>(null);

  function aplicarEmbalagemGravada(produtoId: number, g: EmbalagemCompraGravada) {
    setItens((prev) => prev.map((it) => (
      it.produto_id === produtoId
        ? {
            ...it,
            // A coluna Embal. le `COALESCE(p.unidade_compra, ci.unidade_sigla, ...)` no backend:
            // com a unidade de compra apagada, o que aparece e' o que a linha ja tinha.
            unidade_sigla: g.unidade_compra || it.unidade_sigla,
            qtd_embalagem_padrao: g.qtd_embalagem_padrao,
            unidade_conteudo_embalagem: g.unidade_conteudo_embalagem,
          }
        : it
    )));
  }

  function abrirEditorData(
    item: ItemCotacao,
    rect: { top: number; left: number; bottom: number; right: number; width: number },
  ) {
    if (!item.ultima_compra_em) return;
    const alvo = alvoDaMarcaCompra(item);
    if (!alvo) return;
    setEdicaoData({
      produtoId: alvo.chave.produto_id ?? 0,
      novoProdutoId: alvo.chave.novo_produto_id ?? null,
      rect,
      dataAtualISO: item.ultima_compra_em,
      fornNome: item.ultima_compra_fornecedor_nome ?? null,
    });
  }

  async function confirmarDataCompra(dataISO: string) {
    const alvo = edicaoData;
    if (!alvo) return;
    setSalvandoData(true);
    try {
      const npId = alvo.novoProdutoId ?? null;
      const url = npId != null
        ? `/api/novos-produtos/${npId}/ultima-compra`
        : `/api/produtos/${alvo.produtoId}/ultima-compra`;
      const r = await api.patch<{ ultima_compra_em: string | null }>(url, { data: dataISO });
      setItens((prev) => prev.map((it) => (
        (npId != null ? it.novo_produto_id === npId : it.produto_id === alvo.produtoId)
          ? { ...it, ultima_compra_em: r.ultima_compra_em }
          : it
      )));
      setEdicaoData(null);
      toast.sucesso('Data da compra atualizada.');
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'erro ao gravar a data');
    } finally {
      setSalvandoData(false);
    }
  }

  function removeResp(itemId: number, fornId: number, idx: number) {
    const key = `${itemId}-${fornId}`;
    setRespostas((prev) => {
      const arr = [...(prev[key] || [])];
      if (arr.length <= 1) return prev;
      arr.splice(idx, 1);
      return { ...prev, [key]: arr };
    });
  }

  /* D / F / I — leem e escrevem na PROPRIA linha da resposta (migration 0162). Marcar acende o Salvar; e o Salvar que grava, junto com o
     resto da linha, no mesmo `POST /respostas`. Nao ha endpoint proprio: sao campos da
     resposta, nao uma acao a parte. */
  function decisaoDaResposta(itemId: number, fornId: number, respIdx: number) {
    return respostas[`${itemId}-${fornId}`]?.[respIdx];
  }

  function alternarDecisao(
    itemId: number,
    fornId: number,
    respIdx: number,
    campo: 'desconsiderada' | 'forcada' | 'oculta_pdf',
  ) {
    const key = `${itemId}-${fornId}`;
    setRespostas((prev) => {
      const arr = prev[key];
      if (!arr?.[respIdx]) return prev;
      return { ...prev, [key]: arr.map((l, i) => (i === respIdx ? { ...l, [campo]: !l[campo] } : l)) };
    });
    setTemAlteracoes(true);
  }

  function isForcarCompra(itemId: number, fornId: number, respIdx: number) {
    return !!decisaoDaResposta(itemId, fornId, respIdx)?.forcada;
  }

  function toggleForcarCompra(itemId: number, fornId: number, respIdx: number) {
    alternarDecisao(itemId, fornId, respIdx, 'forcada');
  }

  function isDesconsiderado(itemId: number, fornId: number, respIdx: number) {
    return !!decisaoDaResposta(itemId, fornId, respIdx)?.desconsiderada;
  }

  function toggleDesconsiderar(itemId: number, fornId: number, respIdx: number) {
    alternarDecisao(itemId, fornId, respIdx, 'desconsiderada');
  }

  // PDF default = incluido; `oculta_pdf: true` significa OCULTO (o checkbox da tela mostra o
  // inverso — "incluir no PDF" — e por isso aparece marcado quando isto e false).
  function isOcultoPdf(itemId: number, fornId: number, respIdx: number) {
    return !!decisaoDaResposta(itemId, fornId, respIdx)?.oculta_pdf;
  }

  function toggleOcultoPdf(itemId: number, fornId: number, respIdx: number) {
    alternarDecisao(itemId, fornId, respIdx, 'oculta_pdf');
  }

  function getItensComprados(): SplitComprado[] {
    const resultado: SplitComprado[] = [];
    for (const item of itensAtivos) {
      for (const forn of fornecedores) {
        const resps = getResps(item.id, forn.fornecedor_id);
        const temForcado = resps.some((_, ri) => isForcarCompra(item.id, forn.fornecedor_id, ri));
        if (temForcado) {
          resultado.push({
            item_id: item.id,
            fornecedor_id: forn.fornecedor_id,
            item_desc: item.produto_descricao || item.descricao || '',
            forn_nome: getNomeFornecedor(forn),
          });
          break;
        }
      }
    }
    return resultado;
  }

  function abrirSplitModal() {
    setMenuPrincipal(false);
    const comprados = getItensComprados();
    if (comprados.length === 0) {
      toast.erro(
        'Marque o checkbox "Forcar compra" ao lado do preco de pelo menos um item antes de separar.',
      );
      return;
    }
    setSplitComprados(comprados);
    setSplitModal('choose');
  }

  async function splitNovaCotacao() {
    setSplitModal(null);
    const porForn: Record<string, string[]> = {};
    for (const c of splitComprados) {
      if (!porForn[c.forn_nome]) porForn[c.forn_nome] = [];
      porForn[c.forn_nome]!.push(c.item_desc);
    }
    const resumo = Object.entries(porForn)
      .map(([nome, items]) => `${nome}: ${items.length} item(ns)`)
      .join('\n');
    if (
      !window.confirm(
        `Separar ${splitComprados.length} item(ns) em nova(s) cotacao(oes)?\n\n${resumo}\n\nOs itens serao removidos desta cotacao.`,
      )
    )
      return;

    setSplitLoading(true);
    try {
      if (salvarTudoRef.current) await salvarTudoRef.current();
      const data = await api.post<{
        novas_cotacoes: { numero: string; qtd_itens: number }[];
      }>(`/api/cotacoes/${id}/split`, {
        itens: splitComprados.map((c) => ({ item_id: c.item_id, fornecedor_id: c.fornecedor_id })),
      });
      const nums = data.novas_cotacoes.map((c) => c.numero).join(', ');
      const totalSeparados = data.novas_cotacoes.reduce((s, c) => s + c.qtd_itens, 0);
      if (totalSeparados >= itens.length) {
        window.alert(
          `Todos os itens foram separados.\nCotacao(oes) criada(s): ${nums}\n\nEsta cotacao foi finalizada.`,
        );
        navigate('/cotacoes');
        return;
      }
      toast.sucesso(`Cotacao(oes) criada(s): ${nums}`);
      // O "forcar compra" e quem elegeu estes itens, e eles acabaram de sair daqui (o split
      // APAGA o cotacao_itens da origem). Nao ha mais o que limpar a mao: as marcas vivem na
      // linha e o `carregarDados` rele o que sobrou. A cotacao nova nasce sem marca — o INSERT
      // do split nao copia as colunas de decisao, entao caem no default false.
      carregarDados();
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'erro ao separar itens');
    } finally {
      setSplitLoading(false);
    }
  }

  async function abrirSplitSelecionar() {
    setSplitModal('select');
    setSplitCotacoesLoading(true);
    try {
      const data = await api.get<CotacaoLista[]>('/api/cotacoes?status=aberta');
      setSplitCotacoes(data.filter((c) => String(c.id) !== String(id)));
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'erro ao carregar cotacoes');
      setSplitModal(null);
    } finally {
      setSplitCotacoesLoading(false);
    }
  }

  async function splitParaCotacaoExistente(cotDestino: CotacaoLista) {
    setSplitModal(null);
    if (
      !window.confirm(
        `Mover ${splitComprados.length} item(ns) para ${cotDestino.numero}?\n\nOs itens serao removidos desta cotacao.`,
      )
    )
      return;

    setSplitLoading(true);
    try {
      if (salvarTudoRef.current) await salvarTudoRef.current();
      const data = await api.post<{
        total_movidos: number;
        cotacao_destino: { numero: string };
      }>(`/api/cotacoes/${id}/split-to-existing`, {
        itens: splitComprados.map((c) => ({ item_id: c.item_id, fornecedor_id: c.fornecedor_id })),
        cotacao_destino_id: cotDestino.id,
      });
      if (data.total_movidos >= itens.length) {
        window.alert(
          `Todos os itens foram movidos para ${data.cotacao_destino.numero}.\n\nEsta cotacao foi finalizada.`,
        );
        navigate('/cotacoes');
        return;
      }
      toast.sucesso(`${data.total_movidos} item(ns) movido(s) para ${data.cotacao_destino.numero}`);
      carregarDados();
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'erro ao mover itens');
    } finally {
      setSplitLoading(false);
    }
  }

  // Mover UM item pra outra cotacao (leva TODOS os fornecedores) — abre o seletor de destino.
  async function abrirMoverItem(item: { id: number; produto_descricao?: string | null; descricao?: string | null }) {
    setMoverAlvo({ id: item.id, desc: item.produto_descricao || item.descricao || '' });
    setMoverCotacoesLoading(true);
    try {
      const data = await api.get<CotacaoLista[]>('/api/cotacoes?status=aberta');
      setMoverCotacoes(data.filter((c) => String(c.id) !== String(id)));
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'erro ao carregar cotacoes');
      setMoverAlvo(null);
    } finally {
      setMoverCotacoesLoading(false);
    }
  }

  async function confirmarMoverItem(cotDestino: CotacaoLista) {
    if (!moverAlvo) return;
    const alvo = moverAlvo;
    setMoverAlvo(null);
    if (!window.confirm(`Mover "${alvo.desc}" para ${cotDestino.numero}?\n\nO item sai desta cotacao levando todos os fornecedores.`)) return;
    setMoverEnviando(true);
    try {
      // Salva o estado atual antes (o item precisa estar gravado pra migrar as respostas certas).
      if (salvarTudoRef.current) await salvarTudoRef.current();
      const data = await api.post<{ cotacao_destino: { numero: string }; origem_finalizada: boolean }>(
        `/api/cotacoes/${id}/mover-item`,
        { item_id: alvo.id, cotacao_destino_id: cotDestino.id },
      );
      if (data.origem_finalizada) {
        window.alert(`Item movido para ${data.cotacao_destino.numero}.\n\nEsta cotacao ficou sem itens e foi finalizada.`);
        navigate('/cotacoes');
        return;
      }
      toast.sucesso(`Item movido para ${data.cotacao_destino.numero}`);
      carregarDados();
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'erro ao mover item');
    } finally {
      setMoverEnviando(false);
    }
  }

  // ============================================================
  // Calculos de ranking
  // ============================================================

  // PRECO DA EMBALAGEM A PAGAR = o do fornecedor + DIFAL da filial de fora ([DA-727]). Decisao dele:
  // a cotacao mostra a embalagem COM DIFAL e o unitario fracionado dela. E' ESTE numero que decide
  // o menor preco, os totais e o vencedor — sem ele a filial de MG parecia mais barata do que sai.
  // O que se EDITA e se GRAVA continua sendo o preco do fornecedor (resp.preco_principal).
  function precoEmbPagar(resp: RespostaForm): number {
    const pp = parseQtd(resp.preco_principal);
    return precoEmbComDifal(Number.isFinite(pp) ? pp : 0, resp.filial_uf, resp.difal_pct);
  }

  function calcPrecoUnitario(resp: RespostaForm): number {
    const qe = parseQtd(resp.quantidade_embalagem);
    const qeN = Number.isFinite(qe) && qe > 0 ? qe : 1;
    return precoEmbPagar(resp) / qeN;
  }

  // O unitario do FORNECEDOR, sem DIFAL: o que sai nos documentos que vao pra ele (texto e PDF do
  // pedido) e o que a captura de uma cotacao nova mostra ao lado do preco digitado.
  function calcPrecoUnitarioFornecedor(resp: RespostaForm): number {
    const pp = parseQtd(resp.preco_principal);
    const qe = parseQtd(resp.quantidade_embalagem);
    const ppN = Number.isFinite(pp) ? pp : 0;
    const qeN = Number.isFinite(qe) && qe > 0 ? qe : 1;
    return ppN / qeN;
  }

  // Os dois numeros do card do fornecedor (pedido do Ruan, 30/10/2026: "o da esquerda nosso preco
  // de custo vezes a quantidade da cotacao, o da direita o preco do fornecedor buscado do
  // catalogo"). Antes eram "Soma" (preco dele x caixas) e "Total Forn." (Tot Emb da linha DELE x
  // Preco Emb — com o Tot Emb no 1 do default, saia o preco de uma embalagem de cada item).

  // Nosso custo de TODOS os itens da cotacao (escolha dele), com ou sem preco do fornecedor.
  function custoDaCotacao(): number {
    return itensAtivos.reduce((s, item) => s + custoLojaDoItem(item), 0);
  }

  // O fornecedor: caixas x unidades da caixa do CADASTRO x unitario dele, sem DIFAL ([DA-727]).
  // Com 2+ linhas dele no mesmo item (marcas/embalagens), conta a mais barata que nao esta
  // desconsiderada — somar as duas cobraria o item duas vezes.
  function totalFornecedor(fornId: number): number {
    let total = 0;
    for (const item of itensAtivos) {
      const qtdItem = parseFloat(item.quantidade) || 0;
      let melhor = 0;
      getResps(item.id, fornId).forEach((r, ri) => {
        if (isDesconsiderado(item.id, fornId, ri)) return;
        const uni = calcPrecoUnitarioFornecedor(r);
        if (uni <= 0) return;
        const t = uni * qtdItem * conteudoDaCaixaDoItem(item.qtd_embalagem_padrao, r.quantidade_embalagem);
        if (melhor === 0 || t < melhor) melhor = t;
      });
      total += melhor;
    }
    return total;
  }

  // O contador de itens vencidos por fornecedor vive no modal "Fornecedores Convidados" (DA-430).
  // A versao que morava aqui ignorava as marcas D (desconsiderar) e F (forcar) e discordava dele.

  // ============================================================
  // Marcar comprados (DA-368) — a marca de "compra em aberto" mora no PRODUTO
  // ============================================================
  //
  // Refinamento do dono: a marca e POR PRODUTO escolhendo o fornecedor de quem comprou — que
  // PODE nao ser o vencedor de amarelo (as vezes se compra do fornecedor errado por prazo,
  // relacionamento, etc). Por isso o controle e por CELULA (produto x fornecedor), nao mais
  // um botao em lote que so marcava quem estava vencendo. O botao antigo + `vencedorDoItem`
  // foram removidos — o ranking nao decide mais quem marcar, o clique do comprador decide.

  // Marca/desmarca a celula produto x fornecedor. So um fornecedor por produto fica marcado:
  // o backend SOBRESCREVE `ultima_compra_fornecedor_id`, entao marcar a celula B apaga a marca
  // da celula A do mesmo produto sozinho — o estado local abaixo reflete isso.
  async function marcarCompradoCelula(item: ItemCotacao, forn: FornecedorVinculado, comprado: boolean) {
    const alvo = alvoDaMarcaCompra(item);
    if (!alvo) return;
    try {
      await api.post(`/api/cotacoes/${id}/marcar-comprados`, {
        ...alvo.ids,
        fornecedor_id: forn.fornecedor_id,
        comprado,
        // A qtd e o Tot Emb, em caixas (DA-864): o selo "Chegando" diz "3 CX".
        qtds: [{
          ...alvo.chave,
          qtd: Number(item.quantidade) || 0,
          // A caixa e a do CADASTRO quando ele tem Qtd na Emb (30/10/2026); senao, a do fornecedor.
          emb: (temCaixaNoCadastro(item.qtd_embalagem_padrao) && item.unidade_sigla)
            || getResps(item.id, forn.fornecedor_id).find((r) => (r.embalagem ?? '').trim())?.embalagem || item.unidade_sigla || null,
        }],
      });
      if (comprado) {
        const agora = new Date().toISOString();
        const nomeForn = getNomeFornecedor(forn);
        setItens((prev) => prev.map((it) =>
          it.id === item.id
            ? { ...it, compra_em_aberto: true, ultima_compra_em: agora, ultima_compra_fornecedor_id: forn.fornecedor_id, ultima_compra_fornecedor_nome: nomeForn }
            : it,
        ));
      } else {
        setItens((prev) => prev.map((it) =>
          it.id === item.id
            ? { ...it, compra_em_aberto: false, ultima_compra_em: null, ultima_compra_fornecedor_id: null, ultima_compra_fornecedor_nome: null }
            : it,
        ));
      }
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao marcar comprado');
    }
  }

  // Toggle por linha (desmarcar): desfaz uma marcacao feita por engano. Nao precisa de
  // fornecedor_id — o backend so zera os campos quando comprado=false.
  async function desmarcarComprado(item: ItemCotacao) {
    const alvo = alvoDaMarcaCompra(item);
    if (!alvo) return;
    try {
      await api.post(`/api/cotacoes/${id}/marcar-comprados`, {
        ...alvo.ids,
        comprado: false,
      });
      setItens((prev) => prev.map((it) =>
        it.id === item.id
          ? { ...it, compra_em_aberto: false, ultima_compra_em: null, ultima_compra_fornecedor_id: null, ultima_compra_fornecedor_nome: null }
          : it,
      ));
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao desmarcar');
    }
  }

  // ============================================================
  // Auto-save form do cabecalho (debounce 800ms)
  // ============================================================

  function handleFormChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = e.target;
    setTemAlteracoes(true);
    setFormCotacao((prev) => {
      const novo = { ...prev, [name]: name === 'observacoes' ? value.toUpperCase() : value };
      if (debounceFormRef.current) clearTimeout(debounceFormRef.current);
      debounceFormRef.current = setTimeout(() => {
        api.put(`/api/cotacoes/${id}`, novo).catch(() => undefined);
      }, 800);
      return novo;
    });
  }

  // ============================================================
  // Adicionar produto (busca debounced)
  // ============================================================

  useEffect(() => {
    if (buscaProduto.length < 2) {
      setResultadosProduto([]);
      setResultadosCatalogo([]);
      setResultadosNovos([]);
      return;
    }
    if (buscaTimeoutRef.current) clearTimeout(buscaTimeoutRef.current);
    buscaTimeoutRef.current = setTimeout(async () => {
      try {
        // Busca na loja INTEIRA pra adicionar produtos a cotacao. Antes filtrava so
        // pelo catalogo dos fornecedores convidados, mas isso travava o uso real:
        // se o fornecedor tem catalogo pequeno/vazio, nada aparecia (ex: NOVA CASA
        // com 1 produto → buscar "BROCA" dava 0). Cotacao serve justamente pra cotar
        // o que ainda nao tem preco; o item fora do catalogo cai no fluxo ja existente
        // "Sem cadastro no Catalogo → Cadastrar". O dropdown mostra quais fornecedores
        // tem cada produto na coluna `fornecedores`.
        //
        // O SEGUNDO balcao (22/08/2026): o Catalogo do Fornecedor. Ha item cuja identidade so
        // existe do lado do fornecedor — 61 deles em 22/08, os do checkbox "Exibir sem Codigo
        // Interno" — e ate aqui cotar um deles exigia cadastrar produto na loja antes. Agora eles
        // entram pelo CODIGO DO FORNECEDOR (a busca do catalogo ja normaliza: '403358', '0403358'
        // e '403.358' acham o mesmo item — DA-471).
        //
        // ⚠️ `sem_produto=1` nao e um filtro de conveniencia, e o que evita o item aparecer DUAS
        // vezes: quem tem produto interno ja vem pela busca de produtos acima.
        //
        // O TERCEIRO balcao: a prateleira NOVOS PRODUTOS (DA-606) — o item que o Ruan quer cotar e
        // que nao existe nem na loja nem no catalogo de ninguem. Entra pela DESCRICAO (o caminho
        // principal) e pelo codigo NP##### (a alternativa). So devolve item ATIVO: o ja comprado
        // ja aparece como produto, e mostrar os dois seria o sosia que a prateleira evita.
        const [data, cat, novos] = await Promise.all([
          api.get<ProdutoBusca[]>('/api/produtos', { query: { q: buscaProduto, com_inativos: '1' } }),
          api.get<ItemCatalogoBusca[]>('/api/fornecedor-catalogo/itens', {
            query: { q: buscaProduto, sem_produto: '1' },
          }).catch(() => [] as ItemCatalogoBusca[]),
          api.get<NovoProdutoBusca[]>('/api/novos-produtos/busca', {
            query: { q: buscaProduto },
          }).catch(() => [] as NovoProdutoBusca[]),
        ]);
        // O produto JA na cotacao FICA na lista, apagado e com o aviso (DA-468: ausencia nao se
        // distingue de inexistencia — em 15/10 o 0068 sumiu da busca e leu como "nao encontrou").
        // Quem impede a duplicata e' o clique, que leva ate a linha em vez de adicionar.
        setResultadosProduto((data || []).slice(0, 20));
        // O item do catalogo ja na cotacao sai da lista pela PROPRIA identidade — o produto nao
        // serve de chave aqui, porque ele nao tem produto nenhum.
        const jaCat = new Set(itens.map((i) => i.catalogo_item_id).filter((v): v is number => v != null));
        // Item do catalogo ligado a um NOVO PRODUTO (hub da DA-610) tambem sai: ele vem pela secao
        // Novos Produtos, que acha o NP pelo codigo/descricao de cada fornecedor ligado. Mesmo motivo
        // do `sem_produto=1` acima, pro segundo hub (29/10/2026: o NP00035 aparecia duas vezes e
        // entrava em dobro na cotacao).
        setResultadosCatalogo((cat || []).filter((c) => !jaCat.has(c.id) && c.novo_produto_id == null).slice(0, 20));
        const jaNovo = new Set(itens.map((i) => i.novo_produto_id).filter((v): v is number => v != null));
        setResultadosNovos((novos || []).filter((n) => !jaNovo.has(n.id)).slice(0, 20));
      } catch {
        /* ignore */
      }
    }, 300);
    return () => {
      if (buscaTimeoutRef.current) clearTimeout(buscaTimeoutRef.current);
    };
  }, [buscaProduto, itens]);

  // Produto que ja e' item desta cotacao: o clique na busca leva ate a linha dele (aberta), em
  // vez de adicionar de novo. Limpar a busca e' o que devolve a linha a grade, que ela filtra.
  function irParaItemDaCotacao(itemId: number) {
    setBuscaProduto('');
    if (ladoALado) trocarVista(false);
    setExpandidos((prev) => new Set(prev).add(itemId));
    requestAnimationFrame(() => {
      document.getElementById(`cot-item-${itemId}`)?.scrollIntoView({ block: 'center' });
    });
  }

  async function adicionarProduto(produto: ProdutoBusca) {
    if (!cotacao) return;
    if (produtoInativo(produto)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return; }
    if (debounceFormRef.current) clearTimeout(debounceFormRef.current);
    try {
      const atual = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
      const itensAtuais = (atual.itens || []).map(payloadItemCotacao);
      const novosItens = [
        ...itensAtuais,
        {
          produto_id: produto.id,
          quantidade: '1',
          unidade_sigla: produto.unidade_compra || produto.unidade_sigla || 'UN',
          marca_preferida: null,
        },
      ];
      await api.put(`/api/cotacoes/${id}`, {
        ...formCotacao,
        itens: novosItens,
        fornecedor_ids: fornecedores.map((f) => f.fornecedor_id),
      });
      const data = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
      setItens(data.itens);
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao adicionar produto');
    }
    limparBusca();
  }

  /**
   * Adiciona a cotacao um item que so existe no Catalogo do Fornecedor — sem produto do nosso
   * cadastro. A linha nasce com a DESCRICAO DO FORNECEDOR (decisao do Ruan, 22/08/2026: e o nome
   * que ele reconhece e o que sai impresso no pedido de compra) e com `catalogo_item_id` como
   * identidade.
   *
   * ⚠️ O item NAO pertence ao fornecedor de onde veio: assim que entra, a grade abre linha pra
   * TODOS os convidados, como qualquer item. A unica assimetria e que a coluna da empresa de
   * origem ja chega preenchida — e quem faz isso e o proprio espelho do catalogo (`aplicarCatalogo`),
   * a mesma maquina do botao "Recarregar catalogo". Preencher aqui na mao seria uma segunda copia
   * da regra, que envelheceria sozinha.
   */
  async function adicionarItemCatalogo(item: ItemCatalogoBusca) {
    if (!cotacao) return;
    if (debounceFormRef.current) clearTimeout(debounceFormRef.current);
    try {
      const atual = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
      const perna = (item.pernas || [])[0];
      const novosItens = [
        ...(atual.itens || []).map(payloadItemCotacao),
        {
          produto_id: null,
          catalogo_item_id: item.id,
          descricao_livre: item.descricao_fornecedor,
          quantidade: '1',
          // Sem produto do cadastro nao ha unidade de compra pra herdar: cai na embalagem da
          // oferta do fornecedor e, na falta dela, em UN.
          // ⚠️ `slice(0, 10)`: `fornecedor_catalogo.embalagem` e varchar(20) e `cotacao_itens.unidade_sigla`
          // e varchar(10) — sem o corte, embalagem longa derruba o Salvar com 500 (a mesma familia
          // do estouro de varchar da DA-559, que cancelou nota e nao devolveu estoque).
          unidade_sigla: (perna?.embalagem || 'UN').toUpperCase().slice(0, 10),
          marca_preferida: (item.marca || '').slice(0, 100) || null,
        },
      ];
      await api.put(`/api/cotacoes/${id}`, {
        ...formCotacao,
        itens: novosItens,
        fornecedor_ids: fornecedores.map((f) => f.fornecedor_id),
      });
      const data = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
      setItens(data.itens);
      // Traz a oferta da empresa de origem pra linha recem-criada. Falha aqui nao desfaz o item —
      // ele ja esta na cotacao, e o comprador digita o preco na mao ou clica em "Recarregar catalogo".
      try {
        if (await aplicarCatalogo(false)) setTemAlteracoes(true);
      } catch {
        toast.aviso('Item adicionado. O preço do catálogo não veio — use "Recarregar".');
      }
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao adicionar item do catálogo');
    }
    limparBusca();
  }

  function limparBusca() {
    setResultadosProduto([]);
    setResultadosCatalogo([]);
    setResultadosNovos([]);
    setBuscaProduto('');
  }

  /**
   * Adiciona a cotacao um item da prateleira NOVOS PRODUTOS (DA-606) — o que nao existe nem na loja
   * nem no catalogo de ninguem. Ele entra com a descricao que o Ruan cadastrou e com os campos que
   * a grade usa (unidade, embalagem, marca), pra a linha nascer preenchida em vez de em branco.
   *
   * ⚠️ **EMENDADO em 31/08/2026** — aqui se lia *"nao ha espelho de catalogo pra chamar: por
   * definicao este item nao tem oferta de fornecedor nenhum ainda"*, e deixou de ser verdade.
   * Ele PODE ter uma: a anotacao do proprio cadastro de Novos Produtos (fornecedor + codigo +
   * preco), que a rota `historico-respostas` passou a emitir. Entao o espelho roda aqui como ja
   * rodava no item do catalogo — e a linha daquele fornecedor nasce com o preco em vez de "Sem
   * cadastro". Os OUTROS convidados seguem nascendo vazios, que continua certo.
   */
  async function adicionarNovoProduto(np: NovoProdutoBusca) {
    if (!cotacao) return;
    if (debounceFormRef.current) clearTimeout(debounceFormRef.current);
    try {
      const atual = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
      const novosItens = [
        ...(atual.itens || []).map(payloadItemCotacao),
        {
          produto_id: null,
          novo_produto_id: np.id,
          descricao_livre: np.descricao,
          quantidade: '1',
          // A EMBALAGEM e' a unidade do item da prateleira ([DA-608] p.4).
          unidade_sigla: (np.embalagem || 'UN').toUpperCase().slice(0, 10),
          marca_preferida: (np.marca || '').slice(0, 100) || null,
        },
      ];
      await api.put(`/api/cotacoes/${id}`, {
        ...formCotacao,
        itens: novosItens,
        fornecedor_ids: fornecedores.map((f) => f.fornecedor_id),
      });
      const data = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
      setItens(data.itens);
      setTemAlteracoes(true);
      // Traz a anotacao da prateleira pra linha recem-criada. Falha aqui nao desfaz o item — ele ja
      // esta na cotacao, e o comprador clica em "Recarregar catalogo" (mesma saida do item vindo do
      // Catalogo, logo acima).
      try {
        await aplicarCatalogo(false);
      } catch {
        toast.aviso('Item adicionado. O preço do cadastro não veio — use "Recarregar".');
      }
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao adicionar o novo produto');
    }
    limparBusca();
  }

  async function removerItemCotacao(itemId: number) {
    if (debounceFormRef.current) clearTimeout(debounceFormRef.current);
    try {
      await api.delete(`/api/cotacoes/${id}/itens/${itemId}`);
      const data = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
      setItens(data.itens);
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao remover item');
    }
  }

  /** A "Qtd de Emb" do PRODUTO desce pros fornecedores daquele item — mas NUNCA por cima do que
   *  ja foi lancado (regra do Ruan, 2026-07-29): so preenche a celula VAZIA. Ele digitava a
   *  quantidade na linha do produto e repetia o mesmo numero em cada fornecedor; agora so corrige
   *  a variacao.
   *
   *  A celula que ainda repete o valor ANTERIOR do produto tambem e preenchida — ela e um campo
   *  que veio daqui e ninguem tocou, nao uma quantidade lancada. Sem essa clausula o recurso
   *  travava no ato de digitar: trocar 4 por 40 manda "4" (1o caractere, enche os fornecedores) e
   *  depois "40" (que ja encontra "4" e desiste) — o produto ficava 40 e os fornecedores 4. */
  function espelharQtdNosFornecedores(itemId: number, novo: string, anterior: string) {
    const nov = (novo || '').trim();
    const ant = (anterior || '').trim();
    setRespostas((prev) => {
      const next = { ...prev };
      let mudou = false;
      for (const forn of fornecedores) {
        const key = `${itemId}-${forn.fornecedor_id}`;
        const linhas = prev[key];
        if (!linhas) continue;
        const novas = linhas.map((l) => {
          const atual = (l.quantidade_cotada || '').trim();
          if (atual !== '' && atual !== ant) return l; // ja lancado a mao — nao encosta
          if (atual === nov) return l;
          mudou = true;
          return { ...l, quantidade_cotada: nov };
        });
        if (novas.some((l, i) => l !== linhas[i])) next[key] = novas;
      }
      return mudou ? next : prev;
    });
  }

  function atualizarItem(itemId: number, campo: keyof ItemCotacao, valor: any) {
    if (campo === 'quantidade') {
      espelharQtdNosFornecedores(itemId, String(valor ?? ''), String(itens.find((i) => i.id === itemId)?.quantidade ?? ''));
    }
    setItens((prev) => prev.map((i) => (i.id === itemId ? { ...i, [campo]: valor } : i)));
    setTemAlteracoes(true);
    const doSave = async () => {
      try {
        const atual = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
        const itensAtuais = (atual.itens || []).map((i) =>
          payloadItemCotacao(i.id === itemId ? { ...i, [campo]: valor } : i),
        );
        await api.put(`/api/cotacoes/${id}`, {
          ...formCotacao,
          itens: itensAtuais,
          fornecedor_ids: fornecedores.map((f) => f.fornecedor_id),
        });
      } catch {
        /* ignore */
      }
    };
    itensFlushRef.current = doSave;
    if (debounceFormRef.current) clearTimeout(debounceFormRef.current);
    debounceFormRef.current = setTimeout(async () => {
      await doSave();
      if (itensFlushRef.current === doSave) itensFlushRef.current = null;
    }, 800);
  }

  async function reordenarItens(novos: ItemCotacao[]) {
    setItens(novos);
    try {
      await api.patch(`/api/cotacoes/${id}/reordenar-itens`, {
        ordem: novos.map((it, i) => ({ id: it.id, ordem: i })),
      });
    } catch {
      /* ignore */
    }
  }

  function handleDragStart(idx: number, e: DragEvent<HTMLElement>) {
    setDragIdx(idx);
    const tr = (e.currentTarget as HTMLElement).closest('tr');
    if (tr) e.dataTransfer.setDragImage(tr, 16, 16);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  }

  function handleDragOver(idx: number, e: DragEvent<HTMLTableRowElement>) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragIdx === null || dragIdx === idx) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos: 'above' | 'below' = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    if (dragOverIdx !== idx || dragOverPos !== pos) {
      setDragOverIdx(idx);
      setDragOverPos(pos);
    }
  }

  function handleDragEnd() {
    setDragIdx(null);
    setDragOverIdx(null);
    setDragOverPos(null);
  }

  async function handleDrop(idx: number, e: DragEvent<HTMLTableRowElement>) {
    e.preventDefault();
    const fromIdx = dragIdx;
    if (fromIdx === null || fromIdx === idx) {
      handleDragEnd();
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const pos: 'above' | 'below' = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    let to = idx + (pos === 'below' ? 1 : 0);
    if (to > fromIdx) to -= 1;
    handleDragEnd();
    if (to === fromIdx) return;
    const novos = [...itens];
    const [movido] = novos.splice(fromIdx, 1);
    novos.splice(to, 0, movido!);
    await reordenarItens(novos);
  }

  // Reposicionar item uma casa pra cima/baixo pelos botoes (alternativa ao drag,
  // que trava em telas onde clicar-e-arrastar nao pega bem). idx = posicao na lista COMPLETA.
  async function moverItem(idx: number, delta: number) {
    const to = idx + delta;
    if (to < 0 || to >= itens.length) return;
    const novos = [...itens];
    const [movido] = novos.splice(idx, 1);
    novos.splice(to, 0, movido!);
    await reordenarItens(novos);
  }

  // ============================================================
  // Modal Fornecedores Convidados
  // ============================================================

  // A lista vem do backend pelo termo de busca (efeito abaixo). So abre o modal
  // e fixa os ja convidados como marcados.
  function abrirModalFornecedores() {
    setFornecedorIdsSelecionados(fornecedores.map((f) => f.fornecedor_id));
    setBuscaFornModal('');
    setModalFornecedores(true);
  }

  // Busca fornecedores no SERVIDOR (debounced) enquanto o modal esta aberto.
  // O filtro client-side antigo so via a fatia alfabetica de 500 baixada — fornecedor
  // depois do teto (ex: UNIVERSO/U) sumia da busca. O banco filtra por
  // razao/fantasia/cnpj/telefone alem do teto.
  useEffect(() => {
    if (!modalFornecedores) return;
    if (buscaFornModalTimerRef.current) clearTimeout(buscaFornModalTimerRef.current);
    const termo = buscaFornModal.trim();
    buscaFornModalTimerRef.current = setTimeout(async () => {
      try {
        const data = await api.get<FornecedorMin[]>('/api/fornecedores', {
          query: { busca: termo || undefined },
        });
        setTodosFornecedores(data);
      } catch (e: unknown) {
        toast.erro(e instanceof ApiError ? e.message : 'Erro ao carregar fornecedores');
      }
    }, 250);
    return () => { if (buscaFornModalTimerRef.current) clearTimeout(buscaFornModalTimerRef.current); };
  }, [modalFornecedores, buscaFornModal]);

  function toggleFornecedorSelecionado(fid: number) {
    setFornecedorIdsSelecionados((prev) =>
      prev.includes(fid) ? prev.filter((x) => x !== fid) : [...prev, fid],
    );
  }

  // Setas da janela: movem o fornecedor uma casa na ordem MANUAL ([DA-835]). So mexe no rascunho da
  // janela; quem grava e o Salvar dela (Cancelar descarta, como o resto da janela).
  function moverFornecedorModal(fid: number, delta: number) {
    setFornecedorIdsSelecionados((prev) => {
      const i = prev.indexOf(fid);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const novo = [...prev];
      [novo[i], novo[j]] = [novo[j]!, novo[i]!];
      return novo;
    });
  }

  async function salvarFornecedoresConvidados() {
    try {
      await api.put(`/api/cotacoes/${id}`, {
        ...formCotacao,
        itens: itens.map(payloadItemCotacao),
        fornecedor_ids: fornecedorIdsSelecionados,
      });
      // A ordem vale pra TODAS as cotacoes ([DA-835]) — rota propria, depois do PUT. Se falhar, a
      // lista de convidados ja foi gravada: avisa e segue, a ordem fica como estava.
      try {
        await api.post('/api/cotacoes/ordem-fornecedores', { fornecedor_ids: fornecedorIdsSelecionados });
      } catch (e: unknown) {
        toast.erro(e instanceof ApiError ? `Fornecedores salvos, mas a ordem não: ${e.message}` : 'Fornecedores salvos, mas a ordem não foi gravada');
      }
      const data = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
      setCotacao(data);
      setFornecedores(data.fornecedores);
      setItens(data.itens);
      const tMap: Record<number, string> = {};
      for (const f of data.fornecedores) {
        tMap[f.fornecedor_id] =
          f.total_c_imp != null
            ? Number(f.total_c_imp).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '100,00';
      }
      setTotalCImpForn(tMap);
      setModalFornecedores(false);
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao salvar fornecedores');
    }
  }

  // ============================================================
  // Total c Imp por fornecedor
  // ============================================================

  async function salvarTotalCImpForn(fornId: number, valor: string) {
    const num = parseFloat(String(valor).replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(num) || num <= 0) {
      const f = fornecedores.find((x) => x.fornecedor_id === fornId);
      const anterior = f?.total_c_imp != null ? Number(f.total_c_imp) : 100;
      setTotalCImpForn((prev) => ({
        ...prev,
        [fornId]: anterior.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      }));
      toast.erro('Total c Imp deve ser maior que zero');
      return;
    }
    try {
      await api.patch(`/api/cotacoes/${id}/fornecedor/${fornId}`, { total_c_imp: num });
      setFornecedores((prev) =>
        prev.map((x) => (x.fornecedor_id === fornId ? { ...x, total_c_imp: String(num) } : x)),
      );
      setTotalCImpForn((prev) => ({
        ...prev,
        [fornId]: num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      }));
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao salvar total c imp');
    }
  }

  // ============================================================
  // Limpar respostas dum fornecedor
  // ============================================================

  async function limparRespostasFornecedor(forn: FornecedorVinculado) {
    try {
      await api.delete(`/api/cotacoes/${id}/respostas/${forn.fornecedor_id}`);
      setRespostas((prev) => {
        const next: Record<string, RespostaForm[]> = {};
        for (const key of Object.keys(prev)) {
          if (!key.endsWith(`-${forn.fornecedor_id}`)) next[key] = prev[key]!;
        }
        return next;
      });
      setConfirmarLimparForn(null);
      toast.sucesso(`Respostas de ${getNomeFornecedor(forn)} foram limpas`);
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao limpar respostas');
    }
  }

  // ============================================================
  // Salvar tudo (auto-save respostas)
  // ============================================================

  // Devolve se gravou: quem sai da tela depois de salvar (abrir no Catalogo) so sai com sucesso.
  const salvarTudo = useCallback(async (): Promise<boolean> => {
    if (!fornecedores.length || !itens.length) return true;
    setSalvando(true);
    try {
      // "Ao clicar em Salvar, o sistema salva TUDO" (pedido dele, 22/09/2026). A quantidade do item
      // tem auto-save proprio, com 800ms de espera: quem digitava e clicava em Salvar no mesmo
      // impulso mandava as RESPOSTAS primeiro e a quantidade depois — duas gravacoes fora de ordem
      // pro mesmo gesto, e o Salvar dizendo "salva" sobre algo que ainda estava na fila. O pendente
      // vai antes, e o `await` garante a ordem.
      if (itensFlushRef.current) {
        const flush = itensFlushRef.current;
        itensFlushRef.current = null;
        if (debounceFormRef.current) {
          clearTimeout(debounceFormRef.current);
          debounceFormRef.current = null;
        }
        await flush();
      }
      await Promise.all(
        fornecedores.map(async (forn) => {
          const fornecedor_id = forn.fornecedor_id;
          const respostasArr: any[] = [];
          for (const item of itens) {
            const key = `${item.id}-${fornecedor_id}`;
            const linhas = dedupLinhas(respostas[key] || [{ ...RESP_VAZIA }]);
            for (const resp of linhas) {
              const qProduto = parseQtd(resp.quantidade_produto);
              const qEmb = parseQtd(resp.quantidade_embalagem);
              const qCot = parseQtd(resp.quantidade_cotada);
              const pp = parseQtd(resp.preco_principal);
              respostasArr.push({
                cotacao_item_id: item.id,
                // DA-391 — sem a filial, o F5 devolvia as duas pernas com o preco de uma so.
                filial_id: resp.filial_id ?? null,
                cod_produto_fornecedor: resp.cod_produto_fornecedor,
                descricao_complementar: resp.descricao_fornecedor,
                marca: resp.marca,
                embalagem: resp.embalagem,
                quantidade_produto: Number.isFinite(qProduto) && qProduto > 0 ? qProduto : 1,
                quantidade_embalagem: Number.isFinite(qEmb) && qEmb > 0 ? qEmb : 1,
                quantidade_cotada: Number.isFinite(qCot) && qCot > 0 ? qCot : 1,
                preco_principal: Number.isFinite(pp) && pp > 0 ? pp : 0,
                ipi_percentual: resp.ipi_percentual,
                observacoes: resp.observacoes,
                escolhida: linhas.length === 1 ? true : !!resp.escolhida,
                desconsiderada: !!resp.desconsiderada,
                forcada: !!resp.forcada,
                oculta_pdf: !!resp.oculta_pdf,
              });
            }
          }
          await api.post(`/api/cotacoes/${id}/respostas`, { fornecedor_id, respostas: respostasArr });
        }),
      );
      // A confirmacao era um "✓ Salvo" em cima do botao; o rotulo saiu do cabecalho a pedido do Ruan
      // (15/09/2026) e ela virou toast — sem isso salvar nao teria retorno nenhum alem do botao apagar.
      toast.sucesso('Cotação salva');
      setTemAlteracoes(false);
      limparRascunho(id);
      // A faixa "Edicao nao salva detectada" tem que ir embora JUNTO: `limparRascunho` so apaga o
      // localStorage, e ela e desenhada por `rascunhoOferta`, que vive em memoria. Sem esta linha
      // ela sobrevivia ao Salvar afirmando que ha alteracoes "que nao chegaram a ser salvas" — e o
      // botao Restaurar dela jogaria o retrato velho por cima do que acabou de ser gravado.
      setRascunhoOferta(null);
      return true;
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao salvar respostas');
      return false;
    } finally {
      setSalvando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fornecedores, itens, respostas, id]);

  useEffect(() => {
    salvarTudoRef.current = salvarTudo;
  }, [salvarTudo]);


  // ============================================================
  // Restaurar rascunho
  // ============================================================

  function restaurarRascunho() {
    if (!rascunhoOferta) return;
    const snap = rascunhoOferta;
    if (snap.formCotacao) setFormCotacao(snap.formCotacao);
    // ⛔ A LISTA de itens do rascunho nao vai pro servidor — so os campos que se digitam na linha.
    // O rascunho e um retrato do instante em que a tela abriu, e esta faixa fica na tela ate alguem
    // clicar (no caso real, 20 HORAS). Quem cria ou remove item fala com o servidor no ATO (PUT+GET
    // ao adicionar, DELETE ao remover), entao a lista nunca esta "nao salva": mandar a do rascunho
    // fazia o `reconciliarItensCotacao` APAGAR todo item nascido depois dele, com as respostas junto
    // por cascata — e o `salvarTudo` que vem 100ms adiante morria em 500 (FK) apontando pros itens
    // recem-apagados. Foi assim que a COT-2026-00058 perdeu 5 dos 7 itens em 22/09/2026.
    const itensMesclados = Array.isArray(snap.itens)
      ? itens.map((it) => {
          const s = snap.itens.find((x: any) => Number(x.id) === Number(it.id));
          return s
            ? {
                ...it,
                marca_preferida: s.marca_preferida ?? it.marca_preferida,
                quantidade: s.quantidade ?? it.quantidade,
                unidade_sigla: s.unidade_sigla ?? it.unidade_sigla,
              }
            : it;
        })
      : itens;
    setItens(itensMesclados);
    if (snap.respostas) setRespostas(snap.respostas);
    if (snap.totalCImpForn) setTotalCImpForn(snap.totalCImpForn);
    setRascunhoOferta(null);
    setTimeout(async () => {
      try {
        await api.put(`/api/cotacoes/${id}`, {
          ...snap.formCotacao,
          // Lista vazia aqui NUNCA e intencao de restaurar rascunho — e tela que ainda nao carregou.
          // O backend le `itens: []` como "apague todos", entao ela nao viaja: sem a chave, ele nem
          // entra no ramo de reconciliacao.
          ...(itensMesclados.length ? { itens: itensMesclados.map((i) => payloadItemCotacao(i)) } : {}),
          fornecedor_ids: fornecedores.map((f) => f.fornecedor_id),
        });
      } catch {
        /* ignore */
      }
      void salvarTudoRef.current?.();
    }, 100);
  }

  function descartarRascunho() {
    limparRascunho(id);
    setRascunhoOferta(null);
  }

  // ============================================================
  // Modal IA — handlers
  // ============================================================

  const handleAdicionarItemImportacao = useCallback(
    async (produto: any): Promise<number | null> => {
      const atual = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
      const itensAtuais = atual.itens || [];
      const novosItens = [
        ...itensAtuais.map(payloadItemCotacao),
        {
          produto_id: produto.id,
          quantidade: '1',
          unidade_sigla: produto.unidade_compra || produto.unidade_sigla || 'UN',
          marca_preferida: null,
        },
      ];
      await api.put(`/api/cotacoes/${id}`, {
        ...formCotacao,
        itens: novosItens,
        fornecedor_ids: fornecedores.map((f) => f.fornecedor_id),
      });
      const data = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
      setItens(data.itens);
      const novoItem = data.itens.find((i) => i.produto_id === produto.id);
      return novoItem?.id ?? null;
    },
    [id, formCotacao, fornecedores],
  );

  const handleRemoverItemImportacao = useCallback(
    async (cotacaoItemId: number) => {
      await api.delete(`/api/cotacoes/${id}/itens/${cotacaoItemId}`);
      const data = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
      setItens(data.itens);
      return true;
    },
    [id],
  );

  const handleAplicarImportacao = useCallback(
    async (
      respostasMap: Record<number, Array<Record<string, unknown>>>,
      totalGeral: number | null,
      catalogo: any[],
      idsToRemove: number[],
      opcoesOrigem: { tipo_origem: string; valor_frete: number },
    ) => {
      if (!inserirCotacaoForn) return;
      const fornId = inserirCotacaoForn.fornecedor_id;

      // Persiste catalogo (vinculacoes pra futuras cotacoes)
      if (Array.isArray(catalogo) && catalogo.length > 0) {
        const itensCatalogo = catalogo
          .map((c) => ({
            produto_id: itens.find((it) => it.id === c.cotacao_item_id)?.produto_id,
            codigo_fornecedor: c.codigo_fornecedor,
            marca: c.marca,
            embalagem: c.embalagem,
            quantidade_embalagem: c.quantidade_embalagem,
            preco_unitario: c.preco_unitario,
            ncm: c.ncm,
          }))
          .filter((c) => !!c.produto_id);
        if (itensCatalogo.length > 0) {
          try {
            await api.post('/api/fornecedor-catalogo/upsert-batch', {
              fornecedor_id: fornId,
              cotacao_id: id,
              itens: itensCatalogo,
            });
          } catch {
            /* ignore */
          }
        }
      }

      // Remover itens vinculados-so
      if (Array.isArray(idsToRemove) && idsToRemove.length > 0) {
        const removeSet = new Set(idsToRemove);
        const filtrados = itens.filter((it) => !removeSet.has(it.id));
        try {
          await api.put(`/api/cotacoes/${id}`, {
            ...formCotacao,
            itens: filtrados.map(payloadItemCotacao),
            fornecedor_ids: fornecedores.map((f) => f.fornecedor_id),
          });
          const data = await api.get<CotacaoCompleta>(`/api/cotacoes/${id}`);
          setItens(data.itens);
        } catch {
          /* ignore */
        }
      }

      // Aplicar respostas no state
      setRespostas((prev) => {
        const next: Record<string, RespostaForm[]> = { ...prev };
        for (const [itemIdStr, dados] of Object.entries(respostasMap)) {
          const key = `${itemIdStr}-${fornId}`;
          const variacoes = Array.isArray(dados) ? dados : [dados];
          const antigas = prev[key] || [];
          next[key] = variacoes.map((d: any, idx: number) => {
            const ant = antigas[idx] || ({} as Partial<RespostaForm>);
            return {
              // A importacao le um PDF/print do fornecedor, que nao diz de qual filial e o preco:
              // preserva a filial que a linha ja tinha (DA-391) em vez de zerar a origem.
              filial_id: ant.filial_id ?? null,
              filial_uf: ant.filial_uf ?? null,
              filial_principal: ant.filial_principal !== false,
              cod_produto_fornecedor: d.cod_produto_fornecedor || ant.cod_produto_fornecedor || '',
              descricao_fornecedor: d.descricao_fornecedor || ant.descricao_fornecedor || '',
              marca: d.marca || ant.marca || '',
              embalagem: d.embalagem || ant.embalagem || 'UN',
              quantidade_produto:
                d.quantidade_produto != null ? formatQtd(Number(d.quantidade_produto)) : ant.quantidade_produto || formatQtd(1),
              quantidade_embalagem:
                d.quantidade_embalagem != null
                  ? formatQtd(Number(d.quantidade_embalagem))
                  : ant.quantidade_embalagem || formatQtd(1),
              quantidade_cotada:
                d.quantidade_cotada != null ? formatQtd(Number(d.quantidade_cotada)) : ant.quantidade_cotada || '',
              preco_principal:
                d.preco_principal != null ? formatMoedaSemSimbolo(Number(d.preco_principal)) : ant.preco_principal || '',
              ipi_percentual: d.ipi_percentual != null ? Number(d.ipi_percentual) : ant.ipi_percentual ?? null,
              observacoes: d.observacoes || ant.observacoes || '',
              // Importacao (IA/planilha) traz linha unica por par: ela ja e a valida. Com mais de
              // uma, ninguem nasce eleito — a escolha e do comprador.
              escolhida: variacoes.length === 1 ? true : ant.escolhida ?? false,
              // A importacao troca PRECO e identificacao, nao decide compra: as marcas D/F/I que
              // o comprador ja tinha posto na linha continuam de pe (mesmo criterio dos demais
              // campos aqui, que preservam `ant` quando o arquivo nao traz o dado).
              desconsiderada: ant.desconsiderada ?? false,
              forcada: ant.forcada ?? false,
              oculta_pdf: ant.oculta_pdf ?? false,
            };
          });
        }
        return next;
      });

      // Patch fornecedor
      const patchBody: any = {};
      if (totalGeral && totalGeral > 0) patchBody.total_c_imp = totalGeral;
      if (opcoesOrigem.tipo_origem) patchBody.tipo_origem = opcoesOrigem.tipo_origem;
      if (opcoesOrigem.valor_frete != null) patchBody.valor_frete = opcoesOrigem.valor_frete;
      if (Object.keys(patchBody).length > 0) {
        try {
          await api.patch(`/api/cotacoes/${id}/fornecedor/${fornId}`, patchBody);
          setFornecedores((prev) =>
            prev.map((f) =>
              f.fornecedor_id === fornId
                ? {
                    ...f,
                    ...(patchBody.total_c_imp != null ? { total_c_imp: String(patchBody.total_c_imp) } : {}),
                    ...(opcoesOrigem.tipo_origem ? { tipo_origem: opcoesOrigem.tipo_origem } : {}),
                    ...(opcoesOrigem.valor_frete != null ? { valor_frete: String(opcoesOrigem.valor_frete) } : {}),
                  }
                : f,
            ),
          );
          if (patchBody.total_c_imp != null) {
            setTotalCImpForn((prev) => ({
              ...prev,
              [fornId]: patchBody.total_c_imp.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            }));
          }
        } catch {
          /* ignore */
        }
      }
      setModalImportarIA(false);
      jaCarregou.current = true;
    },
    [inserirCotacaoForn, id, itens, formCotacao, fornecedores],
  );

  // ============================================================
  // PDF helpers — gerados client-side via window.open + print
  // ============================================================

  // Linhas da "cotacao em branco" do fornecedor, como a tela as mostra. O HTML e o PDF saem do
  // SERVIDOR (pdf-cotacao-fornecedor.service.ts): montado aqui e impresso pelo navegador, o PDF
  // saia sem as fotos que nao tinham carregado em 800ms (COT-2026-00071, 22/10/2026).
  function montarItensPdfFornecedor(
    forn: FornecedorVinculado,
    catMap: Record<string, { cod: string; desc: string; marca: string }> = {},
  ) {
    return itensAtivos.map((item) => {
        const resps = getResps(item.id, forn.fornecedor_id);
        // Item ja comprado deste fornecedor mas ainda sem resposta nesta cotacao:
        // pre-preenche COD/DESC/MARCA do catalogo dele pra ele cotar exatamente o que
        // queremos. UNIT/TOTAL ficam em branco (o fornecedor preenche ao cotar).
        const semResposta =
          resps.length === 1 && !resps[0]!.cod_produto_fornecedor && !resps[0]!.descricao_fornecedor && !resps[0]!.marca;
        const cat = semResposta ? catMap[`${item.id}-${forn.fornecedor_id}`] : undefined;
        // A embalagem do fornecedor quando as linhas dele concordam numa so; ofertas dele com
        // embalagens diferentes (CX e UN) nao tem uma conversao unica, e vale a do cadastro.
        // Linha sem codigo, descricao nem preco e' a RESP_VAZIA do convite: o "UN / 1" dela e' default,
        // nao a palavra do fornecedor (COT-2026-00079, TRAMONTINA RS: 13 CX de 120 sairam "13 UN").
        const comEmb = resps.filter((r) =>
          r.quantidade_embalagem != null && String(r.quantidade_embalagem).trim() !== ''
          && (!!(r.cod_produto_fornecedor || '').trim() || !!(r.descricao_fornecedor || '').trim() || !!String(r.preco_principal ?? '').trim()));
        const embUnica = comEmb.length > 0 && comEmb.every((r) => Number(r.quantidade_embalagem) === Number(comEmb[0]!.quantidade_embalagem))
          ? comEmb[0]
          : undefined;
        const qtdPdf = qtdComEmbalagemDoItem(item, embUnica);

        return {
          codigo: item.codigo_interno || '',
          foto_url: item.foto_url || null,
          descricao: item.produto_descricao || '',
          unidade: item.unidade_sigla || 'UN',
          quantidade: Number(item.quantidade).toLocaleString('pt-BR'),
          qtd_texto: qtdPdf.principal,
          conversao: qtdPdf.conversao,
          linhas: resps.map((resp) => ({
            cod: resp.cod_produto_fornecedor || cat?.cod || '',
            desc: resp.descricao_fornecedor || cat?.desc || '',
            marca: resp.marca || cat?.marca || '',
          })),
        };
      });
  }

  // Os PDFs do menu do fornecedor saem do SERVIDOR (Puppeteer espera as fotos) e sao gravados no
  // arquivo escolhido no "Salvar como" (lib/salvar-pdf.ts), ou oferecidos na janela "PDF pronto".
  // Antes cada um abria uma janela e chamava print() em 800ms, e as fotos que nao tinham carregado
  // saiam em branco (COT-2026-00071, 22/10/2026). Com fetch, nao window.open, porque a rota exige
  // o token no header. O item do menu fecha ao clicar, entao a trava contra clique repetido e o
  // aviso de "gerando" ficam aqui — o Puppeteer leva alguns segundos.
  async function baixarPdfDoServidor(rotulo: string, arquivo: string, montar: () => Promise<{ caminho: string; corpo: unknown }>) {
    if (gerandoPdfFornRef.current) return;
    gerandoPdfFornRef.current = true;
    try {
      // "Salvar como" ANTES de gerar: a cotacao em branco espera o catalogo antes de montar, e a
      // janela so abre colada ao clique (lib/salvar-pdf.ts).
      const destino = await escolherArquivoPdf(arquivo);
      if (destino === 'cancelado') return;
      const pedido = await montar();
      toast.mostrar(`Gerando o PDF de ${rotulo}...`);
      const token = getToken();
      const res = await fetch(pedido.caminho, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(pedido.corpo),
      });
      if (!res.ok) {
        const corpo = await res.json().catch(() => null);
        throw new Error(corpo?.erro || corpo?.error || 'Nao foi possivel gerar o PDF');
      }
      const fotosFalharam = Number(res.headers.get('X-Fotos-Falharam') || 0);
      const blob = await res.blob();
      if (destino) {
        try {
          await gravarPdf(destino, blob);
          toast.sucesso(`PDF salvo: ${destino.name}`);
          if (fotosFalharam > 0) {
            toast.aviso(`${fotosFalharam === 1 ? '1 foto nao carregou' : `${fotosFalharam} fotos nao carregaram`} — confira antes de enviar.`);
          }
          return;
        } catch {
          // Nao gravou no arquivo escolhido (aberto em outro programa, sem permissao): o PDF ja
          // foi gerado, entao vai pra janela "PDF pronto" em vez de se perder.
          toast.erro(`Nao consegui gravar em ${destino.name} — baixe pela janela.`);
        }
      }
      const url = URL.createObjectURL(blob);
      // O download NAO sai daqui: depois dos segundos do Puppeteer o clique do menu ja nao conta
      // como gesto do usuario, e o Chrome do Ruan descartou o a.click() calado (22/10/2026, 200 no
      // nginx e nada em Downloads). O botao da janela "PDF pronto" e' um clique novo.
      setPdfPronto((anterior) => {
        if (anterior) URL.revokeObjectURL(anterior.url);
        return { url, arquivo, fotosFalharam };
      });
    } catch (e: unknown) {
      toast.erro(e instanceof Error ? e.message : 'Erro ao gerar o PDF');
    } finally {
      gerandoPdfFornRef.current = false;
    }
  }

  // Documento cujo HTML a tela monta (Pedido de Compra, Compra): o servidor so desenha.
  function baixarHtmlComoPdf(html: string, rotulo: string, arquivo: string) {
    return baixarPdfDoServidor(rotulo, `${arquivo}.pdf`, async () => ({
      caminho: `/api/cotacoes/${id}/pdf-documento`,
      corpo: { html, nome: arquivo },
    }));
  }

  // Busca o catalogo do fornecedor (codigo/descricao/marca ja conhecidos de compras
  // passadas) pra pre-preencher itens sem resposta no PDF "cotacao em branco".
  // SO leitura — nao mexe no estado/salvamento da cotacao (diferente do botao
  // "Recarregar catalogo", que grava como resposta).
  async function carregarCatMapPdf(): Promise<Record<string, { cod: string; desc: string; marca: string }>> {
    if (!id) return {};
    try {
      const dados = await api.get<
        Array<{ cotacao_item_id: number; fornecedor_id: number; cod_produto_fornecedor: string; descricao_complementar: string; marca: string }>
      >(`/api/cotacoes/${id}/historico-respostas`);
      const map: Record<string, { cod: string; desc: string; marca: string }> = {};
      for (const r of dados) {
        map[`${r.cotacao_item_id}-${r.fornecedor_id}`] = {
          cod: r.cod_produto_fornecedor || '',
          desc: r.descricao_complementar || '',
          marca: r.marca || '',
        };
      }
      return map;
    } catch {
      return {};
    }
  }

  // Cotacao em branco: o servidor monta o HTML a partir das linhas (pdf-cotacao-fornecedor.service.ts).
  function gerarPdfFornecedor(forn: FornecedorVinculado, orientacao: 'retrato' | 'paisagem' = 'retrato') {
    return baixarPdfDoServidor(getNomeFornecedor(forn), `cotacao-${cotacao?.numero || id}-${getNomeFornecedor(forn)}.pdf`, async () => {
      const catMap = await carregarCatMapPdf();
      return {
        caminho: `/api/cotacoes/${id}/cotacao-fornecedor/${forn.fornecedor_id}/pdf`,
        corpo: { itens: montarItensPdfFornecedor(forn, catMap), orientacao },
      };
    });
  }

  // Monta texto resumido do Pedido de Compra pra envio via WhatsApp.
  function gerarTextoPedidoCompra(forn: FornecedorVinculado): string {
    const nomeForn = getNomeFornecedor(forn);
    let totalGeral = 0;
    const linhas: string[] = [];
    for (const item of itensAtivos) {
      const precosComForn = fornecedores.flatMap((f) =>
        getResps(item.id, f.fornecedor_id).map((r) => ({ uci: calcPrecoUnitario(r), fornId: f.fornecedor_id, resp: r })),
      ).filter((p) => p.uci > 0);
      if (precosComForn.length === 0) continue;
      const menor = Math.min(...precosComForn.map((p) => p.uci));
      const venc = precosComForn.find((p) => p.uci === menor);
      if (!venc || venc.fornId !== forn.fornecedor_id) continue;
      const qtd = Number(item.quantidade) || 1;
      // O texto vai PRO FORNECEDOR: o vencedor sai pelo preco com DIFAL, mas o numero impresso e o
      // dele, sem DIFAL ([DA-727]) — o mesmo que o PDF do pedido ja imprime.
      // A quantidade e em caixas do CADASTRO e o unitario e por unidade do conteudo: o total
      // multiplica as unidades da caixa (30/10/2026), e o texto diz "36 UN (3 CX)" como o PDF.
      const uciForn = calcPrecoUnitarioFornecedor(venc.resp);
      const totalItem = qtd * conteudoDaCaixaDoItem(item.qtd_embalagem_padrao, venc.resp.quantidade_embalagem) * uciForn;
      totalGeral += totalItem;
      const cod = item.codigo_interno ? `[${item.codigo_interno}] ` : (item.np_codigo ? `[${item.np_codigo}] ` : '');
      const q = qtdComEmbalagemDoItem(item, venc.resp);
      const unit = uciForn.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const tot = totalItem.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      linhas.push(`- ${cod}${item.produto_descricao} - ${q.principal}${q.conversao ? ` ${q.conversao}` : ''} x R$${unit} = R$${tot}`);
    }
    if (linhas.length === 0) return '';
    const cab = [`*PEDIDO DE COMPRA*`, `${nomeForn}`, `Cotacao ${cotacao?.numero ?? ''} - ${new Date().toLocaleDateString('pt-BR')}`, ''];
    const total = totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return [...cab, ...linhas, '', `*TOTAL: R$ ${total}*`].join('\n');
  }

  // Pedido de compra "Compra" (A4 landscape com tabela completa de itens vencedores). O HTML e montado
  // aqui e o PDF sai do servidor (baixarHtmlComoPdf).
  function gerarPdfCompra(forn: FornecedorVinculado) {
    const nomeForn = getNomeFornecedor(forn);
    const baseUrl = window.location.origin;
    let totalGeral = 0;

    const linhasHtml = itensAtivos.map((item, idx) => {
      const respsDoForn = getResps(item.id, forn.fornecedor_id);
      const forcado = respsDoForn.some((r, ri) => isForcarCompra(item.id, forn.fornecedor_id, ri) && calcPrecoUnitario(r) > 0);

      if (!forcado) {
        const outroForcado = fornecedores.some((f) =>
          f.fornecedor_id !== forn.fornecedor_id &&
          getResps(item.id, f.fornecedor_id).some((r, ri) => isForcarCompra(item.id, f.fornecedor_id, ri) && calcPrecoUnitario(r) > 0),
        );
        if (outroForcado) return '';
        const precosComForn = fornecedores.flatMap((f) =>
          getResps(item.id, f.fornecedor_id).map((r, ri) => ({
            uci: calcPrecoUnitario(r),
            fornId: f.fornecedor_id,
            resp: r,
            desc: isDesconsiderado(item.id, f.fornecedor_id, ri),
          })),
        ).filter((p) => p.uci > 0 && !p.desc);
        if (precosComForn.length === 0) return '';
        const menorPreco = Math.min(...precosComForn.map((p) => p.uci));
        const vencedor = precosComForn.find((p) => p.uci === menorPreco);
        if (!vencedor || vencedor.fornId !== forn.fornecedor_id) return '';
      }

      let resp: RespostaForm | undefined;
      let unitario = 0;
      if (forcado) {
        const forcadoResp = respsDoForn.find((r, ri) => isForcarCompra(item.id, forn.fornecedor_id, ri) && calcPrecoUnitario(r) > 0);
        if (!forcadoResp) return '';
        resp = forcadoResp;
        unitario = calcPrecoUnitario(forcadoResp);
      } else {
        const precosDoForn = getResps(item.id, forn.fornecedor_id)
          .map((r, ri) => ({ pu: calcPrecoUnitario(r), resp: r, desc: isDesconsiderado(item.id, forn.fornecedor_id, ri) }))
          .filter((p) => p.pu > 0 && !p.desc);
        if (precosDoForn.length === 0) return '';
        const melhor = precosDoForn.reduce((a, b) => (a.pu <= b.pu ? a : b));
        resp = melhor.resp;
        unitario = melhor.pu;
      }
      // Pedido de compra = documento do FORNECEDOR: o vencedor saiu pelo preco com DIFAL, mas o
      // unitario impresso e o dele, sem DIFAL ([DA-727]).
      unitario = calcPrecoUnitarioFornecedor(resp);
      if (!resp) return '';
      // "Incluir no PDF" desmarcado (I) tira a linha do documento — venceu, mas o comprador
      // nao quer ela no papel. Filtra a SAIDA, nao a disputa (DA-350).
      if (resp.oculta_pdf) return '';

      const qtd = Number(item.quantidade) || 1;
      const qtdPdf = qtdComEmbalagemDoItem({ ...item, quantidade: String(qtd) }, resp);
      const totalItem = qtd * conteudoDaCaixaDoItem(item.qtd_embalagem_padrao, resp.quantidade_embalagem) * unitario;
      totalGeral += totalItem;

      const imgSrc = item.foto_url ? `${baseUrl}${item.foto_url}` : '';
      const imgHtml = imgSrc
        ? `<img src="${imgSrc}" style="width:55px;height:55px;object-fit:cover;border-radius:4px;" />`
        : '<div style="width:55px;height:55px;background:#eee;border-radius:4px;display:flex;align-items:center;justify-content:center;color:#999;font-size:12px;">Sem foto</div>';

      const codFornStr = resp.cod_produto_fornecedor || '';
      const descFornStr = resp.descricao_fornecedor || '';
      return `
        <tr style="border-bottom:1px solid #ddd;${idx % 2 === 1 ? 'background:#f9f9f9;' : ''}">
          <td style="padding:8px 6px;text-align:center;font-weight:bold;color:#2563eb;font-size:14px;">${item.codigo_interno}</td>
          <td style="padding:8px 6px;text-align:center;">${imgHtml}</td>
          <td style="padding:8px 6px;font-size:14px;">
            <strong>${item.produto_descricao || item.descricao || ''}</strong>
            ${codFornStr || descFornStr ? `<br/><span style="color:#666;font-size:12px;">${codFornStr ? `<strong>${codFornStr}</strong> — ` : ''}${descFornStr}</span>` : ''}
            <br/><span style="color:#888;font-size:12px;">Embalagem: ${resp.embalagem || 'UN'} &nbsp; Qtd de Emb: ${Number(resp.quantidade_cotada || 1).toLocaleString('pt-BR')}</span>
          </td>
          <td style="padding:8px 6px;text-align:left;font-size:14px;">${resp.marca || '—'}</td>
          <td style="padding:8px 6px;text-align:center;font-weight:bold;font-size:15px;">${qtdPdf.principal}${qtdPdf.conversao ? `<br/><span style="font-weight:normal;font-size:12px;color:#555;">${qtdPdf.conversao}</span>` : ''}</td>
          <td style="padding:8px 6px;text-align:right;font-size:14px;white-space:nowrap;">${unitario.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          <td style="padding:8px 6px;text-align:right;font-weight:bold;font-size:15px;white-space:nowrap;">${totalItem.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>`;
    }).filter(Boolean).join('');

    const itensVencidos = linhasHtml ? linhasHtml.split('<tr').length - 1 : 0;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Pedido de Compra - ${nomeForn}</title>
<style>
@page { size: A4 landscape; margin: 10mm; }
body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
h1 { font-size: 28px; margin: 0 0 4px; color: #1e40af; }
h2 { font-size: 18px; color: #666; margin: 0 0 16px; }
table { width: 100%; border-collapse: collapse; }
th { background: #1e40af; color: #fff; padding: 10px 6px; text-align: left; font-size: 15px; }
td { font-size: 14px; vertical-align: middle; }
.header-info { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
.total-row td { padding: 14px 6px; font-size: 20px; font-weight: bold; border-top: 3px solid #1e40af; }
</style></head><body>
<div class="header-info">
  <div>
    <h1>PEDIDO DE COMPRA</h1>
    <h2>${nomeForn}</h2>
    <p style="font-size:15px;color:#666;margin:0;">Cotacao: ${cotacao?.numero ?? ''} &nbsp;|&nbsp; Data: ${new Date().toLocaleDateString('pt-BR')} &nbsp;|&nbsp; ${itensVencidos} ite${itensVencidos !== 1 ? 'ns' : 'm'}</p>
  </div>
  <div style="text-align:right;font-size:15px;color:#666;">FIODECOBRE LTDA<br/>49.762.154/0001-00</div>
</div>
<table>
  <thead>
    <tr>
      <th style="text-align:center;width:40px;">COD.</th>
      <th style="text-align:center;width:55px;">IMAGEM</th>
      <th>PRODUTO</th>
      <th style="text-align:left;width:110px;">MARCA</th>
      <th style="text-align:center;width:122px;">QTD</th>
      <th style="text-align:right;width:100px;white-space:nowrap;">UNIT. (R$)</th>
      <th style="text-align:right;width:110px;white-space:nowrap;">TOTAL (R$)</th>
    </tr>
  </thead>
  <tbody>
    ${linhasHtml}
    <tr class="total-row">
      <td colspan="6" style="text-align:right;">TOTAL GERAL</td>
      <td style="text-align:right;">R$ ${totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
    </tr>
  </tbody>
</table>
</body></html>`;
    return baixarHtmlComoPdf(html, nomeForn, `compra-${cotacao?.numero || id}-${nomeForn}`);
  }

  function gerarPdfPedidoCompra(forn: FornecedorVinculado, orientacao: 'retrato' | 'paisagem' = 'retrato') {
    const nomeForn = getNomeFornecedor(forn);
    const baseUrl = window.location.origin;
    let totalGeral = 0;

    // Pedido pra enviar pro FORNECEDOR confirmar precos e embalagens.
    // Diferente do gerarPdfCompra: mostra precoBase + IPI (cru do fornecedor, sem DIFAL/frete que so a loja sabe).
    // 10 colunas com detalhes do que o fornecedor reconhece (cod dele, desc dele, embal, qtd de emb).
    // Sem QTD NA EMB em nenhum PDF do Compras (pedido do Ruan, 28/10/2026): o fornecedor a lia como a
    // quantidade a comprar. A largura dela foi pra DESC. FORNECEDOR, a unica coluna sem largura fixa.
    const linhasHtml = itensAtivos
      .map((item, idx) => {
        const respsDoForn = getResps(item.id, forn.fornecedor_id);
        const forcado = respsDoForn.some((r, ri) => isForcarCompra(item.id, forn.fornecedor_id, ri) && calcPrecoUnitario(r) > 0);
        if (!forcado) {
          const outroForcado = fornecedores.some((f) =>
            f.fornecedor_id !== forn.fornecedor_id &&
            getResps(item.id, f.fornecedor_id).some((r, ri) => isForcarCompra(item.id, f.fornecedor_id, ri) && calcPrecoUnitario(r) > 0),
          );
          if (outroForcado) return '';
          const precosComForn = fornecedores.flatMap((f) =>
            getResps(item.id, f.fornecedor_id).map((r, ri) => ({
              uci: calcPrecoUnitario(r),
              fornId: f.fornecedor_id,
              desc: isDesconsiderado(item.id, f.fornecedor_id, ri),
            })),
          ).filter((p) => p.uci > 0 && !p.desc);
          if (precosComForn.length === 0) return '';
          const menorPreco = Math.min(...precosComForn.map((p) => p.uci));
          const vencedor = precosComForn.find((p) => p.uci === menorPreco);
          if (!vencedor || vencedor.fornId !== forn.fornecedor_id) return '';
        }

        let resp: RespostaForm | undefined;
        if (forcado) {
          resp = respsDoForn.find((r, ri) => isForcarCompra(item.id, forn.fornecedor_id, ri) && calcPrecoUnitario(r) > 0);
        } else {
          const precosDoForn = respsDoForn
            .map((r, ri) => ({ pu: calcPrecoUnitario(r), resp: r, desc: isDesconsiderado(item.id, forn.fornecedor_id, ri) }))
            .filter((p) => p.pu > 0 && !p.desc);
          if (precosDoForn.length === 0) return '';
          const melhor = precosDoForn.reduce((a, b) => (a.pu <= b.pu ? a : b));
          resp = melhor.resp;
        }
        if (!resp) return '';
        if (resp.oculta_pdf) return ''; // idem gerarPdfCompra: I tira a linha do documento

        const emb = resp.embalagem || 'UN';
        const qtdCotada = Number(resp.quantidade_cotada || 1);
        const ppParsed = parseQtd(resp.preco_principal);
        const precoBase = Number.isFinite(ppParsed) ? ppParsed : 0;
        const ipi = resp.ipi_percentual != null ? Number(resp.ipi_percentual) : 0;
        const preco = Math.round(precoBase * (1 + ipi / 100) * 100) / 100;
        const qtd = Number(item.quantidade) || 1;
        // Caixas do CADASTRO x unidades dela x preco por unidade do fornecedor (30/10/2026). Era
        // caixas x Preco Emb, que so fecha quando a embalagem dele e a do cadastro sao a mesma.
        const totalItem = qtd * conteudoDaCaixaDoItem(item.qtd_embalagem_padrao, resp.quantidade_embalagem)
          * (preco / conteudoDaEmbalagem(resp.quantidade_embalagem));
        totalGeral += totalItem;

        const imgSrc = item.foto_url ? `${baseUrl}${item.foto_url}` : '';
        const imgHtml = imgSrc
          ? `<img src="${imgSrc}" style="width:38px;height:38px;object-fit:cover;border-radius:4px;" />`
          : '<div style="width:38px;height:38px;background:#eee;border-radius:4px;display:flex;align-items:center;justify-content:center;color:#999;font-size:10px;">Sem foto</div>';

        return `<tr style="border-bottom:1px solid #ddd;${idx % 2 === 1 ? 'background:#f9f9f9;' : ''}">
<td style="padding:3px 5px;text-align:center;font-weight:bold;color:#2563eb;font-size:11px;">${item.codigo_interno || ''}</td>
<td style="padding:3px 5px;text-align:center;">${imgHtml}</td>
<td style="padding:3px 5px;text-align:center;font-size:11px;word-break:break-all;">${resp.cod_produto_fornecedor || ''}</td>
<td style="padding:3px 5px;font-size:11px;">${resp.descricao_fornecedor || ''}</td>
<td style="padding:3px 5px;text-align:center;font-size:11px;overflow-wrap:anywhere;">${resp.marca || '—'}</td>
<td style="padding:3px 5px;text-align:center;font-size:11px;overflow-wrap:anywhere;">${emb}</td>
<td style="padding:3px 5px;text-align:center;font-size:11px;">${qtdCotada.toLocaleString('pt-BR')}</td>
<td style="padding:3px 5px;text-align:right;font-size:11px;">${preco.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
<td style="padding:3px 5px;text-align:right;font-weight:bold;font-size:11px;white-space:nowrap;">${totalItem.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>`;
      })
      .filter(Boolean)
      .join('');
    if (!linhasHtml) {
      toast.erro(`${nomeForn} nao venceu nenhum item nesta cotacao`);
      return;
    }
    const itensCount = linhasHtml.split('<tr').length - 1;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Pedido de Compra - ${nomeForn}</title>
<style>@page { size: A4 ${orientacao === 'paisagem' ? 'landscape' : 'portrait'}; margin: 10mm; } body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
h1 { font-size: 22px; margin: 0 0 4px; } h2 { font-size: 14px; color: #666; margin: 0 0 16px; }
table { width: 100%; border-collapse: collapse; table-layout: fixed; } th { background: #e5e7eb; padding: 6px 4px; text-align: left; font-size: 11px; border-bottom: 2px solid #999; }
td { font-size: 11px; vertical-align: middle; padding: 3px 5px; }
.header-info { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.total-row td { padding: 10px 5px; font-size: 16px; font-weight: bold; border-top: 2px solid #999; }</style></head><body>
<div class="header-info"><div><h1>${nomeForn}</h1>
<h2>Cotacao: ${cotacao?.numero || ''} &nbsp;&nbsp;|&nbsp;&nbsp; Data: ${new Date().toLocaleDateString('pt-BR')} &nbsp;&nbsp;|&nbsp;&nbsp; ${itensCount} ite${itensCount !== 1 ? 'ns' : 'm'}</h2></div>
<div style="text-align:right;font-size:12px;color:#666;">FIODECOBRE LTDA<br/>49.762.154/0001-00</div></div>
<table><thead><tr>
<th style="text-align:center;width:28px;">FC</th>
<th style="text-align:center;width:45px;">IMAGEM</th>
<th style="text-align:center;width:52px;word-break:break-all;">COD.</th>
<th>DESC. FORNECEDOR</th>
<th style="text-align:center;width:92px;">MARCA</th>
<th style="text-align:center;width:50px;">EMBAL.</th>
<th style="text-align:center;width:38px;">QTD DE EMB</th>
<th style="text-align:right;width:58px;white-space:nowrap;">PRECO (R$)</th>
<th style="text-align:right;width:68px;white-space:nowrap;">TOTAL</th></tr></thead><tbody>${linhasHtml}
<tr class="total-row"><td colspan="8" style="text-align:right;">TOTAL GERAL</td>
<td style="text-align:right;white-space:nowrap;">R$ ${totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>
</tbody></table></body></html>`;
    return baixarHtmlComoPdf(html, nomeForn, `pedido-compra-${cotacao?.numero || id}-${nomeForn}-${orientacao}`);
  }

  // ============================================================
  // Render
  // ============================================================

  const temCotacao = !!cotacao && fornecedores.length > 0;

  // ============================================================
  // RENDER: lista quando nao tem cotacao
  // ============================================================
  if (!cotacao && !loading) {
    return (
      <div className="modulo-pagina" style={{ padding: 16 }}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">
            <FileText size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} /> Cotacoes
          </h2>
          <div className="pagina-acoes">
            <button type="button" className="btn-primary" onClick={() => navigate('/cotacoes/nova')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Plus size={16} /> Nova Cotacao
            </button>
          </div>
        </div>

        <div role="tablist" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--page-border, #e5e7eb)', marginBottom: 12 }}>
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={filtroStatus === t.key}
              onClick={() => setFiltroStatus(t.key)}
              style={{
                background: filtroStatus === t.key ? 'var(--panel-bg)' : 'transparent',
                color: filtroStatus === t.key ? 'var(--text-color)' : 'var(--text-muted, #6b7280)',
                border: 'none',
                borderBottom: filtroStatus === t.key ? '2px solid var(--primary)' : '2px solid transparent',
                padding: '8px 14px',
                fontSize: 14,
                fontWeight: filtroStatus === t.key ? 600 : 500,
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ position: 'relative', marginBottom: 12, maxWidth: 480 }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: 9, color: 'var(--text-muted, #6b7280)' }} />
          <input
            type="text"
            placeholder="Buscar por numero, observacao ou comprador"
            value={buscaLista}
            onChange={(e) => setBuscaLista(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px 8px 32px',
              borderRadius: 4,
              border: '1px solid var(--input-border, #d1d5db)',
              background: 'var(--input-bg)',
              color: 'var(--text-color)',
            }}
          />
        </div>

        <div style={{ background: 'var(--panel-bg)', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--panel-border, #e5e7eb)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Numero</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Observacao</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Data</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Comprador</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Itens</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Forn.</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {loadingLista && (
                <tr>
                  <td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>Carregando...</td>
                </tr>
              )}
              {!loadingLista && listaCotacoes.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>Nenhuma cotacao encontrada</td>
                </tr>
              )}
              {!loadingLista &&
                listaCotacoes.map((c) => (
                  <tr
                    key={c.id}
                    style={{ borderTop: '1px solid var(--grid-row-border, #f3f4f6)', cursor: 'pointer' }}
                    onClick={() => navigate(`/cotacoes/${c.id}/respostas`)}
                  >
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--primary)' }}>{c.numero}</td>
                    <td style={{ padding: '8px 12px' }}>{c.observacoes || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{formatDataSP(c.data_abertura)}</td>
                    <td style={{ padding: '8px 12px' }}>{c.comprador_nome || '—'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{c.total_itens}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{c.total_fornecedores}</td>
                    <td style={{ padding: '8px 12px' }}>{c.status}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>Carregando cotacao...</div>;
  if (!cotacao) return null;

  // Lista do modal de convidados = fornecedores ATIVOS (/api/fornecedores) + qualquer
  // convidado que nao esta nessa lista (tipicamente inativado DEPOIS de ser convidado).
  // Sem isso o convidado inativo some do modal: o contador conta ele mas ele nao aparece
  // pra ser desmarcado/removido. Aqui ele entra marcado com etiqueta "(inativo)".
  const idsFornAtivos = new Set(todosFornecedores.map((f) => f.id));
  const convidadosInativos: FornecedorMin[] = fornecedores
    .filter((fv) => !idsFornAtivos.has(fv.fornecedor_id))
    .map((fv) => ({
      id: fv.fornecedor_id,
      razao_social: fv.razao_social,
      nome_fantasia: fv.nome_fantasia,
      cidade: null,
      uf: null,
    }));
  const fornecedoresModal: FornecedorMin[] = [...todosFornecedores, ...convidadosInativos];

  // Sem respiro no topo (pedido do Ruan, 15/09/2026, "reduza ao maximo o espaco nao utilizado"): o
  // `.app-content` da 20px de padding global e somados aos 16 daqui eram ~45px de tela vazia antes
  // do cabecalho. A margem negativa devolve 16 dos 20 so nesta tela, sem mexer no padding de todas.
  return (
    <div className="modulo-pagina" style={{ padding: '0 16px 4px', marginTop: -16 }}>
      {/* Vista lado a lado — painel de tela inteira por cima da pagina. A pagina segue montada
          por baixo (estado, autosave e rascunho intactos); so o modo "Inserir Cotacao" a exclui. */}
      {ladoALado && !inserirCotacaoForn && fornecedores.length > 0 && (
        <CotacaoLadoALado
          cotacaoId={id}
          itens={itens}
          fornecedores={fornecedores}
          buscaProduto={buscaProduto}
          setBuscaProduto={setBuscaProduto}
          numero={cotacao?.numero || ''}
          observacoes={formCotacao.observacoes || null}
          dataAbertura={formCotacao.data_validade || null}
          getResps={getResps}
          calcPrecoUnitario={calcPrecoUnitario}
          isDesconsiderado={isDesconsiderado}
          isForcarCompra={isForcarCompra}
          isOcultoPdf={isOcultoPdf}
          toggleDesconsiderar={toggleDesconsiderar}
          toggleForcarCompra={toggleForcarCompra}
          toggleOcultoPdf={toggleOcultoPdf}
          custoDaCotacao={custoDaCotacao}
          totalFornecedor={totalFornecedor}
          moverItem={moverItem}
          recarregarCatalogo={() => void recarregarCatalogo()}
          recarregandoCat={recarregandoCat}
          salvarTudo={() => void salvarTudo()}
          salvando={salvando}
          temAlteracoes={temAlteracoes}
          podeEditarCatalogo={podeEditarCatalogo}
          abrirEditorPreco={podeEditarPreco ? abrirEditorPreco : undefined}
          onEditarData={abrirEditorData}
          escondeCompradas={escondeCompradas}
          setEscondeCompradas={setEscondeCompradas}
          cotacaoCriadoEm={cotacao?.criado_em ?? null}
          onDesmarcarComprado={(item) => void desmarcarComprado(item)}
          onMarcarCompradoCelula={(item, forn, comprado) => void marcarCompradoCelula(item, forn, comprado)}
          onVoltarCotacoes={() => navigate('/cotacoes')}
          onSair={(itemId) => {
            trocarVista(false);
            if (itemId != null) {
              // Abre o produto ja expandido na vista Por produto (o "+N" e o clique no nome
              // levam ate onde as variacoes aparecem inteiras).
              setExpandidos((prev) => new Set(prev).add(itemId));
              requestAnimationFrame(() => {
                document.getElementById(`cot-item-${itemId}`)?.scrollIntoView({ block: 'center' });
              });
            }
          }}
        />
      )}

      {/* Caixinha de preco — UMA instancia pras duas vistas. Fica no topo da pagina (fora do painel
          lado a lado) porque e `fixed` e precisa passar por cima dele. */}
      {edicaoPreco && (
        <EditorPrecoCatalogo
          alvo={edicaoPreco}
          salvando={salvandoPreco}
          onCancelar={() => setEdicaoPreco(null)}
          onConfirmar={(v) => void confirmarPrecoCatalogo(v)}
        />
      )}

      {/* Caixinha da DATA da compra — UMA instancia pras duas vistas, mesmo motivo do `fixed` acima. */}
      {edicaoData && (
        <EditorDataCompra
          alvo={edicaoData}
          salvando={salvandoData}
          onCancelar={() => setEdicaoData(null)}
          onConfirmar={(d) => void confirmarDataCompra(d)}
        />
      )}

      {embalagemDe && embalagemDe.produto_id != null && (
        <ModalEmbalagemCompra
          produtoId={embalagemDe.produto_id}
          codigo={embalagemDe.codigo_interno}
          descricao={embalagemDe.produto_descricao}
          onFechar={() => setEmbalagemDe(null)}
          onGravado={(g) => {
            aplicarEmbalagemGravada(embalagemDe.produto_id!, g);
            setEmbalagemDe(null);
            toast.sucesso('Unidade de compra gravada no cadastro do produto.');
          }}
        />
      )}

      {embalagemDe && embalagemDe.produto_id == null && embalagemDe.novo_produto_id != null && (
        <ModalEmbalagemNovoProduto
          novoProdutoId={embalagemDe.novo_produto_id}
          codigo={embalagemDe.np_codigo ?? null}
          descricao={embalagemDe.produto_descricao}
          embalagemAtual={embalagemDe.unidade_sigla ?? null}
          qtdAtual={embalagemDe.qtd_embalagem_padrao ?? null}
          onFechar={() => setEmbalagemDe(null)}
          onGravado={(g) => {
            // Grava no NOVO PRODUTO, nao na cotacao: rebate em toda linha dele sem marcar a cotacao
            // como alterada — mesmo gesto do `aplicarEmbalagemGravada` do produto.
            const npId = embalagemDe.novo_produto_id;
            setItens((prev) => prev.map((it) => (
              it.produto_id == null && it.novo_produto_id === npId
                ? { ...it, unidade_sigla: g.embalagem || it.unidade_sigla, qtd_embalagem_padrao: g.quantidade_embalagem }
                : it
            )));
            setEmbalagemDe(null);
            toast.sucesso('Embalagem gravada no cadastro do Novo Produto.');
          }}
        />
      )}

      {/* Header sticky */}
      <div
        className="pagina-header"
        style={{
          position: 'sticky',
          // -16 e nao 0: o sticky respeita o padding de 20px do `.app-content` e segurava o
          // cabecalho la embaixo mesmo com a margem negativa da pagina (medido: topo em +20). Com -16
          // ele fica a 4px do topo, igual a pagina (a `ContatosDoDia` ja tinha medido o mesmo, com -20).
          top: -16,
          zIndex: 20,
          marginBottom: 8,
          // Destaque do painel so nesta tela (escolha do Ruan, 15/09/2026): no Azul Royal o
          // `--page-header-bg` (#94b5ea) tinha contraste 1,01 com a pagina. Primeiro foi o `--panel-bg`
          // (#cfdef6); no mesmo dia ele pediu LARANJA (#fb923c, orange-400) e depois "escurecer 2
          // tons" → orange-600 da escala Tailwind. Fixo, em qualquer tema.
          background: '#ea580c',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => (inserirCotacaoForn ? setInserirCotacaoForn(null) : navigate(voltarPara))}
              className="btn-icon"
              title="Voltar"
              aria-label="Voltar"
              style={{ marginRight: 4 }}
            >
              <ArrowLeft size={20} />
            </button>
            Cotacoes
          </h2>
          {!inserirCotacaoForn && (
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setMenuPrincipal(!menuPrincipal)}
                className="btn-icon-header"
                aria-label="Mais opcoes"
                title="Mais opcoes"
                style={{ padding: 4 }}
              >
                <MoreVertical size={18} />
              </button>
              {menuPrincipal && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setMenuPrincipal(false)} />
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: '100%',
                      marginTop: 4,
                      zIndex: 50,
                      background: 'var(--popup-bg, #fff)',
                      borderRadius: 8,
                      boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                      border: '1px solid var(--panel-border, #e5e7eb)',
                      padding: '4px 0',
                      minWidth: 200,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setMenuPrincipal(false);
                        navigate(`/cotacoes/${id}/comparativo`);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '8px 16px',
                        fontSize: 13,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-color)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <BarChart3 size={16} color="#9333ea" /> Comparativo
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuPrincipal(false);
                        abrirModalFornecedores();
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '8px 16px',
                        fontSize: 13,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-color)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <ClipboardList size={16} color="var(--primary)" /> Fornecedores Convidados
                    </button>
                    <button
                      type="button"
                      onClick={abrirSplitModal}
                      disabled={splitLoading}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '8px 16px',
                        fontSize: 13,
                        background: 'transparent',
                        border: 'none',
                        cursor: splitLoading ? 'wait' : 'pointer',
                        color: 'var(--text-color)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        opacity: splitLoading ? 0.6 : 1,
                      }}
                    >
                      <Scissors size={16} color="#059669" />{' '}
                      {splitLoading ? 'Separando...' : 'Separar itens comprados'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {inserirCotacaoForn && (
            <span style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 500, marginLeft: 4 }}>
              — Inserir Cotacao: {getNomeFornecedor(inserirCotacaoForn)}
            </span>
          )}
        </div>

        {!inserirCotacaoForn && (
          // `wrap`: com os botoes em `nowrap`, janela estreita (1280) vazava 137px pra fora do cabecalho;
          // assim eles descem pra uma 2a linha so quando nao cabem — em 1536 (a do Ruan) fica uma so.
          <div className="pagina-acoes" style={{ alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: 6 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end', paddingBottom: 2 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--page-fg)', whiteSpace: 'nowrap' }}>N°: {cotacao.numero}</span>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 2 }}>
                Data da Cotacao
              </label>
              <input
                type="date"
                name="data_validade"
                value={formCotacao.data_validade}
                onChange={handleFormChange}
                style={{
                  width: 144,
                  padding: '6px 8px',
                  border: '1px solid var(--input-border, #d1d5db)',
                  borderRadius: 6,
                  fontSize: 13,
                  background: 'var(--input-bg)',
                  color: 'var(--text-color)',
                }}
              />
            </div>
            {/* Campo elastico da linha: e ele que cede largura quando a janela aperta, pra nenhum
                botao quebrar em duas linhas e dobrar a altura do cabecalho. A base e' o MINIMO (120) e
                ele cresce ate 256: com `flexWrap` na linha, o navegador decide a quebra pela base, e
                com base 256 os botoes desciam antes de o campo encolher (medido: 69 → 106px em 1536). */}
            <div style={{ flex: '1 1 120px', minWidth: 120, maxWidth: 256 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 2 }}>
                Observacoes
              </label>
              <input
                type="text"
                name="observacoes"
                value={formCotacao.observacoes}
                onChange={handleFormChange}
                placeholder="Notas internas..."
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid var(--input-border, #d1d5db)',
                  borderRadius: 6,
                  fontSize: 13,
                  background: 'var(--input-bg)',
                  color: 'var(--text-color)',
                }}
              />
            </div>
            {/* Porta visivel do modal onde vivem os totais por fornecedor (DA-430) — sem ela o
                resumo ficaria escondido atras do ⋮ do titulo. */}
            {!inserirCotacaoForn && (
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button
                  type="button"
                  onClick={abrirModalFornecedores}
                  title="Fornecedores convidados: itens ganhos, Soma, Total Forn. e acoes de cada um"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 12px', borderRadius: 6,
                    border: '1px solid var(--primary)',
                    background: 'transparent',
                    color: 'var(--primary)',
                    cursor: 'pointer',
                    fontSize: 12, fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Users size={15} /> Fornecedores
                  {fornecedores.length > 0 && (
                    <span style={{ background: 'var(--primary)', color: '#fff', borderRadius: 9, padding: '0 6px', fontSize: 11, fontWeight: 700 }}>
                      {fornecedores.length}
                    </span>
                  )}
                </button>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="button"
                onClick={() => void recarregarCatalogo()}
                disabled={recarregandoCat}
                title="Atualiza preços e dados a partir do Catálogo do Fornecedor (sobrescreve o preço)"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 6,
                  border: '1px solid var(--primary)',
                  background: recarregandoCat ? 'var(--border)' : 'rgb(220,252,231)',
                  color: recarregandoCat ? 'var(--text-muted)' : 'var(--primary)',
                  cursor: recarregandoCat ? 'default' : 'pointer',
                  fontSize: 12, fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {recarregandoCat
                  ? <Loader2 size={13} className="animate-spin" />
                  : <RefreshCw size={13} />}
                {recarregandoCat ? 'Buscando...' : 'Recarregar'}
              </button>
            </div>
            {/* Troca de vista — mora aqui porque e uma decisao sobre a cotacao inteira,
                nao sobre a tabela. Na vista lado a lado o par reaparece na faixa fina dela. */}
            {fornecedores.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', border: '1px solid var(--primary)', borderRadius: 6, overflow: 'hidden' }}>
                  <button
                    type="button"
                    style={{ border: 'none', padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'default', background: 'var(--primary)', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}
                  >
                    <List size={13} /> Por produto
                  </button>
                  <button
                    type="button"
                    onClick={() => trocarVista(true)}
                    title="Comparar os fornecedores lado a lado, em tela inteira"
                    style={{ border: 'none', padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', background: 'var(--panel-bg)', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}
                  >
                    <LayoutGrid size={13} /> Lado a lado
                  </button>
                </div>
              </div>
            )}
            {/* Salvar e' o ULTIMO da linha e vai sem rotulo (pedido do Ruan, 15/09/2026): o aviso
                "Alteracoes nao salvas" em cima dele saiu — o botao azul aceso ja diz que ha o que
                gravar, e cinza que nao ha. A confirmacao de gravado virou toast no `salvarTudo`. */}
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="button"
                onClick={() => void salvarTudo()}
                disabled={salvando || !temAlteracoes}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', borderRadius: 6, border: 'none',
                  cursor: salvando || !temAlteracoes ? 'default' : 'pointer',
                  fontSize: 13, fontWeight: 600,
                  whiteSpace: 'nowrap',
                  background: salvando || !temAlteracoes ? 'var(--border)' : '#2563eb',
                  color: salvando || !temAlteracoes ? 'var(--text-muted)' : '#fff',
                }}
              >
                {salvando ? <Loader2 size={14} className="animate-spin" /> : null}
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Banner restauracao — numa linha so: titulo e texto correm juntos, e os botoes ficam na
          mesma faixa. Empilhado ele custava ~70px de tela enquanto estivesse aberto. */}
      {rascunhoOferta && (
        <div
          style={{
            marginBottom: 8,
            borderRadius: 8,
            border: '1px solid #fcd34d',
            padding: '4px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: '#fde68a',
            color: '#000',
          }}
        >
          <div style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
            <strong style={{ fontSize: 13, marginRight: 8 }}>Edicao nao salva detectada</strong>
            Encontrei alteracoes desta cotacao no seu navegador feitas em{' '}
            <span style={{ fontFamily: 'monospace' }}>{new Date(rascunhoOferta.salvo_em).toLocaleString('pt-BR')}</span> que nao chegaram a ser salvas no servidor. Quer restaurar?
          </div>
          <button
            type="button"
            onClick={restaurarRascunho}
            style={{ padding: '4px 12px', background: 'var(--accent)', color: 'var(--accent-fg)', borderRadius: 4, border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Restaurar
          </button>
          <button
            type="button"
            onClick={descartarRascunho}
            style={{ padding: '4px 12px', background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--page-fg)', borderRadius: 4, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Manter o conteúdo da tela
          </button>
        </div>
      )}

      {/* Busca de produtos + filtro + expandir/fechar numa linha so — eram duas faixas (~110px). */}
      {!inserirCotacaoForn && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Buscar por nome, codigo, EAN ou codigo do fornecedor (filtra a lista e busca novos para adicionar)..."
            value={buscaProduto}
            onChange={(e) => setBuscaProduto(upperMantendoCursor(e))}
            style={{
              width: '100%',
              padding: '8px 12px 8px 36px',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 13,
              background: 'var(--input-bg)',
              color: 'var(--text-color)',
            }}
          />
          {(resultadosProduto.length > 0 || resultadosCatalogo.length > 0 || resultadosNovos.length > 0) && (
            <div
              style={{
                position: 'absolute',
                zIndex: 50,
                top: '100%',
                left: 0,
                right: 0,
                marginTop: 4,
                background: 'var(--panel-bg)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                maxHeight: 256,
                overflowY: 'auto',
              }}
            >
              {resultadosProduto.map((p) => {
                const itemDaCotacao = itens.find((i) => i.produto_id === p.id);
                return (
                <div
                  key={p.id}
                  onClick={() => (itemDaCotacao ? irParaItemDaCotacao(itemDaCotacao.id) : adicionarProduto(p))}
                  title={itemDaCotacao ? 'Este produto ja esta nesta cotacao — clique para ir ate a linha dele' : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f9fafb',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--primary-soft)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                >
                  {p.foto_url ? (
                    <FotoProduto url={p.foto_url} tam={40} />
                  ) : (
                    <div style={{ width: 40, height: 40, background: 'var(--grid-row-alt-bg)', borderRadius: 4, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImageOff size={14} color="#d1d5db" />
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', marginRight: 8 }}>{p.codigo_interno}</span>
                      <span style={{ fontSize: 13, color: itemDaCotacao ? 'var(--text-muted)' : 'var(--page-fg)' }}>{p.descricao}</span>
                      {p.status === 'inativo' && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#000', background: '#94a3b8', borderRadius: 4, padding: '1px 5px', marginLeft: 6, verticalAlign: 'middle' }}>INATIVO</span>
                      )}
                    </div>
                    {p.fornecedores && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.fornecedores}
                      </div>
                    )}
                  </div>
                  {itemDaCotacao && (
                    <span style={{
                      backgroundColor: '#94a3b8',
                      color: '#000',
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}>
                      Já está nesta cotação · ver linha
                    </span>
                  )}
                  {p.compra_em_aberto && (
                    <span style={{
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      lineHeight: 1.15,
                      backgroundColor: '#fde68a',
                      color: '#000',
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}>
                      <span>🛒 Comprado</span>
                      {p.ultima_compra_em && <span>{formatDataSP(p.ultima_compra_em)}</span>}
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    Est: <span style={{ fontWeight: 600, color: 'var(--page-fg)' }}>{Number(p.estoque_atual || 0).toLocaleString('pt-BR')}</span>
                  </span>
                </div>
                );
              })}

              {/* Itens que so existem no Catalogo do Fornecedor (sem produto do nosso cadastro).
                  Vem DEPOIS dos produtos de proposito: o cadastro da loja e o caminho normal, este
                  e a saida pra cotar o que a loja ainda nao vende. */}
              {resultadosCatalogo.length > 0 && (
                <div style={{ padding: '4px 12px', fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: 'var(--text-muted)', background: 'var(--grid-row-alt-bg)', borderTop: resultadosProduto.length > 0 ? '1px solid var(--border)' : 'none' }}>
                  DO CATÁLOGO DO FORNECEDOR — SEM PRODUTO NO NOSSO CADASTRO
                </div>
              )}
              {resultadosCatalogo.map((c) => (
                <div
                  key={`cat-${c.id}`}
                  onClick={() => void adicionarItemCatalogo(c)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f9fafb',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--primary-soft)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                >
                  {c.foto_url ? (
                    <FotoProduto url={c.foto_url} tam={40} />
                  ) : (
                    <div style={{ width: 40, height: 40, background: 'var(--grid-row-alt-bg)', borderRadius: 4, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImageOff size={14} color="#d1d5db" />
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', marginRight: 8 }}>{c.codigo_fornecedor || '—'}</span>
                      <span style={{ fontSize: 13, color: 'var(--page-fg)' }}>{c.descricao_fornecedor}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.empresa_nome || 'SEM NOME'}{c.marca ? ` · ${c.marca}` : ''}
                    </div>
                  </div>
                  {/* Amarelo + preto: o par que o Ruan enxerga (daltonismo verde/vermelho). */}
                  <span style={{
                    backgroundColor: '#fde68a',
                    color: '#000',
                    padding: '2px 8px',
                    borderRadius: 12,
                    fontSize: 10,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}>
                    CATÁLOGO
                  </span>
                </div>
              ))}

              {/* A TERCEIRA seção: a prateleira Novos Produtos (DA-606) — o item que ainda não
                  existe em lugar nenhum. Por último de propósito: o cadastro da loja é o caminho
                  normal, o catálogo é o do fornecedor, e este é o que ainda não é de ninguém. */}
              {resultadosNovos.length > 0 && (
                <div style={{ padding: '4px 12px', fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: 'var(--text-muted)', background: 'var(--grid-row-alt-bg)', borderTop: (resultadosProduto.length > 0 || resultadosCatalogo.length > 0) ? '1px solid var(--border)' : 'none' }}>
                  NOVOS PRODUTOS — AINDA NÃO EXISTEM NO CADASTRO NEM NO CATÁLOGO
                </div>
              )}
              {resultadosNovos.map((n) => (
                <div
                  key={`np-${n.id}`}
                  onClick={() => void adicionarNovoProduto(n)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f9fafb',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--primary-soft)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                >
                  {n.foto_url ? (
                    <FotoProduto url={n.foto_url} tam={40} />
                  ) : (
                    <div style={{ width: 40, height: 40, background: 'var(--grid-row-alt-bg)', borderRadius: 4, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImageOff size={14} color="#d1d5db" />
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', marginRight: 8 }}>{n.codigo}</span>
                      <span style={{ fontSize: 13, color: 'var(--page-fg)' }}>{n.descricao}</span>
                    </div>
                    {(n.marca || n.embalagem) && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {[n.marca, n.embalagem].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  {/* Cor diferente da do CATÁLOGO: as duas seções aparecem juntas, e o mesmo
                      amarelo nas duas faria elas lerem como a mesma coisa. */}
                  <span style={{
                    backgroundColor: '#c4b5fd',
                    color: '#000',
                    padding: '2px 8px',
                    borderRadius: 12,
                    fontSize: 10,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}>
                    NOVO
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

      {/* Filtro + expand/collapse all. O resumo por fornecedor (balao de ganhos, Soma, Total Forn.
          e o menu de acoes) mora no modal "Fornecedores Convidados" — ver DA-430. */}
      {fornecedores.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer', flexShrink: 0 }}
            title="Oculta produtos cuja ultima compra foi feita depois que esta cotacao foi criada">
            <input type="checkbox" checked={escondeCompradas} onChange={(e) => setEscondeCompradas(e.target.checked)} />
            Esconder já comprei nesta rodada
          </label>

          {itens.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginLeft: 'auto' }}>
              <button
                type="button"
                onClick={() => setExpandidos(new Set(itens.map((i) => i.id)))}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 11, color: 'var(--page-fg)', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 4 }}
                title="Expandir todos os itens"
              >
                <ChevronsDown size={13} /> Expandir todos
              </button>
              <button
                type="button"
                onClick={() => setExpandidos(new Set())}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 11, color: 'var(--page-fg)', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 4 }}
                title="Recolher todos os itens"
              >
                <ChevronsUp size={13} /> Fechar todos
              </button>
            </div>
          )}
        </div>
      )}
        </div>
      )}

      {/* ⛔ O modo "Inserir Cotacao" saiu em 22/09/2026, a pedido dele com print. Era a ultima porta
          que digitava preco na cotacao; sem o preco e sem a importacao por IA (removidos horas
          antes) sobrava so o IPI — que ninguem preenche desde 08/05/2026 — e o vinculo de
          codigo/marca, que se faz no Catalogo. O IPI ja GRAVADO segue valendo: o PDF do Pedido de
          Compra continua aplicando o percentual das 4 cotacoes antigas que o tem.
          ⚠️ O componente e as condicoes `!inserirCotacaoForn` ficaram no arquivo como codigo morto
          (sem porta que os alcance) — faxina anotada em pendencias.md. */}

      {/* Modo normal: tabela de produtos x fornecedores */}
      {!inserirCotacaoForn && (
        <ProdutoFornecedorTable
          cotacaoId={id}
          itens={itens}
          buscaProduto={buscaProduto}
          fornecedores={fornecedores}
          expandidos={expandidos}
          toggleExpandir={toggleExpandir}
          getResps={getResps}
          setResp={setResp}
          removeResp={removeResp}
          calcPrecoUnitario={calcPrecoUnitario}
          totalFornecedor={totalFornecedor}
          totalCImpForn={totalCImpForn}
          setTotalCImpForn={setTotalCImpForn}
          salvarTotalCImpForn={salvarTotalCImpForn}
          atualizarItem={atualizarItem}
          removerItemCotacao={removerItemCotacao}
          abrirMoverItem={abrirMoverItem}
          moverEnviando={moverEnviando}
          dragApi={{
            dragIdx,
            dragOverIdx,
            dragOverPos,
            onDragStart: handleDragStart,
            onDragOver: handleDragOver,
            onDragEnd: handleDragEnd,
            onDrop: handleDrop,
            moverItem,
          }}
          marcas={marcas}
          requestNovaMarca={(ctx) => setNovaMarcaCtx(ctx)}
          requestHistoricoProduto={(p) => setHistoricoProduto(p)}
          requestVendas30d={(pid) => setVendas30d(pid)}
          isForcarCompra={isForcarCompra}
          toggleForcarCompra={toggleForcarCompra}
          isDesconsiderado={isDesconsiderado}
          toggleDesconsiderar={toggleDesconsiderar}
          isOcultoPdf={isOcultoPdf}
          toggleOcultoPdf={toggleOcultoPdf}
          setFotoZoom={setFotoZoom}
          setInserirCotacaoForn={setInserirCotacaoForn}
          setConfirmarLimparForn={setConfirmarLimparForn}
          gerarPdfFornecedor={gerarPdfFornecedor}
          gerarPdfPedidoCompra={gerarPdfPedidoCompra}
          abrirEnviarWhatsApp={abrirEnviarWhatsApp}
          podeEditarCatalogo={podeEditarCatalogo}
          paresAnotacao={paresAnotacao}
          abrirEditorPreco={podeEditarPreco ? abrirEditorPreco : undefined}
          onAbrirNoCatalogo={podeEditarCatalogo ? (f, c) => void abrirNoCatalogo(f, c) : undefined}
          onEditarData={abrirEditorData}
          onEditarEmbalagem={podeEditarCadastroProduto ? setEmbalagemDe : undefined}
          escondeCompradas={escondeCompradas}
          setEscondeCompradas={setEscondeCompradas}
          cotacaoCriadoEm={cotacao?.criado_em ?? null}
          onDesmarcarComprado={(item) => void desmarcarComprado(item)}
          onMarcarCompradoCelula={(item, forn, comprado) => void marcarCompradoCelula(item, forn, comprado)}
        />
      )}

      {/* Modal: Cadastrar produto no Catalogo do Fornecedor */}
      {modalCatalogoCriar && (
        <ModalCadastrarCatalogo
          fornecedorId={modalCatalogoCriar.fornecedor_id}
          produtoId={modalCatalogoCriar.produto_id}
          novoProdutoId={modalCatalogoCriar.novo_produto_id}
          fornNome={modalCatalogoCriar.forn_nome}
          prodDesc={modalCatalogoCriar.prod_desc}
          inicial={modalCatalogoCriar.inicial}
          onFechar={() => setModalCatalogoCriar(null)}
          onSalvo={() => {
            setModalCatalogoCriar(null);
            // ⚠️ `carregarDados()` sozinho NAO basta, e o sintoma e' cruel: o cadastro grava, a tela
            // recarrega e a linha continua dizendo "Sem cadastro no Catalogo do fornecedor" — o
            // operador conclui que o botao nao funciona ([DA-610], visto na tela publicada; pela
            // API o dado ja estava certo).
            // O motivo: o espelho automatico roda em modo `soEspelho`, que de proposito so atualiza
            // preco/qtd/embalagem de linha JA IDENTIFICADA (DA-472 — rodando sozinho a cada
            // abertura, ele nao pode mudar a FORMA do documento nas costas do comprador). Quem
            // preenche codigo, descricao e marca numa linha que nasceu vazia e o espelho COMPLETO,
            // o mesmo do botao "Recarregar catalogo".
            // Aqui rodar o completo e legitimo pelo motivo de sempre: quem pediu foi ele, clicando.
            void aplicarCatalogo(false)
              .then((preenchidos) => {
                if (preenchidos > 0) {
                  setTemAlteracoes(true);
                  toast.sucesso('Cadastrado no Catálogo. Clique em Salvar para a cotação acompanhar.');
                } else {
                  toast.sucesso('Cadastrado no Catálogo do fornecedor.');
                }
              })
              .catch(() => { void carregarDados(); });
          }}
        />
      )}

      <ModalPdfPronto pdf={pdfPronto} onFechar={() => setPdfPronto(null)} />

      {/* Modal Fornecedores Convidados */}
      {modalFornecedores && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: 'var(--modal-bg, var(--panel-bg, #fff))',
              color: 'var(--modal-fg, var(--panel-fg, #1f2937))',
              borderRadius: 8,
              boxShadow: '0 25px 70px rgba(0,0,0,0.25)',
              width: '100%',
              maxWidth: 480,
              maxHeight: 'calc(100vh - 16px)',
              display: 'flex',
              flexDirection: 'column',
              margin: 8,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header" style={{ padding: '12px 20px' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                Fornecedores Convidados ({fornecedorIdsSelecionados.length})
              </h2>
              <button
                type="button"
                className="btn-fechar-modal"
                aria-label="Fechar"
                onClick={() => {
                  setModalFornecedores(false);
                  setBuscaFornModal('');
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '12px 16px 8px' }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  autoFocus
                  value={buscaFornModal}
                  onChange={(e) => setBuscaFornModal(upperMantendoCursor(e))}
                  placeholder="Pesquisar fornecedor..."
                  style={{ width: '100%', padding: '6px 8px 6px 32px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 13 }}
                />
              </div>
            </div>
            <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto' }}>
              {fornecedoresModal
                .filter((f) => {
                  if (!buscaFornModal.trim()) return true;
                  const q = buscaFornModal.trim().toUpperCase();
                  const nome = String(getNomeFornecedor(f) || '').toUpperCase();
                  const cidade = (f.cidade || '').toUpperCase();
                  return nome.includes(q) || cidade.includes(q);
                })
                .slice()
                .sort((a, b) => {
                  // Convidados (ticados) sempre no topo, na ORDEM MANUAL ([DA-835]) — a do array, que
                  // nasce da ordem gravada e muda pelas setas; os nao convidados seguem alfabeticos.
                  const ia = fornecedorIdsSelecionados.indexOf(a.id);
                  const ib = fornecedorIdsSelecionados.indexOf(b.id);
                  if (ia >= 0 && ib >= 0) return ia - ib;
                  if (ia >= 0 || ib >= 0) return ia >= 0 ? -1 : 1;
                  return String(getNomeFornecedor(a) || '').localeCompare(String(getNomeFornecedor(b) || ''), 'pt-BR', { sensitivity: 'base' });
                })
                .map((f) => {
                  const sel = fornecedorIdsSelecionados.includes(f.id);
                  const posSel = fornecedorIdsSelecionados.indexOf(f.id);
                  // Com a busca preenchida as setas somem: o vizinho de cima/baixo pode estar escondido
                  // pelo filtro, e a seta trocaria com alguem que o comprador nao esta vendo.
                  const podeOrdenar = sel && !buscaFornModal.trim();
                  const fornVinc = fornecedores.find((fv) => fv.fornecedor_id === f.id);
                  // Total ganhos: itens onde o melhor preco (menor unit c/imp ou forcado) e deste fornecedor.
                  let itensGanhos = 0;
                  if (sel) {
                    for (const item of itensAtivos) {
                      const candidatos = fornecedores.flatMap((fv) =>
                        getResps(item.id, fv.fornecedor_id).map((r, ri) => ({
                          fornId: fv.fornecedor_id,
                          uci: calcPrecoUnitario(r),
                          forc: isForcarCompra(item.id, fv.fornecedor_id, ri),
                          ign: isDesconsiderado(item.id, fv.fornecedor_id, ri),
                        })),
                      );
                      const validos = candidatos.filter((c) => c.uci > 0 && !c.ign);
                      const forcado = validos.find((c) => c.forc);
                      const menor = validos.length ? Math.min(...validos.map((c) => c.uci)) : 0;
                      const winner = forcado ?? validos.find((c) => c.uci === menor);
                      if (winner?.fornId === f.id) itensGanhos++;
                    }
                  }
                  const menuAberto = menuFornModalAberto === f.id;
                  return (
                    <div
                      key={f.id}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: sel ? '1px solid var(--primary)' : '1px solid var(--border)',
                        background: sel ? 'var(--primary-soft)' : 'var(--panel-bg)',
                        fontSize: 13,
                        position: 'relative',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {podeOrdenar && (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                            <button
                              type="button"
                              onClick={() => moverFornecedorModal(f.id, -1)}
                              disabled={posSel === 0}
                              title="Subir (a ordem vale pra todas as cotacoes)"
                              style={{ background: 'transparent', border: 'none', padding: 0, cursor: posSel === 0 ? 'default' : 'pointer', opacity: posSel === 0 ? 0.25 : 1, display: 'flex' }}
                            >
                              <ChevronUp size={16} color="var(--primary)" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moverFornecedorModal(f.id, 1)}
                              disabled={posSel === fornecedorIdsSelecionados.length - 1}
                              title="Descer (a ordem vale pra todas as cotacoes)"
                              style={{ background: 'transparent', border: 'none', padding: 0, cursor: posSel === fornecedorIdsSelecionados.length - 1 ? 'default' : 'pointer', opacity: posSel === fornecedorIdsSelecionados.length - 1 ? 0.25 : 1, display: 'flex' }}
                            >
                              <ChevronDown size={16} color="var(--primary)" />
                            </button>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleFornecedorSelecionado(f.id)}
                          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', flex: 1, textAlign: 'left' }}
                        >
                          <div
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 3,
                              border: '1px solid',
                              borderColor: sel ? 'var(--primary)' : 'var(--border)',
                              background: sel ? 'var(--accent)' : 'var(--input-bg)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              marginRight: 8,
                            }}
                          >
                            {sel && <Check size={12} color="var(--accent-fg)" />}
                          </div>
                          {sel && fornVinc && (
                            <span
                              title={`${itensGanhos} ${itensGanhos === 1 ? 'item ganho' : 'itens ganhos'}`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 18,
                                height: 18,
                                background: itensGanhos > 0 ? '#22c55e' : 'var(--border)',
                                color: itensGanhos > 0 ? '#fff' : 'var(--text-muted)',
                                fontSize: 10,
                                fontWeight: 700,
                                borderRadius: 9,
                                padding: '0 5px',
                                marginRight: 6,
                                flexShrink: 0,
                              }}
                            >
                              {itensGanhos}
                            </span>
                          )}
                          <span style={{ color: sel ? 'var(--primary)' : 'var(--page-fg)', fontWeight: sel ? 500 : 400 }}>
                            {getNomeFornecedor(f)}
                            {!idsFornAtivos.has(f.id) && (
                              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>(inativo)</span>
                            )}
                          </span>
                        </button>
                        {sel && fornVinc && (
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setMenuFornModalAberto(menuAberto ? null : f.id); }}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-muted)', display: 'inline-flex' }}
                              title="Acoes do fornecedor"
                            >
                              <MoreVertical size={16} />
                            </button>
                            {menuAberto && (
                              <div
                                style={{ position: 'absolute', top: '100%', right: 0, marginTop: 2, background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 30, display: 'flex', flexDirection: 'column', minWidth: 200 }}
                                onMouseLeave={() => setMenuFornModalAberto(null)}
                              >
                                <button type="button" onClick={() => { gerarPdfFornecedor(fornVinc, 'retrato'); setMenuFornModalAberto(null); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--primary)', textAlign: 'left', whiteSpace: 'nowrap' }}>
                                  <FileText size={14} /> Salvar em PDF — Retrato
                                </button>
                                <button type="button" onClick={() => { gerarPdfFornecedor(fornVinc, 'paisagem'); setMenuFornModalAberto(null); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--primary)', textAlign: 'left', whiteSpace: 'nowrap' }}>
                                  <FileText size={14} /> Salvar em PDF — Paisagem
                                </button>
                                <button type="button" onClick={() => { abrirEnviarWhatsApp(fornVinc); setMenuFornModalAberto(null); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: '#0d9d4d', textAlign: 'left', whiteSpace: 'nowrap' }}>
                                  <MessageCircle size={14} /> Enviar PDF
                                </button>
                                <button type="button" onClick={() => { gerarPdfCompra(fornVinc); setMenuFornModalAberto(null); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: '#0f766e', textAlign: 'left', whiteSpace: 'nowrap' }}>
                                  <span style={{ fontWeight: 700, width: 14, textAlign: 'center' }}>$</span> Gerar PDF Compra
                                </button>
                                <button type="button" onClick={() => { gerarPdfPedidoCompra(fornVinc, 'retrato'); setMenuFornModalAberto(null); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: '#16a34a', textAlign: 'left', whiteSpace: 'nowrap' }}>
                                  <ShoppingCart size={14} /> Pedido de Compra — Retrato
                                </button>
                                <button type="button" onClick={() => { gerarPdfPedidoCompra(fornVinc, 'paisagem'); setMenuFornModalAberto(null); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: '#16a34a', textAlign: 'left', whiteSpace: 'nowrap' }}>
                                  <ShoppingCart size={14} /> Pedido de Compra — Paisagem
                                </button>
                                <button type="button" onClick={() => { setConfirmarLimparForn(fornVinc); setMenuFornModalAberto(null); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: '#dc2626', textAlign: 'left', whiteSpace: 'nowrap' }}>
                                  <Trash2 size={14} /> Limpar respostas
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {f.cidade && f.uf && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 24 }}>{f.cidade}/{f.uf}</div>}
                      {sel && fornVinc && (
                        <>
                          {/* O campo "Total c/Imp" saiu daqui em 29/10/2026 (pedido dele, [DA-864]): sobra da
                              [DA-149], ficava com o 100 do default e o PDF do WhatsApp rateava os precos por ele. */}
                          {/* Soma + Total Forn. vieram da barra de chips acima da tabela (DA-430) */}
                          <div style={{ marginLeft: 24, marginTop: 4, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14, fontSize: 12, color: '#475569' }}>
                            <span title="Nosso custo do cadastro x quantidade, de todos os itens da cotacao">
                              Nosso custo: <strong style={{ color: 'var(--primary)', fontSize: 13 }}>{formatMoeda(custoDaCotacao())}</strong>
                            </span>
                            <span title="Preco do fornecedor x quantidade, dos itens que ele cotou">
                              Total Forn.: <strong style={{ color: 'var(--primary)', fontSize: 13 }}>{formatMoeda(totalFornecedor(fornVinc.fornecedor_id))}</strong>
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 16, borderTop: '1px solid var(--border)' }}>
              <button
                type="button"
                onClick={() => {
                  setModalFornecedores(false);
                  setBuscaFornModal('');
                }}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button type="button" onClick={salvarFornecedoresConvidados} className="btn-primary">
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ⛔ O modal de importar cotacao por IA saiu com o botao que o abria (22/09/2026): ele
          gravava PRECO na resposta e no catalogo a partir da tabela do fornecedor. */}

      {/* Confirmar limpar fornecedor */}
      <ConfirmModal
        aberto={!!confirmarLimparForn}
        titulo="Limpar respostas do fornecedor?"
        mensagem={
          confirmarLimparForn
            ? `Zerar TODAS as respostas de "${getNomeFornecedor(confirmarLimparForn)}" nesta cotacao? Os dados serao apagados.`
            : ''
        }
        textoConfirmar="Limpar"
        perigoso
        onConfirmar={() => confirmarLimparForn && limparRespostasFornecedor(confirmarLimparForn)}
        onCancelar={() => setConfirmarLimparForn(null)}
      />

      {enviarWhatsForn && cotacao && (
        <ModalEnviarWhatsApp
          titulo="Enviar Pedido de Compra"
          telefoneInicial={enviarWhatsTel}
          contextoNome={getNomeFornecedor(enviarWhatsForn)}
          contextoExtra={`Cotacao ${cotacao.numero}`}
          inboxFiltro="compras"
          onClose={() => setEnviarWhatsForn(null)}
          onEnviar={async (digits) => {
            // gerar_pdf=true: backend gera PDF via Puppeteer (espelha o template do
            // gerarPdfPedidoCompra desta tela) e manda como anexo. mensagem vai como
            // caption — fica visivel ao lado do PDF no WhatsApp.
            await api.post(`/api/cotacoes/${cotacao.id}/enviar-whatsapp`, {
              jid: digits,
              gerar_pdf: true,
              fornecedor_id: enviarWhatsForn.fornecedor_id,
              caption: gerarTextoPedidoCompra(enviarWhatsForn),
            });
          }}
        />
      )}

      {/* Modal cadastro rapido de marca */}
      {novaMarcaCtx && (
        <ModalCadastroRapido
          titulo="Nova Marca"
          endpoint="/api/marcas"
          campos={[{ key: 'nome', label: 'Nome', obrigatorio: true }]}
          onSalvo={(novo: MarcaOpcao) => handleMarcaSalva(novo)}
          onFechar={() => setNovaMarcaCtx(null)}
        />
      )}

      {/* Modal historico do produto em todos os fornecedores */}
      <ModalHistoricoProduto
        aberto={!!historicoProduto}
        produto={historicoProduto}
        excluirCotacaoId={id ? Number(id) : null}
        onFechar={() => setHistoricoProduto(null)}
      />

      {vendas30d != null && (
        <ModalVendas30d
          produtoId={vendas30d}
          onFechar={() => setVendas30d(null)}
          onEnviar={(l) => {
            setVendas30d(null);
            setEnviarCotacao({ produto_id: l.id, descricao: l.descricao });
          }}
        />
      )}
      {enviarCotacao && (
        <ModalSelecionarCotacao
          produtoId={enviarCotacao.produto_id}
          produtoDesc={enviarCotacao.descricao}
          onClose={() => setEnviarCotacao(null)}
        />
      )}

      {/* Modal Split — separar itens comprados em nova cotacao ou existente */}
      {splitModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }}
        >
          <div
            style={{
              background: 'var(--panel-bg, #fff)',
              borderRadius: 8,
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              width: '100%',
              maxWidth: 480,
              margin: '0 16px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <Scissors size={18} className="text-emerald-600" />
                {splitModal === 'choose' ? 'Separar itens comprados' : 'Selecionar cotacao destino'}
              </h3>
              <button type="button" className="btn-fechar-modal" onClick={() => setSplitModal(null)} aria-label="Fechar">×</button>
            </div>

            {splitModal === 'choose' && (
              <div className="p-4">
                <div className="text-sm text-gray-600 mb-4">
                  {splitComprados.length} item(ns) marcado(s):
                  <ul className="mt-1 ml-4 list-disc text-xs text-gray-500">
                    {splitComprados.slice(0, 6).map((c) => (
                      <li key={`${c.item_id}-${c.fornecedor_id}`}>
                        {c.item_desc}{' '}
                        <span className="text-blue-600">({c.forn_nome})</span>
                      </li>
                    ))}
                    {splitComprados.length > 6 && (
                      <li>...e mais {splitComprados.length - 6}</li>
                    )}
                  </ul>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={splitNovaCotacao}
                    className="flex-1 flex flex-col items-center gap-2 px-4 py-4 rounded-lg border-2 border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 transition"
                  >
                    <Plus size={24} className="text-emerald-600" />
                    <span className="text-sm font-medium text-gray-700">Nova Cotacao</span>
                  </button>
                  <button
                    type="button"
                    onClick={abrirSplitSelecionar}
                    className="flex-1 flex flex-col items-center gap-2 px-4 py-4 rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition"
                  >
                    <Search size={24} className="text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">Selecionar Cotacao</span>
                  </button>
                </div>
              </div>
            )}

            {splitModal === 'select' && (
              <div className="p-4">
                <button
                  type="button"
                  onClick={() => setSplitModal('choose')}
                  className="text-sm text-blue-600 hover:underline mb-3 flex items-center gap-1"
                >
                  <ArrowLeft size={14} /> Voltar
                </button>
                {splitCotacoesLoading ? (
                  <div className="py-6 text-center text-gray-400 text-sm">Carregando...</div>
                ) : splitCotacoes.length === 0 ? (
                  <div className="py-6 text-center text-gray-400 text-sm">
                    Nenhuma cotacao aberta disponivel
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {splitCotacoes.map((cot) => (
                      <button
                        type="button"
                        key={cot.id}
                        onClick={() => splitParaCotacaoExistente(cot)}
                        className="w-full text-left px-3 py-2.5 hover:bg-blue-50 transition"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium text-sm text-gray-800 flex-shrink-0">
                              {cot.numero}
                            </span>
                            {cot.observacoes && (
                              <span className="text-xs text-gray-500 truncate">
                                — {cot.observacoes}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                            {cot.total_itens} itens
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {cot.comprador_nome || ''} — {cot.total_fornecedores} forn.
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Mover item — escolher cotação de destino (leva todos os fornecedores) */}
      {moverAlvo && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}
        >
          <div
            style={{ background: 'var(--panel-bg, #fff)', borderRadius: 8, boxShadow: '0 10px 30px rgba(0,0,0,0.2)', width: '100%', maxWidth: 520, margin: '0 16px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <FolderInput size={18} className="text-blue-600" />
                Mover para outra cotação
              </h3>
              <button type="button" className="btn-fechar-modal" onClick={() => setMoverAlvo(null)} aria-label="Fechar">×</button>
            </div>
            <div className="p-4">
              <div className="text-sm text-gray-600 mb-3">
                Movendo <span className="font-semibold text-gray-800">{moverAlvo.desc}</span> com <span className="font-semibold">todos os fornecedores</span>. Escolha a cotação de destino:
              </div>
              {moverCotacoesLoading ? (
                <div className="py-6 text-center text-gray-400 text-sm">Carregando...</div>
              ) : moverCotacoes.length === 0 ? (
                <div className="py-6 text-center text-gray-400 text-sm">Nenhuma cotação aberta disponível</div>
              ) : (
                <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {moverCotacoes.map((cot) => (
                    <button
                      type="button"
                      key={cot.id}
                      onClick={() => confirmarMoverItem(cot)}
                      disabled={moverEnviando}
                      className="w-full text-left px-3 py-2.5 hover:bg-blue-50 transition"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-sm text-gray-800 flex-shrink-0">{cot.numero}</span>
                          {cot.observacoes && (
                            <span className="text-xs text-gray-500 truncate">— {cot.observacoes}</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{cot.total_itens} itens</span>
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {cot.comprador_nome || ''} — {cot.total_fornecedores} forn.
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal zoom da foto */}
      {fotoZoom && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 60,
          }}
        >
          <button
            type="button"
            className="btn-fechar-modal btn-fechar-modal--abs"
            onClick={() => setFotoZoom(null)}
            aria-label="Fechar"
            style={{ color: '#fff' }}
          >
            ×
          </button>
          <img
            src={fotoZoom}
            alt="Zoom"
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================
// SUBCOMPONENTE — Tabela produto x fornecedor (modo normal)
// ============================================================

interface DragApi {
  dragIdx: number | null;
  dragOverIdx: number | null;
  dragOverPos: 'above' | 'below' | null;
  onDragStart: (idx: number, e: DragEvent<HTMLElement>) => void;
  onDragOver: (idx: number, e: DragEvent<HTMLTableRowElement>) => void;
  onDragEnd: () => void;
  onDrop: (idx: number, e: DragEvent<HTMLTableRowElement>) => Promise<void>;
  moverItem: (idx: number, delta: number) => Promise<void>;
}

// ============================================================
// Modal compacto: Cadastrar produto no Catalogo do Fornecedor
// ============================================================

function ModalCadastrarCatalogo(props: {
  fornecedorId: number;
  // NULL quando a linha da cotacao nao tem produto do nosso cadastro. Ai o codigo do fornecedor
  // deixa de ser opcional: sem produto e sem codigo o item do catalogo nao teria identidade
  // nenhuma, e a API recusa (invariante: PRODUTO ou CODIGO, nunca nenhum dos dois).
  produtoId: number | null;
  /**
   * O item da prateleira NOVOS PRODUTOS ([DA-610]). Faz aqui o mesmo papel do produto: da'
   * identidade a linha, entao o codigo do fornecedor volta a ser OPCIONAL — o que permite
   * cadastrar os cinco convidados na hora e caçar os codigos depois, sem travar a cotacao.
   */
  novoProdutoId: number | null;
  fornNome: string;
  prodDesc: string;
  /**
   * O que a linha da cotacao ja tem. Chega preenchido quando o preco veio da ANOTACAO do Novos
   * Produtos: o Ruan ja digitou fornecedor, codigo, embalagem e quantidade la, e cobrar de novo
   * aqui e' o retrabalho que esta tela existe pra tirar. `undefined` = modal em branco.
   */
  inicial?: {
    codigo_fornecedor: string;
    descricao_fornecedor: string;
    marca: string;
    embalagem: string;
    quantidade_embalagem: string;
  };
  onFechar: () => void;
  onSalvo: () => void;
}) {
  const { fornecedorId, produtoId, novoProdutoId, fornNome, prodDesc, inicial, onFechar, onSalvo } = props;
  const toast = useToast();
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({
    codigo_fornecedor: inicial?.codigo_fornecedor || '',
    descricao_fornecedor: inicial?.descricao_fornecedor || '',
    marca: inicial?.marca || '',
    embalagem: inicial?.embalagem || 'UN',
    quantidade_embalagem: inicial?.quantidade_embalagem || '1',
  });

  async function salvar() {
    if (!form.descricao_fornecedor.trim()) {
      toast.erro('Descrição do fornecedor é obrigatória');
      return;
    }
    if (produtoId == null && novoProdutoId == null && !form.codigo_fornecedor.trim()) {
      toast.erro('Este item não tem produto do nosso cadastro — informe o código do fornecedor.');
      return;
    }
    setSalvando(true);
    try {
      // A identidade e da EMPRESA e vai no corpo raiz; embalagem e quantidade sao da FILIAL e vao na
      // PERNA dela (DA-310). Preco nao entra aqui: este modal so amarra o codigo do fornecedor ao
      // produto — quem traz preco e a cotacao/o Monitor.
      await api.put('/api/fornecedor-catalogo/upsert', {
        // ⛔ A EMPRESA vai no corpo RAIZ, nao so dentro da perna. Faltando aqui, a rota responde
        // 400 "Informe o fornecedor do item." — e este botao devolvia isso desde que nasceu, pra
        // QUALQUER item, inclusive produto do cadastro: o `empresaId` sai de `body.fornecedor_id` e
        // o fallback (ler do item existente) so vale na EDICAO, nunca na criacao.
        // Nunca foi relatado porque a linha "Sem cadastro" e' rara e o erro do toast le como
        // problema do fornecedor, nao do botao. Achado exercitando a tela publicada ([DA-610]).
        fornecedor_id: fornecedorId,
        produto_id: produtoId,
        novo_produto_id: novoProdutoId,
        codigo_fornecedor: form.codigo_fornecedor.trim() || null,
        descricao_fornecedor: form.descricao_fornecedor.trim(),
        marca: form.marca.trim() || null,
        pernas: [{
          fornecedor_id: fornecedorId,
          embalagem: form.embalagem.trim() || 'UN',
          quantidade_embalagem: parseFloat(form.quantidade_embalagem.replace(',', '.')) || 1,
        }],
      });
      onSalvo();
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao salvar no catálogo');
    } finally {
      setSalvando(false);
    }
  }

  const field = (label: string, key: keyof typeof form, opts?: { required?: boolean }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
        {label}{opts?.required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      <input
        type="text"
        value={form[key]}
        onChange={(e) => setForm((prev) => ({ ...prev, [key]: upperMantendoCursor(e) }))}
        style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--input-border, #d1d5db)', fontSize: 13, background: 'var(--input-bg)', color: 'var(--text-color)' }}
      />
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--panel-bg)', borderRadius: 10, padding: 24, width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Cadastrar no Catálogo do Fornecedor</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
          <b>{fornNome}</b> · {prodDesc}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {field('Cód. do Fornecedor', 'codigo_fornecedor')}
          {field('Descrição do Fornecedor', 'descricao_fornecedor', { required: true })}
          {field('Marca', 'marca')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {field('Embalagem', 'embalagem')}
            {field('Qtd na Emb.', 'quantidade_embalagem')}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button type="button" onClick={onFechar} disabled={salvando}
            style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--panel-bg)', color: 'var(--page-fg)', fontSize: 13, cursor: 'pointer' }}>
            Cancelar
          </button>
          <button type="button" onClick={() => void salvar()} disabled={salvando}
            style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: salvando ? 'default' : 'pointer' }}>
            {salvando ? 'Salvando...' : 'Salvar no Catálogo'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================

interface ProdFornProps {
  /** Id da cotacao na URL — chaveia o que persiste por sessao (linha marcada). */
  cotacaoId: string;
  itens: ItemCotacao[];
  buscaProduto: string;
  fornecedores: FornecedorVinculado[];
  expandidos: Set<number>;
  toggleExpandir: (id: number) => void;
  getResps: (i: number, f: number) => RespostaForm[];
  setResp: (i: number, f: number, idx: number, campo: keyof RespostaForm, valor: any) => void;
  removeResp: (i: number, f: number, idx: number) => void;
  calcPrecoUnitario: (r: RespostaForm) => number;
  totalFornecedor: (f: number) => number;
  totalCImpForn: Record<number, string>;
  setTotalCImpForn: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  salvarTotalCImpForn: (f: number, v: string) => Promise<void>;
  atualizarItem: (id: number, campo: keyof ItemCotacao, valor: any) => void;
  removerItemCotacao: (id: number) => Promise<void>;
  abrirMoverItem: (item: { id: number; produto_descricao?: string | null; descricao?: string | null }) => void;
  moverEnviando: boolean;
  dragApi: DragApi;
  marcas: MarcaOpcao[];
  requestNovaMarca: (
    ctx:
      | { tipo: 'preferida'; itemId: number }
      | { tipo: 'resp'; itemId: number; fornId: number; respIdx: number },
  ) => void;
  requestHistoricoProduto: (p: ProdutoHistorico) => void;
  requestVendas30d: (produtoId: number) => void;
  isForcarCompra: (itemId: number, fornId: number, respIdx: number) => boolean;
  toggleForcarCompra: (itemId: number, fornId: number, respIdx: number) => void;
  isDesconsiderado: (itemId: number, fornId: number, respIdx: number) => boolean;
  toggleDesconsiderar: (itemId: number, fornId: number, respIdx: number) => void;
  isOcultoPdf: (itemId: number, fornId: number, respIdx: number) => boolean;
  toggleOcultoPdf: (itemId: number, fornId: number, respIdx: number) => void;
  setFotoZoom: (url: string | null) => void;
  setInserirCotacaoForn: (f: FornecedorVinculado | null) => void;
  setConfirmarLimparForn: (f: FornecedorVinculado | null) => void;
  gerarPdfFornecedor: (f: FornecedorVinculado, orientacao?: 'retrato' | 'paisagem') => void;
  gerarPdfPedidoCompra: (f: FornecedorVinculado, orientacao?: 'retrato' | 'paisagem') => void;
  abrirEnviarWhatsApp: (f: FornecedorVinculado) => void;
  podeEditarCatalogo: boolean;
  /** Pares "itemId-fornId" cujo preco veio da anotacao do Novos Produtos, sem lastro no Catalogo. */
  paresAnotacao: Set<string>;
  /** Abre a caixinha que corrige o preco no Catalogo sem sair da cotacao. Ausente = sem permissao
   *  ou cotacao fechada (a fechada e' foto e nao segue o catalogo). */
  abrirEditorPreco?: (
    item: ItemCotacao,
    forn: FornecedorVinculado,
    respIdx: number,
    rect: { top: number; left: number; bottom: number; right: number; width: number },
  ) => void;
  /** Embal./Qtd na Emb. da linha do fornecedor abrem a linha no Catalogo (e voltam pra ca ao fechar).
   *  Ausente = perfil que nao edita o Catalogo. */
  onAbrirNoCatalogo?: (fornecedorId: number, codigo: string) => void;
  /** Abre a caixinha que corrige a DATA da compra pelo selo "últ. compra" (DA-368). */
  onEditarData: (
    item: ItemCotacao,
    rect: { top: number; left: number; bottom: number; right: number; width: number },
  ) => void;
  /** Abre a janelinha da Unidade de Compra do cadastro (Embal./Qtd Emb). Ausente = perfil sem acesso. */
  onEditarEmbalagem?: (item: ItemCotacao) => void;
  /** Filtro "Esconder ja comprei nesta rodada" (DA-368) + a data de corte pra comparar. */
  escondeCompradas: boolean;
  /** Desliga o filtro pelo aviso da lista vazia — o checkbox mora fora deste componente. */
  setEscondeCompradas: (v: boolean) => void;
  cotacaoCriadoEm: string | null;
  /** Desfaz uma marcacao de "comprado" feita por engano (toggle por linha). */
  onDesmarcarComprado: (item: ItemCotacao) => void;
  /** Marca/desmarca a celula produto x fornecedor (checkbox "C") — pode NAO ser o vencedor de
   *  amarelo, a escolha e do comprador. */
  onMarcarCompradoCelula: (item: ItemCotacao, forn: FornecedorVinculado, comprado: boolean) => void;
}

/** Selo da filial de origem da linha (DA-391). Aparece so em empresa com mais de uma filial —
 *  MARTINS, TAMBASA e COFER tem GO + MG, e ali o mesmo codigo vira DUAS linhas com preco proprio.
 *  A de casa e informacao neutra (cinza-azulado); a de FORA e atencao laranja solido com letra
 *  preta (o padrao da casa pra aviso — daltonismo-safe, nunca verde/vermelho): aquele preco ainda
 *  vai levar DIFAL na entrada, entao comparar com o de casa sem enxergar isso engana. */
export function SeloFilial({ uf, daCasa }: { uf: string; daCasa: boolean }) {
  return (
    <span
      title={daCasa ? `Filial de ${uf} — no estado` : `Filial de ${uf} — sofre DIFAL na entrada`}
      style={{
        display: 'inline-block',
        marginRight: 6,
        padding: '0 5px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        lineHeight: '16px',
        verticalAlign: 'text-bottom',
        whiteSpace: 'nowrap',
        background: daCasa ? 'transparent' : '#f97316',
        color: daCasa ? 'var(--text-muted)' : '#000',
        border: daCasa ? '1px solid var(--border)' : '1px solid #c2410c',
      }}
    >
      {uf}
    </span>
  );
}

// O FornecedorChip (barra acima da tabela) foi removido na DA-430 — nome, Soma, Total Forn. e as
// acoes do menu ⋮ vivem no modal "Fornecedores Convidados", que ja tinha as mesmas 6 acoes.

function ProdutoFornecedorTable(props: ProdFornProps) {
  const {
    cotacaoId,
    itens,
    buscaProduto,
    fornecedores,
    expandidos,
    toggleExpandir,
    getResps,
    setResp,
    removeResp,
    calcPrecoUnitario,
    totalFornecedor,
    totalCImpForn,
    setTotalCImpForn,
    salvarTotalCImpForn,
    atualizarItem,
    removerItemCotacao,
    abrirMoverItem,
    moverEnviando,
    dragApi,
    marcas,
    requestNovaMarca,
    requestHistoricoProduto,
    requestVendas30d,
    isForcarCompra,
    toggleForcarCompra,
    isDesconsiderado,
    toggleDesconsiderar,
    isOcultoPdf,
    toggleOcultoPdf,
    setFotoZoom,
    podeEditarCatalogo,
    paresAnotacao,
    abrirEditorPreco,
    onAbrirNoCatalogo,
    onEditarData,
    onEditarEmbalagem,
    escondeCompradas,
    setEscondeCompradas,
    cotacaoCriadoEm,
    onDesmarcarComprado,
    onMarcarCompradoCelula,
  } = props;

  // Filtro da lista pelo campo de busca do topo (mesma regra do modo Inserir Cotacao) + o
  // "Esconder ja comprei nesta rodada" (DA-368). `itens` (a prop, lista COMPLETA) fica intacto —
  // so `itensVisiveis` filtra, pra `idx = itens.indexOf(item)` abaixo continuar apontando a
  // posicao real (drag-reorder e as setas operam sobre a lista completa, nao a filtrada).
  const itensVisiveis = useMemo(() => {
    let base = itens;
    if (escondeCompradas && cotacaoCriadoEm) {
      const corte = paraDataMs(cotacaoCriadoEm);
      base = base.filter((it) => !(it.ultima_compra_em && paraDataMs(it.ultima_compra_em) >= corte));
    }
    const q = (buscaProduto || '').trim().toLowerCase();
    if (q.length < 2) return base;
    return base.filter((it) =>
      [it.codigo_interno, it.produto_descricao, it.descricao].some(
        (c) => c && String(c).toLowerCase().includes(q),
      ),
    );
  }, [itens, buscaProduto, escondeCompradas, cotacaoCriadoEm]);

  // Quantos o "Esconder ja comprei" tirou — a lista vazia tem que dizer que foi ele, nao a busca.
  const ocultosComprados = useMemo(() => {
    if (!escondeCompradas || !cotacaoCriadoEm) return 0;
    const corte = paraDataMs(cotacaoCriadoEm);
    return itens.filter((it) => it.ultima_compra_em && paraDataMs(it.ultima_compra_em) >= corte).length;
  }, [itens, escondeCompradas, cotacaoCriadoEm]);

  // Linha-mestre marcada por clique — so REALCE (o clique segue expandindo/recolhendo).
  // Marca, nao alterna: clicar de novo na mesma linha nao apaga o destaque (padrao do useGridNavegavel).
  //
  // PERSISTE na sessao, com a mesma familia de chave dos `expandidos` (`cotacao_<id>_*`): F5 no
  // meio de uma cotacao longa devolvia a lista no topo e sem marca nenhuma, entao quem estava no
  // item 40 perdia o lugar. sessionStorage (nao localStorage) de proposito — vale pra recarga da
  // aba, e nao pra "onde eu estava semana passada".
  const [linhaSelecionada, setLinhaSelecionada] = useState<number | null>(() => {
    if (!cotacaoId) return null;
    try {
      const n = Number(sessionStorage.getItem(`cotacao_${cotacaoId}_linha`));
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!cotacaoId) return;
    try {
      if (linhaSelecionada == null) sessionStorage.removeItem(`cotacao_${cotacaoId}_linha`);
      else sessionStorage.setItem(`cotacao_${cotacaoId}_linha`, String(linhaSelecionada));
    } catch {
      /* ignore */
    }
  }, [linhaSelecionada, cotacaoId]);

  // Rola ate a linha restaurada UMA vez, quando os itens chegam do servidor. Devolver so o amarelo
  // sem levar a tela ate ele nao resolve nada — a marca ficaria fora da vista, que e o mesmo
  // "perdi o lugar". `?.` cobre o caso de a linha estar filtrada (busca / "esconder ja comprei").
  const jaRolouAteSelecionada = useRef(false);
  useEffect(() => {
    if (jaRolouAteSelecionada.current || linhaSelecionada == null || itens.length === 0) return;
    jaRolouAteSelecionada.current = true;
    requestAnimationFrame(() => {
      document.getElementById(`cot-item-${linhaSelecionada}`)?.scrollIntoView({ block: 'center' });
    });
  }, [itens, linhaSelecionada]);

  // ESTILOS reusaveis ---------------------------------------------------------
  const TH_BG = '#22d3ee';
  const TH_FG = '#0c4a6e';
  // Amarelo padrao de linha selecionada do sistema (useGridNavegavel). Aqui vive so na
  // linha-mestre do produto, que nao carrega o realce de "menor unitario" — esse mora nas
  // celulas Unitario das linhas de fornecedor, abaixo.
  const SEL_BG = '#fde68a';
  const thStyle = (w: number, align: 'left' | 'center' | 'right' = 'left'): React.CSSProperties => ({
    // w=0 => sem width (coluna auto): no table-layout:fixed ela absorve todo o espaco livre.
    width: w || undefined,
    padding: '8px 6px',
    fontSize: 13,
    fontWeight: 700,
    textTransform: 'uppercase',
    background: TH_BG,
    color: TH_FG,
    textAlign: align,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    borderBottom: '2px solid #0891b2',
  });
  const cellInput: React.CSSProperties = {
    width: '100%',
    padding: '3px 6px',
    border: '1px solid var(--border)',
    borderRadius: 3,
    fontSize: 13,
    background: '#fffbeb',
  };
  // Valores so-leitura como label: sem moldura, sem fundo proprio (herda da linha). Diferencia visualmente do que e editavel.
  const cellBox: React.CSSProperties = {
    display: 'block',
    width: '100%',
    padding: '3px 6px',
    border: 'none',
    borderRadius: 3,
    boxSizing: 'border-box',
    textAlign: 'right',
    background: 'transparent',
    fontSize: 13,
  };
  const btnIco: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 2,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const fornActionBtn = (bg: string): React.CSSProperties => ({
    padding: 3,
    background: bg,
    border: 'none',
    color: '#fff',
    borderRadius: 3,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    width: 22,
    height: 22,
  });

  // Calcula o total da linha: Tot Emb × Preço Emb (sem c/Imp) — o Preço Emb ja e o da caixa ([DA-864]).
  function totalLinha(resp: RespostaForm): number {
    const qDe = parseQtd(resp.quantidade_cotada);
    const pp = parseQtd(resp.preco_principal);
    return (Number.isFinite(qDe) ? qDe : 0) * (Number.isFinite(pp) ? pp : 0);
  }

  // Total geral: soma do menor unitario ganhador x qtd pedida de cada item.
  const totalGeralCompra = useMemo(() => {
    let total = 0;
    for (const item of itens) {
      if (itemInativo(item)) continue;
      const candidatos = fornecedores.flatMap((f) =>
        getResps(item.id, f.fornecedor_id).map((r, ri) => ({
          uci: calcPrecoUnitario(r),
          ignorado: isDesconsiderado(item.id, f.fornecedor_id, ri),
          forcado: isForcarCompra(item.id, f.fornecedor_id, ri),
          // Unidades da caixa do CADASTRO (30/10/2026); a do fornecedor so sem cadastro.
          qtdEmb: conteudoDaCaixaDoItem(item.qtd_embalagem_padrao, r.quantidade_embalagem),
        })),
      );
      const validos = candidatos.filter((c) => c.uci > 0 && !c.ignorado);
      const forcado = validos.find((c) => c.forcado);
      const menor = validos.length > 0 ? Math.min(...validos.map((c) => c.uci)) : 0;
      const winner = forcado ?? validos.find((c) => c.uci === menor);
      if (winner) {
        const qtdPedida = Number(item.quantidade) || 0;
        total += winner.uci * qtdPedida * winner.qtdEmb;
      }
    }
    return total;
  }, [itens, fornecedores, getResps, calcPrecoUnitario, isDesconsiderado, isForcarCompra]);

  /*
   * A tabela ocupa o que SOBRA da area rolavel, medido em runtime — mesma receita da
   * `EstatisticasContatos` ([DA-704]). O `calc(100vh - 240px)` de antes reservava 240px pro que vem
   * em cima, e em cima havia ~480 (barra de menus, avisos, cabecalho, busca): o fim da tabela caia
   * abaixo da tela e surgiam DUAS barras de rolagem (print do Ruan, 15/09/2026).
   * A conta e' `fundo da area rolavel − topo da tabela − o que vem depois dela`, e nenhum dos termos
   * depende da altura atual, entao converge numa passada. O observer vai no bloco da PAGINA (filho
   * direto da area rolavel): e' ele que muda quando a faixa de rascunho some ou os avisos recolhem.
   */
  const caixaTabelaRef = useRef<HTMLDivElement>(null);
  const [alturaTabela, setAlturaTabela] = useState<number | null>(null);
  useLayoutEffect(() => {
    const caixa = caixaTabelaRef.current;
    if (!caixa) return;
    let agendado = 0;
    const areaRolavel = (() => {
      let el = caixa.parentElement;
      while (el) {
        if (/(auto|scroll)/.test(getComputedStyle(el).overflowY)) return el;
        el = el.parentElement;
      }
      return null;
    })();
    let blocoPagina: HTMLElement = caixa;
    if (areaRolavel) {
      while (blocoPagina.parentElement && blocoPagina.parentElement !== areaRolavel) blocoPagina = blocoPagina.parentElement;
    }
    const medir = () => {
      agendado = 0;
      const rc = caixa.getBoundingClientRect();
      const depois = blocoPagina.getBoundingClientRect().bottom - rc.bottom;
      let limite = window.innerHeight;
      if (areaRolavel) {
        // O padding de baixo da area rolavel conta no `scrollHeight` e nao aparece no retangulo.
        const cs = getComputedStyle(areaRolavel);
        limite = areaRolavel.getBoundingClientRect().bottom
          - (parseFloat(cs.paddingBottom) || 0) - (parseFloat(cs.borderBottomWidth) || 0);
      }
      // Piso de 240: em janela muito baixa a pagina volta a rolar, em vez de a tabela sumir.
      const alvo = Math.max(240, Math.floor(limite - rc.top - depois));
      setAlturaTabela((atual) => (atual !== null && Math.abs(atual - alvo) <= 1 ? atual : alvo));
    };
    const agendar = () => { if (!agendado) agendado = requestAnimationFrame(medir); };
    medir();
    window.addEventListener('resize', agendar);
    const observador = new ResizeObserver(agendar);
    if (areaRolavel) observador.observe(areaRolavel);
    if (blocoPagina !== caixa) observador.observe(blocoPagina);
    return () => {
      if (agendado) cancelAnimationFrame(agendado);
      window.removeEventListener('resize', agendar);
      observador.disconnect();
    };
  }, []);

  return (
    <div>
      {/* Chips de fornecedor movidos pra linha do contador (componente pai) */}
    <div ref={caixaTabelaRef} style={{ background: 'var(--panel-bg)', borderRadius: 8, border: '1px solid var(--border)', overflow: 'auto', maxHeight: alturaTabela ?? 'calc(100vh - 240px)' }}>
      {/* --input-border preto: pinta a borda dos inputs (vence a regra global !important), dando a moldura preta nos campos de edicao.
          Colunas so-leitura recebem a mesma moldura via cellBox. Linhas da grade ficam sutis (var--border padrao). */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, tableLayout: 'fixed', '--input-border': '#000' } as React.CSSProperties}>
        <colgroup>
          <col style={{ width: 110 }} />{/* Fornecedor */}
          <col style={{ width: 56 }} />{/* Imagem */}
          {/* Codigo — 96px cabe o codigo do fornecedor de 10 digitos (medido: 80px de texto + 12 de
              padding). Nao cabe o de 16, que existe em 4 itens do catalogo contra 78 de 8-9 digitos:
              dimensionar pra excecao custaria ~50px em TODA linha (DA-448). O excedente quebra de
              linha em vez de invadir a Descricao — ver `overflowWrap` na celula. */}
          <col style={{ width: 96 }} />{/* Codigo */}
          <col />{/* Descricao — absorve todo o espaco livre */}
          <col style={{ width: 92 }} />{/* Alteracao — dd/mm/aaaa da ultima mudanca de preco no Catalogo */}
          <col style={{ width: 112 }} />{/* Marca */}
          <col style={{ width: 72 }} />{/* Embal. */}
          <col style={{ width: 80 }} />{/* Qtd na Emb */}
          <col style={{ width: 76 }} />{/* Qtd de Emb */}
          <col style={{ width: 100 }} />{/* Preço Emb — so-leitura (fonte = Catalogo); pacote que a loja paga */}
          <col style={{ width: 92 }} />{/* Unitário — Preço Emb ÷ Qtd na Emb; amarelo no menor preço */}
          <col style={{ width: 96 }} />{/* Total — so na linha da loja: Tot Emb × Qtd Emb × Unitário (custo) */}
          <col style={{ width: 126 }} />{/* Configurações — I/D/F/C + lixeira (DA-368: +22px pro checkbox C) */}
        </colgroup>
        <thead>
          <tr>
            <th style={{ ...thStyle(110, 'left'), borderRight: '1px solid #0891b2' }}>Fornecedor</th>
            <th style={{ ...thStyle(56, 'center'), borderRight: '1px solid #0891b2' }}>Imagem</th>
            <th style={{ ...thStyle(96, 'left'), borderRight: '1px solid #0891b2' }}>Código</th>
            <th style={{ ...thStyle(0, 'left'), borderRight: '1px solid #0891b2' }}>Descrição</th>
            <th style={{ ...thStyle(92, 'center'), borderRight: '1px solid #0891b2' }} title="Data da última mudança de preço no Catálogo do fornecedor">Alteração</th>
            <th style={{ ...thStyle(112, 'left'), borderRight: '1px solid #0891b2' }}>Marca</th>
            <th style={{ ...thStyle(72, 'center'), borderRight: '1px solid #0891b2' }}>Embal.</th>
            {/* Titulos curtos pra caber numa linha (pedido do Ruan, 15/09/2026): "Qtd na Emb" pedia
                82px num espaco de 67 e dobrava a altura do cabecalho. "Qtd Emb" = unidades por
                embalagem; "Tot Emb" = quantas embalagens comprar (era "Qtd de Emb"). */}
            <th style={{ ...thStyle(80, 'center'), borderRight: '1px solid #0891b2' }}>Qtd Emb</th>
            <th style={{ ...thStyle(76, 'center'), borderRight: '2px solid #0891b2' }}>Tot Emb</th>
            <th style={{ ...thStyle(100, 'right'), borderRight: '1px solid #0891b2' }} title="Preço da embalagem/caixa — vem do Catálogo do fornecedor (alterar lá)">Preço Emb</th>
            <th style={{ ...thStyle(92, 'right'), borderRight: '2px solid #0891b2' }} title="Preço Emb ÷ Qtd na Emb — é o que decide o menor preço; amarelo no vencedor">Unitário</th>
            <th style={{ ...thStyle(96, 'right'), borderRight: '2px solid #0891b2' }} title="Tot Emb × Qtd Emb × Unitário da loja (custo cadastrado)">Total</th>
            <th style={{ ...thStyle(126, 'center') }} title="Incluir no PDF (I) · Desconsiderar (D) · Forçar compra (F) · Comprei (C) · Remover">Configurações</th>
          </tr>
        </thead>
        <tbody>
          {itensVisiveis.map((item) => {
            // idx = posicao na lista COMPLETA (drag-reorder e numeracao operam sobre `itens`,
            // nao sobre a lista filtrada). itensVisiveis reusa as mesmas referencias de itens.
            const idx = itens.indexOf(item);
            const exp = expandidos.has(item.id);
            // Calculo do melhor preco do produto (ranking por Unit. simples, exclui ignorados).
            const candidatos = fornecedores.flatMap((f) =>
              getResps(item.id, f.fornecedor_id).map((r, ri) => ({
                uci: calcPrecoUnitario(r),
                fornNome: getNomeFornecedor(f),
                fornId: f.fornecedor_id,
                respIdx: ri,
                ignorado: isDesconsiderado(item.id, f.fornecedor_id, ri),
                forcado: isForcarCompra(item.id, f.fornecedor_id, ri),
              })),
            );
            const forcadosItem = candidatos.filter((c) => c.forcado && c.uci > 0);
            const temForcadoItem = forcadosItem.length > 0;
            const validos = candidatos.filter((c) => c.uci > 0 && !c.ignorado);
            const menorPreco = validos.length > 0 ? Math.min(...validos.map((c) => c.uci)) : 0;

            // Linha marcada: fundo amarelo + texto forcado escuro (no tema dark o --page-fg e
            // claro e sumiria no amarelo). Mesma receita da vista lado a lado.
            const sel = linhaSelecionada === item.id;
            const cFg = sel ? '#000' : 'var(--page-fg)';
            const cMuted = sel ? '#78350f' : 'var(--text-muted)';
            // Produto INATIVO: linha riscada e travada (pedido dele, 28/10/2026) — so remover/mover/ordenar.
            const inativo = itemInativo(item);
            // Item com produto do cadastro grava a Unidade de Compra na ficha; item da prateleira NOVOS
            // PRODUTOS grava a embalagem no cadastro DELE (31/10/2026). Item so de catalogo nao tem onde.
            const editarEmbalagem = onEditarEmbalagem && (item.produto_id != null || item.novo_produto_id != null) && !inativo
              ? () => onEditarEmbalagem(item) : undefined;

            const isDragSource = dragApi.dragIdx === idx;
            const isDropAbove =
              dragApi.dragOverIdx === idx && dragApi.dragOverPos === 'above' && dragApi.dragIdx !== idx;
            const isDropBelow =
              dragApi.dragOverIdx === idx && dragApi.dragOverPos === 'below' && dragApi.dragIdx !== idx;

            return (
              <Fragment key={item.id}>
                {/* Linha de cabecalho do produto (drag/expand/foto/descricao/marca pref/embal/qtd...) */}
                <tr
                  id={`cot-item-${item.id}`}
                  onDragOver={(e) => dragApi.onDragOver(idx, e)}
                  onDragEnd={dragApi.onDragEnd}
                  onDrop={(e) => dragApi.onDrop(idx, e)}
                  onClick={() => { setLinhaSelecionada(item.id); toggleExpandir(item.id); }}
                  style={{
                    borderTop: idx === 0 ? 'none' : '4px solid var(--page-fg)',
                    background: sel ? SEL_BG : idx % 2 === 1 ? 'var(--grid-row-alt-bg)' : 'var(--panel-bg)',
                    cursor: 'pointer',
                    opacity: isDragSource ? 0.4 : 1,
                    boxShadow: isDropAbove
                      ? 'inset 0 3px 0 var(--primary)'
                      : isDropBelow
                        ? 'inset 0 -3px 0 var(--primary)'
                        : sel
                          ? 'inset 3px 0 0 var(--accent)'
                          : 'none',
                  }}
                >
                  {/* Fornecedor (mestre): zona de controles — indice + expandir + arrastar */}
                  <td style={{ padding: '8px 6px', borderRight: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: cMuted, width: 16, textAlign: 'center', flexShrink: 0 }}>{idx + 1}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleExpandir(item.id); }}
                        style={btnIco}
                        title={exp ? 'Recolher fornecedores' : 'Expandir fornecedores'}
                      >
                        {exp ? <Minus size={14} color="var(--primary)" /> : <Plus size={14} color="var(--primary)" />}
                      </button>
                      <span
                        draggable
                        onDragStart={(e) => dragApi.onDragStart(idx, e as unknown as DragEvent<HTMLElement>)}
                        style={{ ...btnIco, cursor: 'grab' }}
                        title="Arraste para reordenar"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <GripVertical size={14} color="#9ca3af" />
                      </span>
                      {/* Setas cima/baixo — reposiciona sem arrastar (drag trava em algumas telas) */}
                      <div style={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); void dragApi.moverItem(idx, -1); }}
                          disabled={idx === 0}
                          style={{ ...btnIco, padding: 0, opacity: idx === 0 ? 0.25 : 1, cursor: idx === 0 ? 'default' : 'pointer' }}
                          title="Mover para cima"
                        >
                          <ChevronUp size={16} color="var(--primary)" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); void dragApi.moverItem(idx, 1); }}
                          disabled={idx === itens.length - 1}
                          style={{ ...btnIco, padding: 0, opacity: idx === itens.length - 1 ? 0.25 : 1, cursor: idx === itens.length - 1 ? 'default' : 'pointer' }}
                          title="Mover para baixo"
                        >
                          <ChevronDown size={16} color="var(--primary)" />
                        </button>
                      </div>
                    </div>
                  </td>
                  {/* Imagem (mestre): foto do produto */}
                  <td style={{ padding: '6px 4px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                    {item.foto_url ? (
                      <FotoProduto
                        url={item.foto_url}
                        tam={40}
                        style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }}
                        onClick={(e) => { e.stopPropagation(); setFotoZoom(item.foto_url); }}
                      />
                    ) : (
                      <div style={{ width: 40, height: 40, background: 'var(--grid-row-alt-bg)', borderRadius: 4, border: '1px solid var(--border)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ImageOff size={14} color="#9ca3af" />
                      </div>
                    )}
                  </td>
                  {/* Codigo (mestre): nosso codigo interno — ou o da prateleira nova, com o selo
                      lilas que a distingue ([DA-610]). Vazia, esta coluna deixava tres itens de
                      nome parecido indistinguiveis na grade. */}
                  <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 700, color: cMuted, borderRight: '1px solid var(--border)' }}>
                    {item.codigo_interno || (item.np_codigo && (
                      <span style={{ background: '#c4b5fd', color: '#000', borderRadius: 3, padding: '1px 6px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}
                            title="Item da prateleira Novos Produtos — ainda nao virou produto do cadastro">
                        {item.np_codigo}
                      </span>
                    ))}
                  </td>
                  {/* Descricao (mestre): nossa descricao + Estoque atual */}
                  <td style={{ padding: '6px 8px', borderRight: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {inativo && <SeloInativo style={{ marginRight: 0 }} />}
                      <span
                        style={{
                          fontSize: 15, fontWeight: 700, color: cFg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
                          ...(inativo ? { textDecoration: 'line-through' } : {}),
                        }}
                        title={inativo ? `${MSG_PRODUTO_INATIVO}. Fora do total e do que vai pro fornecedor.` : undefined}
                      >
                        {item.produto_descricao || item.descricao}
                      </span>
                      <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: cMuted, whiteSpace: 'nowrap' }}>
                        (Estoque {Number(item.estoque_atual || 0).toLocaleString('pt-BR')})
                      </span>
                      {item.produto_id != null && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            requestHistoricoProduto({
                              id: item.produto_id!,
                              codigo_interno: item.codigo_interno,
                              descricao: item.produto_descricao || item.descricao,
                              foto_url: item.foto_url,
                            });
                          }}
                          style={btnIco}
                          title="Histórico em todos os fornecedores"
                        >
                          <BookOpen size={14} color="#9ca3af" />
                        </button>
                      )}
                    </div>
                    {/* Selo de ultima compra (DA-368) — so aparece com dado; toggle desfaz uma
                        marcacao feita por engano. Discreto (texto, sem cor de fundo) pra nao
                        colidir com o amarelo de "menor unitario" das linhas abaixo. */}
                    {item.ultima_compra_em ? (
                      <div style={{ marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onEditarData(item, e.currentTarget.getBoundingClientRect()); }}
                          style={{ background: 'var(--primary)', border: 'none', padding: '2px 8px', margin: 0, cursor: 'pointer', color: '#fff', fontSize: 11, textAlign: 'left', fontFamily: 'inherit', fontWeight: 700, borderRadius: 12 }}
                          title="Ajustar a data da compra"
                        >
                          🛒 últ. compra {formatDataSP(item.ultima_compra_em)}
                          {item.ultima_compra_fornecedor_nome ? ` · ${item.ultima_compra_fornecedor_nome}` : ''}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onDesmarcarComprado(item); }}
                          style={{ ...btnIco, padding: 0 }}
                          title="Desmarcar (desfazer 'comprado')"
                        >
                          <X size={12} color={cMuted} />
                        </button>
                      </div>
                    ) : item.ultima_entrada_xml_em ? (
                      <div style={{ marginTop: 2 }}>
                        <span
                          style={{ background: 'var(--primary)', padding: '2px 8px', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 12, opacity: 0.6 }}
                          title="Data da última entrada via XML (sem marcação de compra nesta cotação)"
                        >
                          📦 últ. entrada {formatDataSP(item.ultima_entrada_xml_em)}
                          {item.ultima_entrada_xml_fornecedor_nome ? ` · ${item.ultima_entrada_xml_fornecedor_nome}` : ''}
                        </span>
                      </div>
                    ) : null}
                  </td>
                  {/* Alteracao (mestre): a data e de cada perna do fornecedor, nas linhas abaixo. Aqui
                      fica o botao das vendas dos ultimos 30 dias (pedido dele, 30/10/2026) — o mesmo
                      da lista de Produtos (DA-862), abrindo a mesma janela do Mais Vendidos. */}
                  <td style={{ padding: '6px 4px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                    {item.produto_id != null && item.vendas_30d != null && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); requestVendas30d(item.produto_id!); }}
                        title="Vendas dos ultimos 30 dias — clique para ver a linha do Mais Vendidos"
                        style={{ background: '#fff', color: Number(item.vendas_30d) > 0 ? '#000' : '#475569', border: '1px solid #94a3b8', borderRadius: 8, padding: '1px 8px', fontSize: 11, lineHeight: '14px', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}
                      >
                        {/* Duas linhas: em uma ("10 vendas · 30d") passava dos 92px da coluna em 1920 a 125% */}
                        <span style={{ display: 'block' }}>{Number(item.vendas_30d)} {Number(item.vendas_30d) === 1 ? 'venda' : 'vendas'}</span>
                        <span style={{ display: 'block', fontSize: 10, fontWeight: 600 }}>30 dias</span>
                      </button>
                    )}
                  </td>
                  {/* Marca preferida — alinhada com Marca dos fornecedores */}
                  <td style={{ padding: 2, borderRight: '1px solid var(--border)' }} onClick={(e) => e.stopPropagation()}>
                    {inativo ? (
                      <span style={{ display: 'block', padding: '3px 6px', fontSize: 13, color: cMuted }}>
                        {item.marca_preferida || item.produto_marca || '—'}
                      </span>
                    ) : (
                      <AutocompleteMarca
                        value={item.marca_preferida || item.produto_marca || ''}
                        onChange={(v) => atualizarItem(item.id, 'marca_preferida', v)}
                        marcas={marcas}
                        onNovaMarca={() => requestNovaMarca({ tipo: 'preferida', itemId: item.id })}
                        placeholder={item.produto_marca || 'Pref.'}
                        inputStyle={{ padding: '3px 6px', fontSize: 13 }}
                      />
                    )}
                  </td>
                  {/* Embal. - unidade_sigla do item (BB, UN, RL...). Embal. e Qtd na Emb vem do cadastro:
                      o clique abre a janelinha que grava la (ModalEmbalagemCompra). */}
                  <td
                    style={{
                      padding: '6px 4px', textAlign: 'center', fontSize: 13, color: cFg, borderRight: '1px solid var(--border)',
                      cursor: editarEmbalagem ? 'pointer' : undefined,
                    }}
                    title={editarEmbalagem ? (item.produto_id != null ? 'Clique para editar a Unidade de Compra no cadastro do produto' : 'Clique para editar a embalagem no cadastro do Novo Produto') : undefined}
                    onClick={editarEmbalagem}
                  >
                    {item.unidade_sigla || '—'}
                  </td>
                  {/* Qtd na Emb - do cadastro (mesmo clique da Embal.) */}
                  <td
                    style={{
                      padding: '6px 4px', textAlign: 'center', fontSize: 13, color: cFg, borderRight: '1px solid var(--border)',
                      cursor: editarEmbalagem ? 'pointer' : undefined,
                    }}
                    title={editarEmbalagem ? (item.produto_id != null ? 'Clique para editar a Unidade de Compra no cadastro do produto' : 'Clique para editar a embalagem no cadastro do Novo Produto') : undefined}
                    onClick={editarEmbalagem}
                  >
                    {item.qtd_embalagem_padrao && Number(item.qtd_embalagem_padrao) > 0 ? (
                      <>
                        <span style={{ fontWeight: 600 }}>{Number(item.qtd_embalagem_padrao).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</span>
                        <span style={{ marginLeft: 3, color: cMuted, fontSize: 12 }}>
                          {(item.unidade_conteudo_embalagem || item.unidade_venda_sigla || '').toUpperCase()}
                        </span>
                      </>
                    ) : (
                      <span style={{ color: 'var(--border)' }}>—</span>
                    )}
                  </td>
                  {/* Qtd de Emb - editavel (quantas embalagens compraremos) */}
                  <td style={{ padding: 2, borderRight: '2px solid var(--border)' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={item.quantidade}
                      onChange={(e) => atualizarItem(item.id, 'quantidade', e.target.value)}
                      disabled={inativo}
                      style={{ ...cellInput, textAlign: 'center' }}
                    />
                  </td>
                  {/* Preço Emb (mestre): vazio */}
                  <td style={{ borderRight: '1px solid var(--border)' }} />
                  {/* Unitário (mestre): nosso custo cadastrado, alinhado sobre o Unitário dos fornecedores */}
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: cFg, borderRight: '2px solid var(--border)' }}>
                    {Number(item.preco_custo || 0) > 0 ? formatMoedaSemSimbolo(Number(item.preco_custo), 4) : '—'}
                  </td>
                  {/* Total (mestre, so na linha da loja — pedido dele, 28/10/2026): Tot Emb × Qtd Emb ×
                      Unitário. O custo do cadastro e' por unidade do CONTEUDO (144 de 144 produtos com
                      embalagem: custo abaixo do preco Pix; o 0781 custa 5,18 e a caixa de 12 sai 62,14),
                      e o Tot Emb conta caixas (regra dele, 29/10). A primeira versao fazia Tot Emb ×
                      custo e 3 caixas de disjuntor davam R$ 15,53. A quantidade chega do banco como
                      "10.000" ou digitada como "1,5" — por isso a virgula vira ponto e nada de parseQtd. */}
                  {(() => {
                    const total = inativo ? 0 : custoLojaDoItem(item);
                    return (
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: cFg, borderRight: '2px solid var(--border)' }}>
                        {total > 0 ? formatMoedaSemSimbolo(total, 2) : '—'}
                      </td>
                    );
                  })()}
                  {/* Configurações (mestre): mover item pra outra cotação + remover */}
                  <td style={{ padding: 2, textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'center' }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); void abrirMoverItem(item); }}
                        style={btnIco}
                        title="Mover este item para outra cotação (leva todos os fornecedores)"
                        disabled={moverEnviando}
                      >
                        <FolderInput size={16} color="var(--primary)" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm(`Remover "${item.produto_descricao || item.descricao}" da cotação?`)) {
                            void removerItemCotacao(item.id);
                          }
                        }}
                        style={btnIco}
                        title="Remover item da cotação"
                      >
                        <Trash2 size={16} color="#dc2626" />
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Linhas dos fornecedores (uma N por fornecedor; cada fornecedor pode ter varias respostas) */}
                {exp && fornecedores.map((forn, fornIdx) => {
                  const linhas = getResps(item.id, forn.fornecedor_id);
                  const temFiliais = Number(forn.total_filiais || 0) > 1;
                  return linhas.map((resp, ri) => {
                    const pu = calcPrecoUnitario(resp);
                    const ignorado = isDesconsiderado(item.id, forn.fornecedor_id, ri);
                    const forcado = isForcarCompra(item.id, forn.fornecedor_id, ri);
                    // Item inativo nao disputa: sem realce de menor preco nem de forcado.
                    const isMenor = !inativo &&
                      ((pu > 0 && menorPreco > 0 && pu === menorPreco && !ignorado && !temForcadoItem) || forcado);
                    const isFirst = ri === 0;
                    // O preco desta linha veio da ANOTACAO do Novos Produtos e nao tem lastro no
                    // Catalogo do fornecedor. Sem este aviso a linha ficaria identica a uma
                    // cadastrada de verdade — a tela afirmaria um cadastro que nao existe, e o
                    // botao que promove desapareceria junto com o estado "Sem cadastro".
                    const ehAnotacao = paresAnotacao.has(`${item.id}-${forn.fornecedor_id}`);
                    // Embal./Qtd na Emb. abrem a linha no Catalogo — so com codigo (e' por ele que o
                    // Catalogo acha a linha) e com permissao de editar o Catalogo.
                    const codCatalogo = resp.cod_produto_fornecedor;
                    const abreNoCatalogo = onAbrirNoCatalogo && codCatalogo
                      ? () => onAbrirNoCatalogo(forn.fornecedor_id, codCatalogo)
                      : undefined;
                    const propsCelulaCatalogo = {
                      style: { padding: 2, borderRight: '1px solid var(--border)', cursor: abreNoCatalogo ? 'pointer' : undefined },
                      title: abreNoCatalogo ? 'Abrir no Catálogo do fornecedor' : undefined,
                      onClick: abreNoCatalogo,
                    };
                    const sublinhadoCatalogo = abreNoCatalogo ? 'underline dotted' : undefined;

                    return (
                      <tr
                        key={`${item.id}-${forn.fornecedor_id}-${ri}`}
                        style={{
                          borderBottom: '1px solid var(--border)',
                          background: fornIdx % 2 === 1 ? 'var(--grid-row-alt-bg)' : 'var(--panel-bg)',
                        }}
                      >
                        {isFirst && (
                          <td rowSpan={linhas.length} style={{ padding: '6px 8px', verticalAlign: 'middle', fontSize: 13, fontWeight: 700, color: '#0c4a6e', borderRight: '1px solid var(--border)' }}>
                            {getNomeFornecedor(forn)}
                          </td>
                        )}
                        {/* Imagem — em branco na linha do fornecedor (a foto e do produto, na linha-mestre) */}
                        <td style={{ borderRight: '1px solid var(--border)' }} />
                        {/* Colunas do catalogo: read-only (fonte = fornecedor_catalogo).
                            Sem entrada no catalogo, colSpan=6 so com o AVISO — o botao "Cadastrar"
                            saiu em 22/09/2026 junto com as portas de preco: criar registro de
                            catalogo a partir da cotacao e escrever na fonte de fora dela. O aviso
                            fica, e diz onde se resolve, pra a linha nao virar beco. */}
                        {(!resp.cod_produto_fornecedor && !resp.descricao_fornecedor) ? (
                          <td colSpan={6} style={{ padding: '4px 8px', borderRight: '2px solid var(--border)', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                              <span>Sem cadastro no Catálogo do fornecedor — cadastre por lá</span>
                            </div>
                          </td>
                        ) : (
                          <>
                            {/* Cod. Fornec. — read-only */}
                            <td style={{ padding: 2, borderRight: '1px solid var(--border)' }}>
                              {/* Alinhado a ESQUERDA como o cabecalho e como o codigo do produto na
                                  linha de cima. Com o `textAlign: 'right'` do cellBox, a sobra da
                                  coluna ficava do lado errado: o codigo encostava na Descricao (que
                                  alinha a esquerda) e os dois liam como um texto so. `overflowWrap`
                                  segura o codigo longo dentro da coluna — cortar nao serve, e o
                                  numero que o fornecedor usa pra achar o item. */}
                              <div style={{ ...cellBox, fontSize: 13, textAlign: 'left', overflowWrap: 'anywhere' }}>{resp.cod_produto_fornecedor || '—'}</div>
                            </td>
                            {/* Desc. Fornecedor — read-only. Leva o selo da UF quando a empresa tem
                                mais de uma filial (DA-391): ali a MESMA descricao e o MESMO codigo
                                aparecem duas vezes, uma por filial, e sem o selo as linhas ficam
                                gemeas. A descricao e a coluna elastica — o selo cabe sem apertar
                                nenhuma largura fixa. */}
                            <td style={{ padding: 2, borderRight: '1px solid var(--border)' }}>
                              <div style={{ ...cellBox, fontSize: 13, textAlign: 'left', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {temFiliais && resp.filial_uf && <SeloFilial uf={resp.filial_uf} daCasa={resp.filial_principal} />}
                                {resp.descricao_fornecedor || '—'}
                              </div>
                              {/* A procedencia da linha da anotacao. Vai AQUI, na coluna elastica,
                                  pelo mesmo motivo do selo da UF: nenhuma largura fixa aperta. Nao
                                  usa cor pra significar nada (daltonismo) — quem informa e o texto. */}
                              {ehAnotacao && isFirst && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, paddingLeft: 4, fontSize: 10, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                                  <span
                                    title="Ainda não há cadastro deste item no Catálogo deste fornecedor. O preço veio do que você digitou em Novos Produtos."
                                    style={{ fontWeight: 700, fontSize: 9, padding: '0 4px', borderRadius: 3, border: '1px solid var(--border)', letterSpacing: 0.4 }}
                                  >
                                    NP
                                  </span>
                                  <span>do cadastro Novos Produtos</span>
                                  {/* O "Cadastrar no Catálogo" saiu com o outro (22/09/2026): e a
                                      MESMA escrita no catalogo, so que na linha do NP. */}
                                </div>
                              )}
                            </td>
                            {/* Alteracao — read-only: quando o PRECO desta perna mudou pela ultima vez
                                no Catalogo (mesma data da coluna "Atualizado em" de la). Hora no title.
                                Sem data com preco = preco anterior a 14/07/2026, quando o Catalogo
                                passou a carimbar (medido 21/10: 298 pernas, todas nascidas na
                                migracao da DA-310; nenhuma depois de 16/07). */}
                            <td style={{ padding: 2, borderRight: '1px solid var(--border)' }}>
                              <div
                                style={{ ...cellBox, fontSize: 13, textAlign: 'center' }}
                                title={resp.preco_atualizado_em
                                  ? `Preço alterado em ${formatDataSP(resp.preco_atualizado_em)} ${formatHoraSP(resp.preco_atualizado_em)}`
                                  : resp.preco_principal.trim()
                                    ? 'Preço sem data: anterior a 14/07/2026, quando o Catálogo passou a registrar as alterações'
                                    : 'Sem preço no Catálogo do fornecedor'}
                              >
                                {resp.preco_atualizado_em ? formatDataSP(resp.preco_atualizado_em) : '—'}
                              </div>
                            </td>
                            {/* Marca — read-only */}
                            <td style={{ padding: 2, borderRight: '1px solid var(--border)' }}>
                              <div style={{ ...cellBox, fontSize: 13, textAlign: 'left' }}>{resp.marca || '—'}</div>
                            </td>
                            {/* Embal. — read-only (fallback: unidade de compra do produto). O clique
                                abre a linha no Catalogo, onde ela se edita (ver abrirNoCatalogo). */}
                            <td {...propsCelulaCatalogo}>
                              <div style={{ ...cellBox, fontSize: 13, textAlign: 'center', textDecoration: sublinhadoCatalogo }}>{resp.embalagem || item.unidade_sigla || '—'}</div>
                            </td>
                            {/* Qtd na Emb — read-only; o clique abre no Catalogo, como a Embal. */}
                            <td {...propsCelulaCatalogo}>
                              {/* `textAlign`, nao `justifyContent`: o `cellBox` e display:block e
                                  ignora propriedade de flex — a centralizacao estava escrita e
                                  inerte, e o valor caia no `textAlign: 'right'` do cellBox,
                                  desalinhado da mesma coluna na linha do produto (que centraliza). */}
                              <div style={{ ...cellBox, fontSize: 13, textAlign: 'center', textDecoration: sublinhadoCatalogo }}>{resp.quantidade_embalagem || '—'}</div>
                            </td>
                          </>
                        )}
                        {/* Qtd de Emb */}
                        <td style={{ padding: 2, borderRight: '2px solid var(--border)' }}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={resp.quantidade_cotada ?? ''}
                            onChange={(e) => setResp(item.id, forn.fornecedor_id, ri, 'quantidade_cotada', e.target.value)}
                            disabled={inativo}
                            style={{ ...cellInput, textAlign: 'center' }}
                          />
                        </td>
                        {/* PREÇO EMB — o pacote que a loja paga. A fonte e o Catalogo do fornecedor;
                            o clique abre a caixinha que grava LA (religada em 28/10/2026, pedido
                            dele — ver abrirEditorPreco). Sem realce: quem decide o menor preco e o
                            Unitario. */}
                        <td style={{ padding: 2, borderRight: '1px solid var(--border)' }}>
                          <div
                            onClick={(e) => {
                              if (!abrirEditorPreco || inativo) return;
                              abrirEditorPreco(item, forn, ri, e.currentTarget.getBoundingClientRect());
                            }}
                            title={abrirEditorPreco && !inativo
                              ? 'Preço da embalagem (Catálogo do fornecedor) — clique para corrigir'
                              : 'Preço vem do Catálogo do fornecedor'}
                            style={{
                              ...cellBox,
                              fontWeight: 700,
                              // Veste a MESMA cara dos campos editaveis desta tabela (o `cellInput`
                              // da Qtd de Emb, ao lado). Na lado a lado o creme se confundiria com o
                              // amarelo de "menor unitario" — la o sinal e o tracejado.
                              ...(abrirEditorPreco && !inativo
                                ? { cursor: 'pointer', background: '#fffbeb', border: '1px solid var(--border)' }
                                : {}),
                              ...(ignorado ? { textDecoration: 'line-through', color: 'var(--text-muted)' } : {}),
                            }}
                          >
                            {(() => {
                              // [DA-727] A celula mostra a embalagem COM DIFAL (decisao dele); o preco
                              // do fornecedor recua pra linha de baixo, porque e ele que se edita no
                              // clique e e ele que o extrato/pedido do fornecedor mostra.
                              const pp = parseQtd(resp.preco_principal);
                              if (!resp.preco_principal || !Number.isFinite(pp) || pp <= 0) return resp.preco_principal ? resp.preco_principal : '—';
                              if (!difalSeAplica(resp.filial_uf)) return resp.preco_principal;
                              const estimado = !aliquotaDifalValida(resp.difal_pct);
                              const pct = estimado ? '4,49' : Number(resp.difal_pct).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
                              return (
                                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.15 }}
                                  title={`Preco do fornecedor ${resp.preco_principal} + DIFAL ${pct}%${estimado ? ' (% laranja: aliquota a conferir — estimativa de 4,49%; escolha no clique)' : ''}. O clique edita o preco do fornecedor.`}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                    {/* [DA-860] Aliquota a conferir: o "%" laranja do Monitor, no lugar do "*"
                                        discreto (pedido dele, 28/10/2026). Mesmo par de cor da caixa de la. */}
                                    {estimado && (
                                      <span style={{ background: '#f97316', color: '#000', border: '1px solid #c2410c', borderRadius: 3, fontSize: 9, fontWeight: 700, lineHeight: 1, padding: '1px 3px' }}>%</span>
                                    )}
                                    {formatMoedaSemSimbolo(precoEmbComDifal(pp, resp.filial_uf, resp.difal_pct), 2)}
                                  </span>
                                  {/* Uma linha so: "s/ DIFAL ... · est." quebrava em duas na celula de 100px.
                                      Em 10px o milhar ("s/DIFAL 2.334,65*") pedia 84px de 81; em 9px, ~76. */}
                                  <span style={{ fontSize: 9, fontWeight: 400, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                    s/DIFAL {resp.preco_principal}
                                  </span>
                                </span>
                              );
                            })()}
                          </div>
                        </td>
                        {/* UNITÁRIO — Preço Emb ÷ Qtd na Emb, 4 casas, read-only. É o número que decide o menor preço,
                            então leva o realce: amarelo no menor preço, verde se compra forçada. */}
                        <td style={{ padding: 2, borderRight: '2px solid var(--border)' }}>
                          <div
                            title={isMenor && !forcado ? 'Menor preço deste item (unitário)' : forcado ? 'Compra forçada' : 'Preço Emb ÷ Qtd na Emb'}
                            style={{
                              ...cellBox,
                              fontSize: 13,
                              fontWeight: 700,
                              ...(ignorado
                                ? { textDecoration: 'line-through', color: 'var(--text-muted)' }
                                : pu > 0
                                  ? { color: 'var(--page-fg)' }
                                  : { color: 'var(--border)' }),
                              ...(forcado
                                ? { background: '#d1fae5', color: '#065f46' }
                                : isMenor
                                  ? { background: '#fde68a', color: '#000' }
                                  : {}),
                            }}
                          >
                            {pu > 0 ? formatMoedaSemSimbolo(pu, 4) : '—'}
                          </div>
                        </td>
                        {/* TOTAL — so na linha da loja (pedido dele, 28/10/2026); aqui fica vazio */}
                        <td style={{ borderRight: '2px solid var(--border)' }} />
                        {/* CONFIGURAÇÕES — I (incluir no PDF) · D (desconsiderar) · F (forçar) · C (comprei) · remover variação */}
                        <td style={{ padding: 2, textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                            <input
                              type="checkbox"
                              checked={!isOcultoPdf(item.id, forn.fornecedor_id, ri)}
                              onChange={() => toggleOcultoPdf(item.id, forn.fornecedor_id, ri)}
                              disabled={inativo}
                              title={inativo ? MSG_PRODUTO_INATIVO : 'Incluir no PDF e WhatsApp (I)'}
                              style={{ width: 14, height: 14, accentColor: 'var(--primary)', cursor: inativo ? 'not-allowed' : 'pointer', margin: 0 }}
                            />
                            <input
                              type="checkbox"
                              checked={ignorado}
                              onChange={() => toggleDesconsiderar(item.id, forn.fornecedor_id, ri)}
                              disabled={inativo}
                              title={inativo ? MSG_PRODUTO_INATIVO : 'Desconsiderar do cálculo do menor preço (D)'}
                              style={{ width: 14, height: 14, accentColor: '#f97316', cursor: inativo ? 'not-allowed' : 'pointer', margin: 0 }}
                            />
                            {pu > 0 && !ignorado && !inativo ? (
                              <input
                                type="checkbox"
                                checked={forcado}
                                onChange={() => toggleForcarCompra(item.id, forn.fornecedor_id, ri)}
                                title="Forçar compra deste item (F)"
                                style={{ width: 14, height: 14, accentColor: '#059669', cursor: 'pointer', margin: 0 }}
                              />
                            ) : (
                              <span style={{ width: 14, display: 'inline-block' }} />
                            )}
                            {/* C (comprei) — DA-368, refinado: marca o produto como comprado DESTE
                                fornecedor, que pode NAO ser o vencedor de amarelo (o comprador as
                                vezes fecha com outro por prazo/relacionamento). So um fornecedor
                                por produto fica marcado — marcar aqui move a marca de outra celula
                                do mesmo produto sozinho (o backend sobrescreve). So aparece com
                                oferta de verdade (preco > 0) e item com onde guardar a marca
                                (produto ou prateleira Novos Produtos). */}
                            {alvoDaMarcaCompra(item) != null && pu > 0 ? (() => {
                              const compradoAqui = item.compra_em_aberto === true && item.ultima_compra_fornecedor_id === forn.fornecedor_id;
                              return (
                                <button
                                  type="button"
                                  onClick={() => onMarcarCompradoCelula(item, forn, !compradoAqui)}
                                  title={compradoAqui ? 'Comprado deste fornecedor — clique pra desmarcar' : 'Comprei deste fornecedor — marca o produto como comprado'}
                                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 18, padding: 0, border: 'none', borderRadius: 4, background: compradoAqui ? '#2563eb' : 'transparent', cursor: 'pointer' }}
                                >
                                  <ShoppingCart size={14} color={compradoAqui ? '#fff' : '#2563eb'} />
                                </button>
                              );
                            })() : (
                              <span style={{ width: 20, display: 'inline-block' }} />
                            )}
                            {!isFirst && !inativo ? (
                              <button
                                type="button"
                                onClick={() => removeResp(item.id, forn.fornecedor_id, ri)}
                                style={btnIco}
                                title="Remover variação"
                              >
                                <Trash2 size={13} color="#9ca3af" />
                              </button>
                            ) : (
                              <span style={{ width: 13, display: 'inline-block' }} />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })}
              </Fragment>
            );
          })}
          {itens.length === 0 && (
            <tr>
              <td colSpan={13} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                Nenhum produto na cotação. Use a busca acima para adicionar.
              </td>
            </tr>
          )}
          {itens.length > 0 && itensVisiveis.length === 0 && (
            <tr>
              <td colSpan={13} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                {(buscaProduto || '').trim().length >= 2 && (
                  <>Nenhum item da cotação corresponde a “{buscaProduto}”. Se quiser adicionar um produto novo, ele aparece no menu acima.</>
                )}
                {ocultosComprados > 0 && (
                  <AvisoOcultosComprados qtd={ocultosComprados} onMostrar={() => setEscondeCompradas(false)} />
                )}
              </td>
            </tr>
          )}
        </tbody>
        {totalGeralCompra > 0 && (
          <tfoot>
            <tr style={{ borderTop: '2px solid ' + TH_BG }}>
              <td colSpan={13} style={{ padding: '12px 16px', textAlign: 'right', background: '#ecfeff' }}>
                <span style={{ fontSize: 15, color: 'var(--text-muted)', marginRight: 12 }}>Total Geral:</span>
                <strong style={{ fontSize: 20, color: '#0c4a6e' }}>{formatMoeda(totalGeralCompra)}</strong>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
    </div>
  );
}


// ============================================================
// SUBCOMPONENTE — Modo "Inserir Cotacao" focado num fornecedor
// ============================================================

interface InserirCotProps {
  forn: FornecedorVinculado;
  itens: ItemCotacao[];
  buscaProduto: string;
  getResps: (i: number, f: number) => RespostaForm[];
  setResp: (i: number, f: number, idx: number, campo: keyof RespostaForm, valor: any) => void;
  removeResp: (i: number, f: number, idx: number) => void;
  calcPrecoUnitario: (r: RespostaForm) => number;
  totalFornecedor: (f: number) => number;
  totalCImpForn: Record<number, string>;
  setTotalCImpForn: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  salvarTotalCImpForn: (f: number, v: string) => Promise<void>;
  marcas: MarcaOpcao[];
  requestNovaMarca: (
    ctx: { tipo: 'resp'; itemId: number; fornId: number; respIdx: number },
  ) => void;
  requestHistoricoProduto: (p: ProdutoHistorico) => void;
  isForcarCompra: (itemId: number, fornId: number, respIdx: number) => boolean;
  toggleForcarCompra: (itemId: number, fornId: number, respIdx: number) => void;
  isDesconsiderado: (itemId: number, fornId: number, respIdx: number) => boolean;
  toggleDesconsiderar: (itemId: number, fornId: number, respIdx: number) => void;
  isOcultoPdf: (itemId: number, fornId: number, respIdx: number) => boolean;
  toggleOcultoPdf: (itemId: number, fornId: number, respIdx: number) => void;
  setFotoZoom: (url: string | null) => void;
  setModalImportarIA: (v: boolean) => void;
}

function InserirCotacaoFornecedor(props: InserirCotProps) {
  const {
    forn,
    itens,
    buscaProduto,
    getResps,
    setResp,
    removeResp,
    calcPrecoUnitario,
    totalFornecedor,
    marcas,
    requestNovaMarca,
    requestHistoricoProduto,
    isForcarCompra,
    toggleForcarCompra,
    isDesconsiderado,
    toggleDesconsiderar,
    isOcultoPdf,
    toggleOcultoPdf,
    setFotoZoom,
    setModalImportarIA,
  } = props;

  const itensVisiveis = useMemo(() => {
    const q = (buscaProduto || '').trim().toLowerCase();
    if (q.length < 2) return itens;
    return itens.filter((it) =>
      [it.codigo_interno, it.produto_descricao, it.descricao].some((c) => c && String(c).toLowerCase().includes(q)),
    );
  }, [itens, buscaProduto]);

  return (
    <div style={{ background: 'var(--panel-bg)', borderRadius: 8, border: '1px solid var(--primary)', marginBottom: 16, overflow: 'auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          background: 'var(--primary-soft)',
          borderBottom: '1px solid var(--primary)',
        }}
      >
        <ListChecks size={20} color="var(--primary)" />
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>
          Inserir Cotacao — {getNomeFornecedor(forn)}
        </h3>
        <span style={{ fontSize: 11, color: 'var(--primary)' }}>
          ({itens.length} produto{itens.length !== 1 ? 's' : ''})
        </span>
        {/* ⛔ O "Importar (IA)" saiu em 22/09/2026: ele lia a tabela do fornecedor e gravava PRECO
            na resposta da cotacao, que e' exatamente a porta que a decisao dele fechou. Preco novo
            entra pelo Catalogo do fornecedor, pela nota ou pelo monitor. */}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#fef3c7', borderBottom: '1px solid #fbbf24' }}>
            <th colSpan={7}></th>
            <th style={{ textAlign: 'right', padding: '4px 12px' }}>
              <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', color: '#a16207', lineHeight: 1 }}>Total</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#854d0e' }}>{formatMoeda(totalFornecedor(forn.fornecedor_id))}</div>
            </th>
            <th colSpan={3}></th>
          </tr>
          <tr style={{ background: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}>
            <th style={{ textAlign: 'left', padding: '6px 8px', width: 60 }}>FC</th>
            <th style={{ textAlign: 'left', padding: '6px 8px', minWidth: 220 }}>Produto</th>
            <th style={{ textAlign: 'left', padding: '6px 8px', width: 100 }}>Cod. Forn.</th>
            <th style={{ textAlign: 'left', padding: '6px 8px', width: 200 }}>Desc. Fornecedor</th>
            <th style={{ textAlign: 'left', padding: '6px 8px', width: 100 }}>Marca</th>
            <th style={{ textAlign: 'center', padding: '6px 8px', width: 70 }}>Embal.</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', width: 80 }}>Qtd Emb</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', width: 90 }}>Preco</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', width: 60 }}>IPI %</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', width: 90 }}>Unit.</th>
            <th style={{ textAlign: 'center', padding: '6px 8px', width: 104 }}>Configurações</th>
          </tr>
        </thead>
        <tbody>
          {itensVisiveis.map((item, idx) => {
            const linhas = getResps(item.id, forn.fornecedor_id);
            return (
              <Fragment key={item.id}>
                {linhas.map((resp, ri) => {
                  const pu = calcPrecoUnitario(resp);
                  return (
                    <tr key={`${item.id}-${ri}`} style={{ borderTop: ri === 0 ? '2px solid var(--border)' : '1px solid var(--border)', background: idx % 2 === 1 && ri === 0 ? 'var(--grid-row-alt-bg)' : 'var(--panel-bg)' }}>
                      {ri === 0 && (
                        <td rowSpan={linhas.length} style={{ padding: '6px 8px', verticalAlign: 'top', fontSize: 11, fontWeight: 700, color: 'var(--primary)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {item.foto_url ? (
                              <FotoProduto
                                url={item.foto_url}
                                tam={32}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setFotoZoom(item.foto_url)}
                              />
                            ) : (
                              <div style={{ width: 32, height: 32, background: 'var(--grid-row-alt-bg)', borderRadius: 4, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <ImageOff size={12} color="#9ca3af" />
                              </div>
                            )}
                            <span>{item.codigo_interno || item.np_codigo || ''}</span>
                          </div>
                        </td>
                      )}
                      {ri === 0 && (
                        <td rowSpan={linhas.length} style={{ padding: '6px 8px', verticalAlign: 'top' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--page-fg)' }}>{item.produto_descricao || item.descricao}</div>
                            {item.produto_id != null && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  requestHistoricoProduto({
                                    id: item.produto_id!,
                                    codigo_interno: item.codigo_interno,
                                    descricao: item.produto_descricao || item.descricao,
                                    foto_url: item.foto_url,
                                  });
                                }}
                                title="Ver historico deste produto em todos os fornecedores"
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}
                              >
                                <BookOpen size={14} />
                              </button>
                            )}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                            Qtd: {Number(item.quantidade).toLocaleString('pt-BR')} {item.unidade_sigla || ''}
                            {item.marca_preferida && <span style={{ marginLeft: 8, color: 'var(--primary)' }}>Pref: {item.marca_preferida}</span>}
                          </div>
                        </td>
                      )}
                      <td style={{ padding: '4px 6px' }}>
                        <input
                          value={resp.cod_produto_fornecedor}
                          onChange={(e) => setResp(item.id, forn.fornecedor_id, ri, 'cod_produto_fornecedor', upperMantendoCursor(e))}
                          style={{ width: '100%', padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 3, fontSize: 11 }}
                        />
                      </td>
                      {/* Descricao — SO-LEITURA (decisao do Ruan, 28/08/2026): ela vem do Catalogo
                          do Fornecedor e so se escreve la. Era o unico campo de descricao aberto
                          da tela; o modo "Por produto" ja nascera read-only. */}
                      <td style={{ padding: '4px 6px' }}>
                        <div
                          title={resp.descricao_fornecedor || ''}
                          style={{ padding: '3px 6px', fontSize: 11, color: 'var(--page-fg)', overflowWrap: 'anywhere' }}
                        >
                          {resp.descricao_fornecedor || '—'}
                        </div>
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        <AutocompleteMarca
                          value={resp.marca}
                          onChange={(v) => setResp(item.id, forn.fornecedor_id, ri, 'marca', v)}
                          marcas={marcas}
                          onNovaMarca={() =>
                            requestNovaMarca({
                              tipo: 'resp',
                              itemId: item.id,
                              fornId: forn.fornecedor_id,
                              respIdx: ri,
                            })
                          }
                          inputStyle={{ padding: '3px 6px', fontSize: 11 }}
                        />
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <input
                          value={resp.embalagem}
                          onChange={(e) => setResp(item.id, forn.fornecedor_id, ri, 'embalagem', upperMantendoCursor(e))}
                          style={{ width: 60, padding: '3px 4px', border: '1px solid var(--border)', borderRadius: 3, fontSize: 11, textAlign: 'center' }}
                        />
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                        <input
                          inputMode="decimal"
                          value={resp.quantidade_embalagem}
                          onChange={(e) => setResp(item.id, forn.fornecedor_id, ri, 'quantidade_embalagem', e.target.value)}
                          style={{ width: 70, padding: '3px 4px', border: '1px solid var(--border)', borderRadius: 3, fontSize: 11, textAlign: 'right' }}
                        />
                      </td>
                      {/* ⛔ PRECO SO-LEITURA (decisao dele, 22/09/2026) — esta era a ultima porta
                          que digitava preco na cotacao. A fonte e' o Catalogo do fornecedor. */}
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                        <div
                          title="Preço vem do Catálogo do fornecedor"
                          style={{ width: 80, padding: '3px 4px', fontSize: 11, textAlign: 'right', fontWeight: 600, marginLeft: 'auto' }}
                        >
                          {resp.preco_principal || '—'}
                        </div>
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                        <input
                          inputMode="decimal"
                          value={resp.ipi_percentual ?? ''}
                          onChange={(e) => {
                            const v = e.target.value.trim();
                            const n = v === '' ? null : parseFloat(v.replace(',', '.'));
                            setResp(item.id, forn.fornecedor_id, ri, 'ipi_percentual', Number.isFinite(n) ? n : null);
                          }}
                          style={{ width: 50, padding: '3px 4px', border: '1px solid var(--border)', borderRadius: 3, fontSize: 11, textAlign: 'right' }}
                        />
                      </td>
                      {/* Unit. — unitario simples (Preco ÷ Qtd Emb), sem imposto, 4 casas */}
                      <td
                        style={{
                          padding: '4px 6px',
                          textAlign: 'right',
                          fontSize: 12,
                          fontWeight: 700,
                          color: isDesconsiderado(item.id, forn.fornecedor_id, ri)
                            ? 'var(--text-muted)'
                            : pu > 0
                              ? 'var(--page-fg)'
                              : 'var(--text-muted)',
                          textDecoration: isDesconsiderado(item.id, forn.fornecedor_id, ri)
                            ? 'line-through'
                            : 'none',
                        }}
                      >
                        {pu > 0 ? formatMoedaSemSimbolo(pu, 4) : '—'}
                      </td>
                      {/* Configurações — I (incluir no PDF) · D (desconsiderar) · F (forçar) · remover */}
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                          <input
                            type="checkbox"
                            checked={!isOcultoPdf(item.id, forn.fornecedor_id, ri)}
                            onChange={() => toggleOcultoPdf(item.id, forn.fornecedor_id, ri)}
                            title="Incluir no PDF e WhatsApp (I)"
                            style={{ width: 12, height: 12, accentColor: 'var(--primary)', cursor: 'pointer', margin: 0 }}
                          />
                          <input
                            type="checkbox"
                            checked={isDesconsiderado(item.id, forn.fornecedor_id, ri)}
                            onChange={() => toggleDesconsiderar(item.id, forn.fornecedor_id, ri)}
                            title="Desconsiderar do cálculo do menor preço (D)"
                            style={{ width: 12, height: 12, accentColor: '#f97316', cursor: 'pointer', margin: 0 }}
                          />
                          {pu > 0 && !isDesconsiderado(item.id, forn.fornecedor_id, ri) ? (
                            <input
                              type="checkbox"
                              checked={isForcarCompra(item.id, forn.fornecedor_id, ri)}
                              onChange={() => toggleForcarCompra(item.id, forn.fornecedor_id, ri)}
                              title="Forçar compra deste item (F)"
                              style={{ width: 12, height: 12, accentColor: '#059669', cursor: 'pointer', margin: 0 }}
                            />
                          ) : (
                            <span style={{ width: 12, display: 'inline-block' }} />
                          )}
                          {linhas.length > 1 ? (
                            <button
                              type="button"
                              onClick={() => removeResp(item.id, forn.fornecedor_id, ri)}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'inline-flex' }}
                              title="Remover variação"
                            >
                              <Trash2 size={13} />
                            </button>
                          ) : (
                            <span style={{ width: 13, display: 'inline-block' }} />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
